import { ApiError } from './api';
import type { Money, MoneyRange } from './money';

/** How a trip goes on from one stop to the next. */
export const modes = ['flight', 'train', 'bus', 'ferry', 'car', 'foot'] as const;
export type Mode = (typeof modes)[number];

/** What a stop's icon says. */
export const statuses = ['unresolved', 'expensive', 'booked', 'estimate'] as const;
export type StopStatus = (typeof statuses)[number];

/** The ways one gets to a thing done in a town, the world's: a jeepney in Manila, a songthaew in
 * Chiang Mai, a marshrutka in Tbilisi (a minibus), a tuk-tuk, a Grab (a ride hailed). */
export const cityWays = [
  'walk',
  'bike',
  'scooter',
  'metro',
  'tram',
  'train',
  'cable',
  'bus',
  'minibus',
  'jeepney',
  'songthaew',
  'tuktuk',
  'motortaxi',
  'taxi',
  'ridehail',
  'car',
  'boat',
  'ferry',
  'cyclo',
] as const;
export type CityWay = (typeof cityWays)[number];

/** A thing done at a stop: the Skytree at eleven, a museum after, a dinner. */
export interface AgendaItem {
  id: string;
  name: string;
  /** `2027-03-09`. */
  day: string;
  /** On the local clock at the stop, `11:00`, as a stay's check-in is; each when said. */
  starts: string | null;
  ends: string | null;
  place: (Point & { name: string }) | null;
  /** What it costs in the trip's money: what the plan counts. */
  price: Money | null;
  /** What it costs in the money it is paid in, when not the trip's: ¥3,100. */
  paid: Money | null;
  /** Its photos and PDFs, each fetched as a document's file. */
  files: { id: string; name: string; type: string | null }[];
  /** The tags given it, to find things done together. */
  tags: string[];
  /** A ticket or a table is had for it. */
  booked: boolean;
  link: string | null;
  /** How one gets there from the place before, and about how long, when said: else a walk. */
  by: CityWay | null;
  byMinutes: number | null;
  /** The way there in parts, when it has changes: by train to the centre, by bus, on foot. */
  parts: WayPart[];
}

/** The kinds of place a stay is, as Booking.com sorts its places. */
export const lodgings = [
  'hotel',
  'hostel',
  'apartment',
  'guesthouse',
  'bnb',
  'resort',
  'villa',
  'house',
  'ryokan',
  'capsule',
  'camping',
  'homestay',
  'boat',
] as const;
export type Lodging = (typeof lodgings)[number];

/** One part of a way with changes: by what, to where — a station, a stop — and how long. */
export interface WayPart {
  by: CityWay;
  /** Where it ends; none for the last part, which ends where the way does. */
  to: (Point & { name: string }) | null;
  minutes: number | null;
}

/** A day's ways that belong to no thing: back to the room, to the room checked into, on. */
export const dayLegs = ['back', 'in', 'leave'] as const;
export type DayLeg = (typeof dayLegs)[number];

/** How one gets about one of a day's own ways, where said: else walked. */
export interface DayWay {
  /** `2027-03-09`. */
  day: string;
  leg: DayLeg;
  by: CityWay | null;
  byMinutes: number | null;
  parts: WayPart[];
}

/** A way as said: by what, how long, in parts when it has changes. */
export interface WaySaid {
  by: CityWay | null;
  byMinutes: number | null;
  parts: WayPart[];
}

/** A thing done as sent: its price in minor units of the trip's currency. */
export interface AgendaInput {
  stop: string;
  name: string;
  day: string;
  starts: string | null;
  ends: string | null;
  place: (Point & { name: string }) | null;
  /** In minor units of `currency`. */
  price: number | null;
  /** The money the price is in: `JPY`. */
  currency: string;
  booked: boolean;
  link: string | null;
  by: CityWay | null;
  byMinutes: number | null;
  parts: WayPart[];
}

/** A booking as the planner shows it. */
export interface Booking {
  id: string;
  kind: 'stay' | 'ticket';
  name: string;
  day: string | null;
  nights: number;
  /** What it costs in the trip's money: what the plan counts. */
  price: Money;
  /** What it costs in the money it is paid in, when not the trip's: ฿1,900 at the desk. */
  paid: Money | null;
  pay: 'paid' | 'later';
  reference: string | null;
  /** The link it was read from, to open again. */
  link: string | null;
  /** A stay's check-in and check-out times, `HH:MM` local at its place. */
  checkIn: string | null;
  checkOut: string | null;
  /** A ticket's flight: its number, `AY100`, and its airline when known. */
  flight: { number: string; airline: string | null } | null;
  /** Where a stay is: its point and address. */
  place: (Point & { name: string }) | null;
  /** What kind of place a stay is: a hotel, a hostel, a ryokan; when said. */
  lodging: Lodging | null;
  /** Until when it is cancelled free, on its stop's clock: `2027-03-06T23:59`. */
  freeUntil: string | null;
  /** The text of the QR code on its ticket, to show from the phone. */
  qr: string | null;
  /** A ticket's legs, in order. */
  legs: TicketLeg[];
  /** The people on a ticket. */
  passengers: Passenger[];
  /** What a ticket allows. */
  rules: TicketRules;
  /** What happened to it: moved, late or cancelled; none when nothing did. */
  incident: Incident | null;
  /** The booking it was bought in place of, one cancelled. */
  replaces: string | null;
}

/** What can happen to a booking: its time moved, it is late, or it was cancelled. */
export const incidentKinds = ['moved', 'late', 'cancelled'] as const;
export type IncidentKind = (typeof incidentKinds)[number];

/** What came of a cancelled booking's money: coming back, back, a voucher, or gone. */
export const refunds = ['pending', 'received', 'voucher', 'lost'] as const;
export type Refund = (typeof refunds)[number];

/** What happened to a booking. */
export interface Incident {
  kind: IncidentKind;
  /** When a late one really leaves and arrives, each an instant when said. */
  leavesAt: string | null;
  arrivesAt: string | null;
  refund: Refund | null;
  /** How much comes back, or the voucher is worth; none said is all of it. */
  amount: Money | null;
  voucherUntil: string | null;
  /** What of its price does not come back. */
  lost: Money;
}

/** What happened to a booking, as sent. */
export interface IncidentInput {
  kind: IncidentKind;
  leavesAt?: string | null;
  arrivesAt?: string | null;
  refund?: Refund | null;
  /** In minor units. */
  amount?: number | null;
  voucherUntil?: string | null;
}

/** What a ticket allows: its bags, and when check-in opens and closes before it leaves. */
export interface TicketRules {
  cabinPieces: number | null;
  cabinKg: number | null;
  /** `55x40x23`. */
  cabinSize: string | null;
  personalItem: boolean;
  /** Each checked bag's kilograms: none, `[23]`, `[20, 30]`. */
  checkedBags: number[];
  onlineOpensH: number | null;
  onlineClosesM: number | null;
  deskOpensH: number | null;
  deskClosesM: number | null;
  notes: string;
}

/** One leg of a ticket: times are instants in UTC, said local at each end. */
export interface TicketLeg {
  origin: string;
  destination: string;
  departureAt: string | null;
  arrivalAt: string | null;
  actualDepartureAt: string | null;
  actualArrivalAt: string | null;
  number: string | null;
  /** Its airline's IATA code, `EY`, by air; and its name when known. */
  airline: string | null;
  airlineName: string | null;
  /** Who runs it when no airline code says: `Elron`, `Tallink`. */
  operator: string | null;
  /** What it goes in: the aircraft, the train's type, the bus's model, the ship, the car. */
  vehicle: string | null;
  /** An aircraft's or a vehicle's registration, a ship's IMO number. */
  registration: string | null;
  /** Its class: `2nd class`, `Sleeper`. */
  travelClass: string | null;
  /** A vehicle carried aboard a ferry, and its plate. */
  carried: Carried | null;
  carriedPlate: string | null;
  gate: string | null;
  arrivalGate: string | null;
  /** How it is boarded and left: by an air bridge, stairs or a bus. */
  boarding: Apron | null;
  deplaning: Apron | null;
  terminalFrom: string | null;
  terminalTo: string | null;
  /** As the crow flies, when both ends are airports known. */
  km: number | null;
  /** The zone of each end, when it is an airport known. */
  originZone: string | null;
  destinationZone: string | null;
}

/** How a flight is boarded or left: by an air bridge, by stairs, or by a bus to the stand. */
export const aprons = ['bridge', 'stairs', 'bus'] as const;
export type Apron = (typeof aprons)[number];

/** What a ferry carries besides its passengers. */
export const carriedKinds = ['car', 'van', 'motorcycle', 'bicycle', 'camper'] as const;
export type Carried = (typeof carriedKinds)[number];

/** One person on a ticket. */
export interface Passenger {
  name: string;
  /** The fellow traveller they are, if one. */
  traveller: string | null;
  price: Money | null;
  number: string | null;
  /** A seat on each leg, in the legs' order. */
  seats: (string | null)[];
  /** A coach on each leg, by train. */
  coaches: (string | null)[];
  /** Where the seat is on each leg, when said: by a window, in the middle, on an aisle. */
  kinds: (SeatKind | null)[];
  /** Their own bags; `null` for the ticket's. */
  bags: PassengerBags | null;
}

/** One person's own bags: the cabin bag's kilograms, each checked bag's. */
export interface PassengerBags {
  cabinKg: number | null;
  checked: number[];
}

/** Where a seat is across its row. */
export const seatKinds = ['window', 'middle', 'aisle'] as const;
export type SeatKind = (typeof seatKinds)[number];

/** A ticket's details as sent: wall clocks local at each end, or instants. */
export interface TicketInput {
  legs: {
    origin: string;
    destination: string;
    departureAt: string | null;
    arrivalAt: string | null;
    actualDepartureAt: string | null;
    actualArrivalAt: string | null;
    number: string | null;
    airline: string | null;
    operator: string | null;
    vehicle: string | null;
    registration: string | null;
    travelClass: string | null;
    carried: Carried | null;
    carriedPlate: string | null;
    gate: string | null;
    arrivalGate: string | null;
    boarding: Apron | null;
    deplaning: Apron | null;
    terminalFrom: string | null;
    terminalTo: string | null;
    originZone: string | null;
    destinationZone: string | null;
  }[];
  passengers: {
    name: string;
    traveller: string | null;
    price: number | null;
    number: string | null;
    seats: (string | null)[];
    coaches: (string | null)[];
    kinds: (SeatKind | null)[];
    bags: PassengerBags | null;
  }[];
  rules: TicketRules;
}

/** A booking as sent. */
export interface BookingInput {
  stop: string;
  kind: 'stay' | 'ticket';
  name: string;
  day: string | null;
  nights: number;
  /** In minor units of `currency`. */
  price: number;
  /** The money the price is in, `THB`; none, or left out, for the trip's own. */
  currency?: string | null;
  pay: 'paid' | 'later';
  reference: string | null;
  /** The link it was read from, to open again. */
  link: string | null;
  checkIn?: string | null;
  checkOut?: string | null;
  /** A flight number as typed, `AY 100`. */
  flight?: string | null;
  place?: (Point & { name: string }) | null;
  lodging?: Lodging | null;
  /** Until when it is cancelled free, on its stop's clock: `2027-03-06T23:59`. */
  freeUntil?: string | null;
  /** The text of the QR code on its ticket. */
  qr?: string | null;
  /** The booking it is bought in place of, one cancelled. */
  replaces?: string | null;
}

/** A booking read from a typed line, the parts it could not read left empty. */
export interface Draft {
  stop: string | null;
  kind: 'stay' | 'ticket';
  name: string;
  day: string | null;
  nights: number;
  price: number | null;
  /** The price's currency when it said one (`€`, `EUR`); else the stop's is meant. */
  currency: string | null;
  pay: 'paid' | 'later';
  reference: string | null;
  /** The link it was read from, to open again. */
  link: string | null;
  /** A stay's hours as a mail said them, on the local clock: `15:00`. */
  checkIn?: string | null;
  checkOut?: string | null;
  /** A flight's number as a mail said it: `NH962`. */
  flight?: string | null;
  /** A bus's or a train's ride as its ticket's file said it. */
  ticket?: DraftTicket | null;
}

/** A ticket's ride read from its file: how it goes, when it can be told, and its legs. */
export interface DraftTicket {
  mode: 'bus' | 'train' | 'ferry' | 'flight' | null;
  legs: DraftRide[];
}

/** A leg as a ticket says it, its times on the local clock at each end: `2025-09-14T08:00`. */
export interface DraftRide {
  origin: string;
  destination: string;
  departure: string;
  arrival: string;
  number: string | null;
  operator: string | null;
  /** Its ship by name, as a ferry's ticket says it: `Viking XPRS`. */
  vehicle: string | null;
  platform: string | null;
  seat: string | null;
  passenger: string | null;
  /** The ticket's own number: an airline's thirteen digits, `057 236 272 215 2`. */
  ticket: string | null;
}

/** A trip in the list. */
export const roles = ['owner', 'editor', 'viewer'] as const;
/** What a member may do on a trip: everything, change the plan, or see the route only. */
export type Role = (typeof roles)[number];

/** What a trip is for, in the order offered. */
export const purposes = [
  'holiday',
  'work',
  'study',
  'visit',
  'culture',
  'adventure',
  'sport',
] as const;
export type Purpose = (typeof purposes)[number];

export interface TripSummary {
  role: Role;
  id: string;
  name: string;
  /** What it is for; none when not said. */
  purpose: Purpose | null;
  start: string;
  end: string;
  budget: Money;
  stops: number;
  days: number;
  /** The trip it branches off; none for a trip of its own or a family's root. */
  parent: string | null;
  /** The trip it meets again, the town and day it meets it, and the flight it comes on. */
  meets: { trip: string; city: string; day: string | null; way: string | null } | null;
  /** Its place in its family, the root 0, each trip before its branches: its colour. */
  place: number;
  /** How many go on it. */
  people: number;
  /** Its towns, from where it parts to where it meets another again. */
  route: string[];
  /** How many of its bookings were cancelled. */
  cancelled: number;
  /** Who joins it from another trip, and where. */
  joined: { people: number; city: string }[];
  /** When it was archived: out of the list, read only until restored; none while it is not. */
  archivedAt: string | null;
}

/** A day's sky, from its WMO weather code. */
export const skies = ['sun', 'part', 'cloud', 'fog', 'rain', 'snow', 'storm'] as const;
export type Sky = (typeof skies)[number];

/** What a day at a place usually is, from twenty years of its weather. */
/** A part of a day, by its local hours (`PARTS`): its sky, temperature and rain. */
export interface WeatherPart {
  sky: Sky;
  temp: number;
  /** Per cent: the forecast's highest chance, or the usual share of rainy days. */
  rainChance: number | null;
  rainMm: number;
}

/** The parts a day is told in, by their first hour and the hour they end, local. */
export const PARTS = [
  ['night', 0, 5],
  ['early', 5, 8],
  ['morning', 8, 11],
  ['midday', 11, 14],
  ['afternoon', 14, 17],
  ['evening', 17, 21],
  ['late', 21, 24],
] as const;

export interface UsualWeather {
  high: number;
  low: number;
  /** Where nine days in ten fall. */
  highFrom: number;
  highTo: number;
  lowFrom: number;
  lowTo: number;
  /** The days with a millimetre of rain or more, per cent. */
  rainy: number;
  rainMm: number;
  wind: number;
  sky: Sky;
  /** How many days it is read from. */
  days: number;
  parts: WeatherPart[];
}

/** One day of a stop: as it was when gone by, forecast within sixteen days, else the usual
 * weather; none when unknown. */
export type DayWeather =
  | {
      day: string;
      kind: 'was' | 'forecast' | 'usual';
      sky: Sky;
      high: number;
      low: number;
      /** Per cent: the forecast's chance, or the usual share of rainy days. */
      rainChance: number | null;
      rainMm: number;
      wind: number;
      sunrise: string | null;
      sunset: string | null;
      /** Its parts, each of `PARTS`; none when its hours are not known. */
      parts: WeatherPart[];
      usual: UsualWeather | null;
    }
  | { day: string; kind: null };

/** A trip's weather, each stop placed by its days. */
export interface TripWeather {
  weather: 'ok' | 'failed' | 'off';
  today: string;
  forecastDays: number;
  years: [number, number];
  stops: { stop: string; city: string; days: DayWeather[] }[];
  /** Each way timed, its weather as it goes: every three hours, where it is then. */
  ways: { stop: string; cells: WayWeather[] }[];
}

/** A stretch of a way and the weather where it is then, the part of that place's day. */
export interface WayWeather {
  /** Instants. */
  from: string;
  to: string;
  /** Its day there, by the sun. */
  day: string;
  lat: number;
  lon: number;
  /** The clock where it is then, `Asia/Almaty`; none out at sea. */
  zone: string | null;
  kind: 'was' | 'forecast';
  sky: Sky;
  temp: number;
  rainChance: number | null;
  /** Before the sun rises there, or after it sets. */
  night: boolean;
}

/** A trip copied to other dates: its name, its first day, and what comes along. */
export interface CopyInput {
  name: string;
  start: string;
  /** Each booking kept as the estimate of what it cost. */
  bookings: boolean;
  /** The fellow travellers along for the same days. */
  travellers: boolean;
  /** The trips branching off it copied too. */
  branches: boolean;
}

/** A stop of a family's trip: where and when. */
export interface KinStop {
  id: string;
  city: string;
  start: string;
  end: string;
}

/** A trip of a family as the caller sees it: its money only when they are on it. */
export interface Kin {
  id: string;
  name: string;
  /** The caller's role on it; none when they are not on it. */
  role: Role | null;
  parent: string | null;
  /** The parent's stop its people part at. */
  partsAt: KinStop | null;
  /** The other trip's stop it meets that trip again at. */
  meets: { trip: string; stop: KinStop | null } | null;
  /** Whether the people of its other branches see it. */
  shared: boolean;
  /** Its place in the whole family, the root 0, each trip before its branches: its colour. */
  place: number;
  people: number;
  start: string;
  end: string;
  days: number;
  budget: Money | null;
  stops: (KinStop & {
    country: string;
    days: number;
    /** Only a change on the way, passed through: no night there. */
    transfer: boolean;
    mode: Mode | null;
    /** Its time zone, when it is on the map: its days begin at its midnight. */
    zone: string | null;
    /** When its way on leaves, when said. */
    departureAt: string | null;
    /** When it arrives at the next stop, when said. */
    arrivalAt: string | null;
    /** Its ticket's legs, when more than one: each its ends, times and their zones. */
    legs: {
      origin: string;
      destination: string;
      departureAt: string | null;
      arrivalAt: string | null;
      originZone: string | null;
      destinationZone: string | null;
    }[];
    /** Where its way on leaves from and arrives at: a flight's airports, by name. */
    ends: { from: string; to: string } | null;
    /** What was cancelled there: a way by how it goes (`flight`), `ticket`, or a `stay`. */
    cancelled: string[];
    place: Point | null;
  })[];
}

/** A trip's family: the root first, each trip before its branches. */
export interface Family {
  trips: Kin[];
}

/** A branch as sent: its name, the parent's stop it parts at, its budget and who goes. */
export interface BranchInput {
  name: string;
  partsAt: string;
  /** In minor units, in the parent's currency. */
  budget: number;
  /** Account ids of the parent's people who go on it too. */
  people: string[];
}

/** A stop as the planner shows it, every figure computed by the server. */
/** A point on the map, in degrees. */
export interface Point {
  lat: number;
  lon: number;
}

/**
 * Where a stop is drawn: its point, `guessed` from its city's airport, and the exact place's
 * name when it is not the town itself: `Tartu Airport`, a bus station, an address.
 */
export interface Placed extends Point {
  guessed: boolean;
  name: string | null;
}

/** An exact place found to put a stop at. */
export interface Spot extends Point {
  name: string;
  detail: string;
}

/** One end of a way: where, what it is, and whether it was said rather than worked out. */
export interface WayEnd extends Point {
  name: string;
  own: boolean;
  /** The airports near it, nearest first, to choose from. */
  airports: Spot[];
}

/** A flight a way is planned to take: `GE111` on `2027-01-11`, by LIFT, an A320. */
export interface WayFlight {
  number: string;
  day: string;
  airline: string | null;
  vehicle: string | null;
}

/**
 * A way's own track, read from a file: its points and each one's height for the map, and what
 * all its points add up to — how far, how much up and down, how high, how long.
 */
/** How a drawn way goes between its anchors. */
export type DrawMode = 'foot' | 'bike' | 'car' | 'straight';

/** A point a way was drawn through, and its name. */
export interface Anchor {
  lon: number;
  lat: number;
  name: string;
}

export interface Track {
  name: string;
  format: 'gpx' | 'kml' | 'kmz' | 'geojson' | 'csv' | 'fit' | 'tcx' | 'drawn';
  /** `[lon, lat]` a point, in order. */
  path: [number, number][];
  heights: (number | null)[];
  /** Each point's time (Unix seconds), when the file said one: where one was when. */
  times: (number | null)[];
  metres: number;
  ascent: number | null;
  descent: number | null;
  lowest: number | null;
  highest: number | null;
  seconds: number | null;
  /** A drawn way's anchors and how it goes between them. */
  anchors: { mode: DrawMode; points: Anchor[] } | null;
}

export interface PlanStop {
  id: string;
  city: string;
  /** Where it is drawn: its own point, or `guessed` from its city's airport; none if unknown. */
  place: Placed | null;
  country: string;
  /** The money of its country, `JPY`; none for a country without one. */
  currency: string | null;
  /** Its IANA time zone, by its point: its way's times are local here. */
  zone: string | null;
  /** Only a change on the way, passed through: no night there. */
  transfer: boolean;
  /** Its way on's own track, from a file: drawn in place of the way worked out. */
  track: Track | null;
  days: number;
  start: string;
  end: string;
  unresolved: boolean;
  accommodation: MoneyRange;
  food: MoneyRange;
  local: MoneyRange;
  activities: MoneyRange;
  /** When its way on leaves and arrives while how it goes is not chosen yet: none when it is,
   * or nothing is said. */
  wayTimes: { departureAt: string | null; arrivalAt: string | null } | null;
  /** The way on, and the link it was found at. */
  out: {
    mode: Mode;
    estimate: MoneyRange;
    link: string | null;
    /** When it departs and arrives, each an instant in UTC when said. */
    departureAt: string | null;
    arrivalAt: string | null;
    /** The flight it is planned to take before a ticket, as a link named it: its number and
     * the day it leaves, its airline and aircraft when known. */
    flight: WayFlight | null;
    /** About how long it takes, in minutes, when both ends are on the map. */
    minutes: number | null;
    /** By car: its road, the fuel it takes in tenths of a litre, and a litre's price. */
    fuel: { km: number; tenths: number; perLitre: Money } | null;
    /** Where it leaves from and arrives at — an airport, a station — `own` when said rather
     * than worked out; none off the map. */
    from: WayEnd | null;
    to: WayEnd | null;
  } | null;
  bookings: Booking[];
  /** What is done here, by day and then by when it starts. */
  agenda: AgendaItem[];
  /** How one gets back to one's room on its days, where said. */
  dayWays: DayWay[];
  openNights: number;
  status: StopStatus;
  cost: { paid: Money; toPay: Money; estimate: MoneyRange; total: MoneyRange; expected: Money };
  perDay: MoneyRange | null;
  expectedPerDay: Money | null;
  /** The ways it could go on to the next stop, each at what it should cost; by sea only from a
   * place on the sea to another. */
  ways: { mode: Mode; estimate: MoneyRange }[];
}

/** What a stop's things planned with a price cost together: known money. */
export function plannedOf(stop: PlanStop): Money {
  return {
    currency: stop.activities.min.currency,
    minor: stop.agenda.reduce((all, thing) => all + (thing.price?.minor ?? 0), 0),
  };
}

/** A stop's activities still estimated: what its things planned with a price leave of them. */
export function activitiesLeft(stop: PlanStop): MoneyRange {
  const planned = plannedOf(stop).minor;
  const left = (end: Money): Money => ({ ...end, minor: Math.max(0, end.minor - planned) });
  return { min: left(stop.activities.min), max: left(stop.activities.max) };
}

/** What a stop's things to do come to: the planned, and the estimate they leave. */
export function thingsOf(stop: PlanStop): MoneyRange {
  const planned = plannedOf(stop).minor;
  const { min, max } = activitiesLeft(stop);
  return {
    min: { ...min, minor: min.minor + planned },
    max: { ...max, minor: max.minor + planned },
  };
}

/** One country's part of the trip. */
export interface PlanCountry {
  country: string;
  days: number;
  total: MoneyRange;
  expected: Money;
  expectedPerDay: Money | null;
}

/** One what-if change to one stop. */
export interface Change {
  stop: string;
  days: number | null;
  remove: boolean;
}

/** What a scenario does to lowest, expected and highest (below zero: it saves). */
export interface Delta {
  min: Money;
  expected: Money;
  max: Money;
}

/** A scenario the server suggests. */
export interface Scenario {
  kind: 'shift' | 'skip';
  from: { id: string; city: string };
  to: { id: string; city: string };
  days: number;
  changes: Change[];
  delta: Delta;
}

/** What some changes would do, before they are made. */
export interface Preview {
  delta: Delta;
  totals: Plan['totals'];
}

/** A route kept beside the plan, as the trip would be with it. */
export interface Variant {
  id: string;
  name: string | null;
  codes: string[];
  modes: Mode[];
  nights: number[];
  link: string | null;
  /** Its places as read; none when it no longer fits the plan. */
  places: Airport[];
  /** The stop it leaves from; none when it goes on from the end of the trip. */
  from: { id: string; city: string } | null;
  /** The stop it joins the plan again at, if it does. */
  rejoin: { id: string; city: string } | null;
  /** The stops it leaves out, with how many bookings each has. */
  dropped: { id: string; city: string; bookings: number }[];
  /** Its new places as stops of the trip with it: their nights, costs and where they are. */
  added: PlanStop[];
  /** Each country as the trip would have it with it; none when it does not fit. */
  countries: PlanCountry[];
  /** The stop before its first new place as the trip would have it: its way on leads there. */
  into: PlanStop | null;
  /** What was said of each leg: its price and times; the legs after them estimated. */
  fares: Fare[];
  /** What was said of a stay at each place after the first; the rest at typical prices. */
  stays: (Stay | null)[];
  /** The trip's totals with it, what it changes, and its last day; none when it does not fit. */
  totals: Plan['totals'] | null;
  delta: Delta | null;
  end: string | null;
}

/** What was said of a variant's leg: its price in cents, lowest and highest, and its times. */
export interface Fare {
  price: RangeInput | null;
  departureAt: string | null;
  arrivalAt: string | null;
  /** The ticket in mind, as a draft. */
  name: string | null;
  /** Where it is, to open again rather than search for it. */
  link: string | null;
}

/** A hotel in mind for a stay, as a draft: a night there in cents, and where it is. */
export interface Hotel {
  name: string;
  price: RangeInput;
  link: string | null;
}

/** What was said of a stay at a variant's place, in cents. */
export interface Stay {
  accommodation: RangeInput;
  food: RangeInput;
  local: RangeInput;
  activities: RangeInput;
  /** The hotels in mind, the one chosen first: its night is the stay's bed. */
  hotels: Hotel[];
}

/** A stay as said, or none for its typical prices. */
function stayOf(item: unknown): Stay | null {
  if (item === null) return null;
  const stay = record(item, 'stay');
  return {
    accommodation: cents(stay.accommodation),
    food: cents(stay.food),
    local: cents(stay.local),
    activities: cents(stay.activities),
    hotels: list(stay, 'hotels', (item) => {
      const hotel = record(item, 'hotel');
      return {
        name: text(hotel, 'name'),
        price: cents(hotel.price),
        link: maybe(hotel, 'link', () => text(hotel, 'link')),
      };
    }),
  };
}

/** A range in cents as the server sends it. */
function cents(value: unknown): RangeInput {
  const r = record(value, 'range');
  return { min: number(r, 'min'), max: number(r, 'max') };
}

/** The plan's totals and its variants. */
export interface Variants {
  totals: Plan['totals'];
  end: string | null;
  variants: Variant[];
}

/** A trip with everything the planner screen shows. */
export interface Plan {
  /** The public holidays at home while the trip goes on, by a code the UI names. */
  holidays: { day: string; code: string }[];
  trip: {
    id: string;
    name: string;
    /** What it is for; none when not said. */
    purpose: Purpose | null;
    start: string;
    end: string;
    budget: Money;
    currency: string;
    /** Who shares its money, its owners and editors: what each one's part is. */
    people: number;
    /** When it was archived, read only since: `2027-04-02T10:00:00Z`; none while it is not. */
    archivedAt: string | null;
  };
  stops: PlanStop[];
  totals: {
    days: number;
    paid: Money;
    toPay: Money;
    /** What cancellations cost that does not come back, and what is still coming back. */
    lost: Money;
    refundsPending: Money;
    estimate: MoneyRange;
    total: MoneyRange;
    expected: Money;
    over: Money | null;
    /** Past it a day at a stop not yet settled is dear. */
    dearPerDay: Money;
    /** What its documents cost, paid: the insurances, the visas. */
    documents: Money;
  };
  countries: PlanCountry[];
  /** What the trip's car uses, and a litre's price in each country of the route. */
  car: {
    /** Electric: it uses kWh, and its prices are a kWh's. */
    electric: boolean;
    /** The traveller's own, not hired: no rental to book. */
    own: boolean;
    useTenths: number;
    prices: { country: string; perLitre: Money; own: boolean }[];
  };
  biggestShare: { country: string; basisPoints: number } | null;
  travellers: Traveller[];
  whatIf: Scenario[];
  /** The caller's role: an owner or an editor, the only ones a plan with money goes to. */
  role: Role;
}

/** A fellow traveller's own leg. */
export interface TravellerLeg {
  id: string;
  day: string;
  /** The way as written: `Tallinn → Tokyo`. */
  way: string;
  price: Money | null;
  reference: string | null;
}

/** Someone who joins the trip for part of the way, with legs of their own. */
export interface Traveller {
  id: string;
  name: string;
  /** The first and last day together. */
  joins: string;
  leaves: string;
  legs: TravellerLeg[];
  /** The working days of leave the stay costs them. */
  leaveDays: number;
  /** The public holidays while together, by a code the UI names. */
  holidays: { day: string; code: string }[];
}

/** A fellow traveller as sent. */
export interface TravellerInput {
  name: string;
  joins: string;
  leaves: string;
  legs: { day: string; way: string; price: number | null; reference: string | null }[];
}

/** An amount range as sent, in minor units. */
/**
 * An airport with scheduled flights, as map search knows it; or a town without one that
 * OpenStreetMap found, its name as its code and its region as its name.
 */
export interface Airport {
  code: string;
  city: string;
  name: string;
  /** ISO country code. */
  country: string;
  lat: number;
  lon: number;
  /** A town reached on the ground, not an airport. */
  town: boolean;
}

/** A way by road: its kilometres, minutes at the wheel, and `[lon, lat]` along it. */
export interface Road {
  km: number;
  /** Its length to the metre: a walk's. */
  metres: number;
  minutes: number;
  /** How much of it is a ferry line. */
  ferryKm: number;
  path: [number, number][];
  /** Each turn along it, as a walk is guided. */
  turns: Turn[];
}

/** A turn of a way: where, what is done there, onto which street, and how far it goes on. */
export interface Turn {
  at: [number, number];
  /** OSRM's maneuver: `depart`, `turn`, `continue`, `arrive`… */
  kind: string;
  /** `left`, `slight right`, `straight`…; empty when none. */
  towards: string;
  street: string;
  metres: number;
}

/** One part of a ride across town: walked, or on one line from a stop to a stop. */
export interface RideLeg {
  /** `walk`, `subway`, `tram`, `bus`, `rail`, `ferry`…, as the router says it. */
  mode: string;
  /** The line by its name, `G`, or who runs it, when on one. */
  line: string | null;
  /** The line's own colour, `b6007a`, when said. */
  color: string | null;
  /** Where it is headed, as its front says. */
  headsign: string | null;
  from: string;
  to: string;
  minutes: number;
  /** `[lon, lat]` along it. */
  path: [number, number][];
}

/** A ride across town by its transport: door to door, by what, along where. */
export interface Ride {
  minutes: number;
  legs: RideLeg[];
  path: [number, number][];
}

export const partKinds = ['empty', 'exact', 'several', 'prefix', 'fix', 'none'] as const;

/** One typed place of a route, as read. */
export interface RoutePart {
  text: string;
  kind: (typeof partKinds)[number];
  /** The airport taken for it, when one is. */
  place: Airport | null;
  /** What is offered to pick: a city's airports, a start's places, close names, or direct hubs. */
  options: Airport[];
}

/** One estimated flight of a searched route. */
export interface RouteLeg {
  from: Airport;
  to: Airport;
  km: number;
  /** Likely flown without a change. */
  direct: boolean;
  minutes: number;
  /** What a ticket usually costs, in euros. */
  estimate: MoneyRange;
  /** Where it likely changes, with the added distance in percent. */
  via: { place: Airport; detour: number }[];
}

/** A typed route: its places and, once all are known, its flights. */
export interface RouteSearch {
  /** What was searched: the text typed, or the airports a pasted link goes through. */
  typed: string;
  /** The flights a pasted Google Flights link names. */
  flights: { from: string; to: string; day: string; flight: string | null }[];
  /** The fare a pasted Google Flights booking showed. */
  price: Money | null;
  parts: RoutePart[];
  legs: RouteLeg[];
  total: MoneyRange | null;
}

/** What a day typically costs in a country, in euro cents. */
export interface Typical {
  bed: RangeInput;
  food: RangeInput;
  local: RangeInput;
  fun: RangeInput;
  /** Where they come from: gathered for TripPath, or priced from Eurostat's or the World Bank's price levels. */
  basis: TypicalBasis;
}

export const typicalBases = ['gathered', 'eurostat', 'icp'] as const;
export type TypicalBasis = (typeof typicalBases)[number];

export interface RangeInput {
  min: number;
  max: number;
}

/** A trip as sent. */
export interface TripInput {
  name: string;
  start: string;
  budget: number;
  currency: string;
  /** What it is for; null says none, left out keeps what it says. */
  purpose?: Purpose | null;
}

/** A stop as sent. */
export interface StopInput {
  city: string;
  country: string;
  days: number;
  accommodation: RangeInput;
  food: RangeInput;
  local: RangeInput;
  activities: RangeInput;
  out: {
    mode: Mode;
    estimate: RangeInput;
    link: string | null;
    departureAt?: string | null;
    arrivalAt?: string | null;
  } | null;
  /** When its way on leaves and arrives while how it goes is not chosen yet. */
  wayTimes?: { departureAt: string | null; arrivalAt: string | null } | null;
  unresolved: boolean;
  /** The point picked for it, and the exact place's name when it is not the town itself; none
   * to be placed by its city's airport. */
  place: (Point & { name?: string }) | null;
}

/** A flight found for a leg: one ticket, perhaps with a change on the way. */
export interface FoundFlight {
  from: Airport;
  via: Airport[];
  to: Airport;
  price: Money;
  paid: boolean;
  line: number;
  /** The Google Flights link it was pasted as. */
  link: string | null;
}

/** One way to fly a leg, priced. */
export interface RoutePath {
  codes: string[];
  /** Indexes into the comparison's flights, in order. */
  flights: number[];
  stopovers: { at: Airport; nights: number; cost: Money }[];
  flightsCost: Money;
  hotelsCost: Money;
  /** Paid tickets this path leaves unused. */
  lost: Money;
  total: Money;
  nights: number;
  skipsPaid: boolean;
  cheapest: boolean;
  fewest: boolean;
  /** What choosing it does to the trip's expected total. */
  change: Money;
  /** One traveller's kilograms of CO₂e: its flights and stopovers. */
  co2: number;
}

/** The options found for the way on from a stop, read, and every path through them. */
export interface Comparison {
  stop: string;
  from: string;
  to: string;
  estimate: MoneyRange | null;
  perDay: Money | null;
  text: string;
  maxLegs: number;
  chosen: string | null;
  flights: FoundFlight[];
  hotels: { at: Airport; price: Money; nights: number; line: number }[];
  problems: { line: number; text: string; problem: 'unread' | 'code' }[];
  paths: RoutePath[];
}

/** A leg with options kept. */
export interface KeptLeg {
  stop: string;
  options: number;
  chosen: string | null;
}

/** A town to put a stop at: a city with an airport, or one OpenStreetMap knows. */
export interface Town extends Point {
  name: string;
  country: string;
  detail: string;
  airport: boolean;
}

export const osmStates = ['ok', 'short', 'off', 'failed'] as const;

/** Towns found for a name, and how OpenStreetMap answered. */
export interface TownSearch {
  towns: Town[];
  osm: (typeof osmStates)[number];
}

/** One field the server refused, and why (a code the UI translates). */
export interface FieldProblem {
  field: string;
  problem: string;
}

/** The server refused the input; `fields` says which and why. */
export class InvalidInput extends Error {
  readonly fields: FieldProblem[];
  constructor(fields: FieldProblem[]) {
    super('invalid input');
    this.name = 'InvalidInput';
    this.fields = fields;
  }
}

/** There is no such trip or stop. */
/** The session ran out, or there is none: sign in again. */
export class SignedOut extends Error {
  constructor() {
    super('signed out');
    this.name = 'SignedOut';
  }
}

/** The caller's role does not allow it. */
export class Forbidden extends Error {
  constructor() {
    super('forbidden');
    this.name = 'Forbidden';
  }
}

/** The event the window hears when any call finds the session gone. */
export const SIGNED_OUT = 'trippath:signed-out';

/** A change to a trip that is archived: it is restored first. */
export class Archived extends Error {
  constructor() {
    super('archived');
    this.name = 'Archived';
  }
}

export class NotFound extends Error {
  constructor() {
    super('not found');
    this.name = 'NotFound';
  }
}

// ---- reading answers: every field checked, no casts (docs/02 §7) ----

type Fields = Record<string, unknown>;

function record(value: unknown, where: string): Fields {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new ApiError(`${where}: expected an object`);
  }
  return Object.fromEntries(Object.entries(value));
}

function text(fields: Fields, key: string): string {
  const value = fields[key];
  if (typeof value !== 'string') {
    throw new ApiError(`${key}: expected text`);
  }
  return value;
}

function number(fields: Fields, key: string): number {
  const value = fields[key];
  if (typeof value !== 'number') {
    throw new ApiError(`${key}: expected a number`);
  }
  return value;
}

function flag(fields: Fields, key: string): boolean {
  const value = fields[key];
  if (typeof value !== 'boolean') {
    throw new ApiError(`${key}: expected true or false`);
  }
  return value;
}

function list<T>(fields: Fields, key: string, read: (value: unknown) => T): T[] {
  const value = fields[key];
  if (!Array.isArray(value)) {
    throw new ApiError(`${key}: expected a list`);
  }
  return value.map(read);
}

function maybe<T>(fields: Fields, key: string, read: (value: unknown) => T): T | null {
  const value = fields[key];
  return value === null ? null : read(value);
}

function oneOf<T extends string>(fields: Fields, key: string, allowed: readonly T[]): T {
  const value = text(fields, key);
  const found = allowed.find((item) => item === value);
  if (found === undefined) {
    throw new ApiError(`${key}: unexpected ${value}`);
  }
  return found;
}

function money(value: unknown): Money {
  const fields = record(value, 'money');
  return { minor: number(fields, 'minor'), currency: text(fields, 'currency') };
}

function range(value: unknown): MoneyRange {
  const fields = record(value, 'range');
  return { min: money(fields.min), max: money(fields.max) };
}

/** A trip's purpose; none when not said, as a server before trips had one says nothing. */
function purposeOf(fields: Fields): Purpose | null {
  return maybe({ purpose: fields.purpose ?? null }, 'purpose', () =>
    oneOf(fields, 'purpose', purposes),
  );
}

function summary(value: unknown): TripSummary {
  const fields = record(value, 'trip');
  return {
    role: oneOf(fields, 'role', roles),
    id: text(fields, 'id'),
    name: text(fields, 'name'),
    purpose: purposeOf(fields),
    start: text(fields, 'start'),
    end: text(fields, 'end'),
    budget: money(fields.budget),
    stops: number(fields, 'stops'),
    days: number(fields, 'days'),
    // Not said by a server before trips branched: a trip of its own.
    parent: maybe({ parent: fields.parent ?? null }, 'parent', (parent) =>
      text({ parent }, 'parent'),
    ),
    meets: maybe({ meets: fields.meets ?? null }, 'meets', (value) => {
      const meets = record(value, 'meets');
      const said = (key: string) =>
        maybe({ [key]: meets[key] ?? null }, key, () => text(meets, key));
      return {
        trip: text(meets, 'trip'),
        city: text(meets, 'city'),
        day: said('day'),
        way: said('way'),
      };
    }),
    place: number({ place: fields.place ?? 0 }, 'place'),
    people: number({ people: fields.people ?? 0 }, 'people'),
    route: list({ route: fields.route ?? [] }, 'route', (city) => text({ city }, 'city')),
    cancelled: number({ cancelled: fields.cancelled ?? 0 }, 'cancelled'),
    joined: list({ joined: fields.joined ?? [] }, 'joined', (value) => {
      const joined = record(value, 'joined');
      return { people: number(joined, 'people'), city: text(joined, 'city') };
    }),
    archivedAt: maybe({ archivedAt: fields.archivedAt ?? null }, 'archivedAt', (archivedAt) =>
      text({ archivedAt }, 'archivedAt'),
    ),
  };
}

function kinStop(value: unknown): KinStop {
  const fields = record(value, 'stop');
  return {
    id: text(fields, 'id'),
    city: text(fields, 'city'),
    start: text(fields, 'start'),
    end: text(fields, 'end'),
  };
}

/** A trip's family as the server sends it. */
export function parseFamily(value: unknown): Family {
  return {
    trips: list(record(value, 'family'), 'trips', (item) => {
      const fields = record(item, 'trip');
      return {
        id: text(fields, 'id'),
        name: text(fields, 'name'),
        role: maybe(fields, 'role', () => oneOf(fields, 'role', roles)),
        parent: maybe(fields, 'parent', () => text(fields, 'parent')),
        partsAt: maybe(fields, 'partsAt', kinStop),
        meets: maybe(fields, 'meets', (value) => {
          const meets = record(value, 'meets');
          return { trip: text(meets, 'trip'), stop: maybe(meets, 'stop', kinStop) };
        }),
        shared: flag(fields, 'shared'),
        place: number(fields, 'place'),
        people: number(fields, 'people'),
        start: text(fields, 'start'),
        end: text(fields, 'end'),
        days: number(fields, 'days'),
        budget: maybe(fields, 'budget', money),
        stops: list(fields, 'stops', (value) => {
          const stop = record(value, 'stop');
          return {
            ...kinStop(value),
            country: text(stop, 'country'),
            days: number(stop, 'days'),
            transfer: stop.transfer === true,
            mode: maybe(stop, 'mode', () => oneOf(stop, 'mode', modes)),
            zone: maybe(stop, 'zone', () => text(stop, 'zone')),
            departureAt: maybe({ at: stop.departureAt ?? null }, 'at', (at) =>
              text({ at }, 'at'),
            ),
            arrivalAt: maybe({ at: stop.arrivalAt ?? null }, 'at', (at) => text({ at }, 'at')),
            legs: list({ legs: stop.legs ?? [] }, 'legs', (item) => {
              const leg = record(item, 'leg');
              const said = (key: string) => maybe(leg, key, () => text(leg, key));
              return {
                origin: text(leg, 'origin'),
                destination: text(leg, 'destination'),
                departureAt: said('departureAt'),
                arrivalAt: said('arrivalAt'),
                originZone: said('originZone'),
                destinationZone: said('destinationZone'),
              };
            }),
            ends: maybe({ ends: stop.ends ?? null }, 'ends', (value) => {
              const ends = record(value, 'ends');
              return { from: text(ends, 'from'), to: text(ends, 'to') };
            }),
            cancelled: list({ cancelled: stop.cancelled ?? [] }, 'cancelled', (word) =>
              text({ word }, 'word'),
            ),
            place: maybe(stop, 'place', (value) => {
              const place = record(value, 'place');
              return { lat: number(place, 'lat'), lon: number(place, 'lon') };
            }),
          };
        }),
      };
    }),
  };
}

function agendaItem(value: unknown): AgendaItem {
  const fields = record(value, 'thing');
  return {
    id: text(fields, 'id'),
    name: text(fields, 'name'),
    day: text(fields, 'day'),
    starts: maybe(fields, 'starts', () => text(fields, 'starts')),
    ends: maybe(fields, 'ends', () => text(fields, 'ends')),
    place: maybe(fields, 'place', (value) => {
      const place = record(value, 'place');
      return {
        lat: number(place, 'lat'),
        lon: number(place, 'lon'),
        name: text(place, 'name'),
      };
    }),
    price: maybe(fields, 'price', money),
    // Not said by a server before a thing had a money of its own: in the trip's.
    paid: maybe({ paid: fields.paid ?? null }, 'paid', money),
    // Not said by a server before a thing had files: none.
    files: list({ files: fields.files ?? [] }, 'files', (value) => {
      const file = record(value, 'file');
      return {
        id: text(file, 'id'),
        name: text(file, 'name'),
        type: maybe(file, 'type', (type) => text({ type }, 'type')),
      };
    }),
    // Not said by a server before a thing had tags: none.
    tags: list({ tags: fields.tags ?? [] }, 'tags', (tag) => text({ tag }, 'tag')),
    booked: flag(fields, 'booked'),
    link: maybe(fields, 'link', () => text(fields, 'link')),
    by: maybe({ by: fields.by ?? null }, 'by', (by) => oneOf({ by }, 'by', cityWays)),
    byMinutes: maybe({ minutes: fields.byMinutes ?? null }, 'minutes', (minutes) =>
      number({ minutes }, 'minutes'),
    ),
    parts: list({ parts: fields.parts ?? [] }, 'parts', wayPart),
  };
}

function wayPart(value: unknown): WayPart {
  const fields = record(value, 'part');
  return {
    by: oneOf(fields, 'by', cityWays),
    to: maybe(fields, 'to', (value) => {
      const to = record(value, 'to');
      return { lat: number(to, 'lat'), lon: number(to, 'lon'), name: text(to, 'name') };
    }),
    minutes: maybe(fields, 'minutes', () => number(fields, 'minutes')),
  };
}

function dayWay(value: unknown): DayWay {
  const fields = record(value, 'dayWay');
  return {
    day: text(fields, 'day'),
    leg: oneOf(fields, 'leg', dayLegs),
    by: maybe(fields, 'by', () => oneOf(fields, 'by', cityWays)),
    byMinutes: maybe(fields, 'byMinutes', () => number(fields, 'byMinutes')),
    parts: list({ parts: fields.parts ?? [] }, 'parts', wayPart),
  };
}

function booking(value: unknown): Booking {
  const fields = record(value, 'booking');
  return {
    id: text(fields, 'id'),
    kind: oneOf(fields, 'kind', ['stay', 'ticket'] as const),
    name: text(fields, 'name'),
    day: maybe(fields, 'day', (day) => text({ day }, 'day')),
    nights: number(fields, 'nights'),
    price: money(fields.price),
    // Not said by a server before a booking had a money of its own: in the trip's.
    paid: maybe({ paid: fields.paid ?? null }, 'paid', money),
    pay: oneOf(fields, 'pay', ['paid', 'later'] as const),
    reference: maybe(fields, 'reference', (reference) => text({ reference }, 'reference')),
    link: maybe(fields, 'link', (link) => text({ link }, 'link')),
    checkIn: maybe(fields, 'checkIn', () => text(fields, 'checkIn')),
    checkOut: maybe(fields, 'checkOut', () => text(fields, 'checkOut')),
    lodging: maybe({ lodging: fields.lodging ?? null }, 'lodging', (lodging) =>
      oneOf({ lodging }, 'lodging', lodgings),
    ),
    freeUntil: maybe({ until: fields.freeUntil ?? null }, 'until', (until) =>
      text({ until }, 'until'),
    ),
    qr: maybe({ qr: fields.qr ?? null }, 'qr', (qr) => text({ qr }, 'qr')),
    flight: maybe(fields, 'flight', (value) => {
      const flight = record(value, 'flight');
      return {
        number: text(flight, 'number'),
        airline: maybe(flight, 'airline', () => text(flight, 'airline')),
      };
    }),
    legs: list(fields, 'legs', (item) => {
      const leg = record(item, 'leg');
      const said = (key: string) => maybe(leg, key, () => text(leg, key));
      return {
        origin: text(leg, 'origin'),
        destination: text(leg, 'destination'),
        departureAt: said('departureAt'),
        arrivalAt: said('arrivalAt'),
        actualDepartureAt: said('actualDepartureAt'),
        actualArrivalAt: said('actualArrivalAt'),
        number: said('number'),
        operator: said('operator'),
        vehicle: said('vehicle'),
        registration: said('registration'),
        travelClass: said('travelClass'),
        carried: maybe(leg, 'carried', () => oneOf(leg, 'carried', carriedKinds)),
        carriedPlate: said('carriedPlate'),
        gate: said('gate'),
        arrivalGate: said('arrivalGate'),
        boarding: maybe(leg, 'boarding', () => oneOf(leg, 'boarding', aprons)),
        deplaning: maybe(leg, 'deplaning', () => oneOf(leg, 'deplaning', aprons)),
        terminalFrom: said('terminalFrom'),
        terminalTo: said('terminalTo'),
        airline: said('airline'),
        airlineName: said('airlineName'),
        km: maybe(leg, 'km', () => number(leg, 'km')),
        originZone: said('originZone'),
        destinationZone: said('destinationZone'),
      };
    }),
    passengers: list(fields, 'passengers', (item) => {
      const passenger = record(item, 'passenger');
      return {
        name: text(passenger, 'name'),
        traveller: maybe(passenger, 'traveller', () => text(passenger, 'traveller')),
        price: maybe(passenger, 'price', money),
        number: maybe(passenger, 'number', () => text(passenger, 'number')),
        seats: list(passenger, 'seats', (seat) =>
          seat === null ? null : text({ seat }, 'seat'),
        ),
        coaches: list(passenger, 'coaches', (coach) =>
          coach === null ? null : text({ coach }, 'coach'),
        ),
        kinds: list(passenger, 'kinds', (kind) =>
          kind === null ? null : oneOf({ kind }, 'kind', seatKinds),
        ),
        bags: maybe(passenger, 'bags', (item) => {
          const bags = record(item, 'bags');
          return {
            cabinKg: maybe(bags, 'cabinKg', () => number(bags, 'cabinKg')),
            checked: list(bags, 'checked', (kg) => number({ kg }, 'kg')),
          };
        }),
      };
    }),
    rules: ((value: unknown) => {
      const rules = record(value, 'rules');
      const count = (key: string) => maybe(rules, key, () => number(rules, key));
      return {
        cabinPieces: count('cabinPieces'),
        cabinKg: count('cabinKg'),
        cabinSize: maybe(rules, 'cabinSize', () => text(rules, 'cabinSize')),
        personalItem: flag(rules, 'personalItem'),
        checkedBags: list(rules, 'checkedBags', (kg) => number({ kg }, 'kg')),
        onlineOpensH: count('onlineOpensH'),
        onlineClosesM: count('onlineClosesM'),
        deskOpensH: count('deskOpensH'),
        deskClosesM: count('deskClosesM'),
        notes: text(rules, 'notes'),
      };
    })(fields.rules),
    place: maybe(fields, 'place', (value) => {
      const place = record(value, 'place');
      return {
        lat: number(place, 'lat'),
        lon: number(place, 'lon'),
        name: text(place, 'name'),
      };
    }),
    // Not said by a server before it kept them: nothing happened, it replaces none.
    incident: maybe({ incident: fields.incident ?? null }, 'incident', (value) => {
      const incident = record(value, 'incident');
      const said = (key: string) => maybe(incident, key, () => text(incident, key));
      return {
        kind: oneOf(incident, 'kind', incidentKinds),
        leavesAt: said('leavesAt'),
        arrivesAt: said('arrivesAt'),
        refund: maybe(incident, 'refund', () => oneOf(incident, 'refund', refunds)),
        amount: maybe(incident, 'amount', money),
        voucherUntil: said('voucherUntil'),
        lost: money(incident.lost),
      };
    }),
    replaces: maybe({ replaces: fields.replaces ?? null }, 'replaces', (value) =>
      text({ value }, 'value'),
    ),
  };
}

/** A way's end, when the answer has one: a variant's new place has none. */
function endOf(value: unknown): WayEnd | null {
  if (value === undefined || value === null) return null;
  const end = record(value, 'end');
  return {
    lat: number(end, 'lat'),
    lon: number(end, 'lon'),
    name: text(end, 'name'),
    own: flag(end, 'own'),
    airports: list(end, 'airports', (item) => {
      const airport = record(item, 'airport');
      return {
        lat: number(airport, 'lat'),
        lon: number(airport, 'lon'),
        name: text(airport, 'name'),
        detail: '',
      };
    }),
  };
}

function stop(value: unknown): PlanStop {
  const fields = record(value, 'stop');
  const cost = record(fields.cost, 'cost');
  return {
    id: text(fields, 'id'),
    city: text(fields, 'city'),
    place: maybe(fields, 'place', spot),
    country: text(fields, 'country'),
    // Not said by a server before a stop said its money.
    currency: maybe({ currency: fields.currency ?? null }, 'currency', (code) =>
      text({ code }, 'code'),
    ),
    zone: maybe(fields, 'zone', () => text(fields, 'zone')),
    transfer: flag(fields, 'transfer'),
    // Not said by a server before a way had a track of its own.
    track: maybe({ track: fields.track ?? null }, 'track', track),
    days: number(fields, 'days'),
    start: text(fields, 'start'),
    end: text(fields, 'end'),
    unresolved: flag(fields, 'unresolved'),
    accommodation: range(fields.accommodation),
    food: range(fields.food),
    local: range(fields.local),
    activities: range(fields.activities),
    // Not said by a server before it kept them: none.
    wayTimes: maybe({ wayTimes: fields.wayTimes ?? null }, 'wayTimes', (times) => {
      const said = record(times, 'wayTimes');
      return {
        departureAt: maybe(said, 'departureAt', () => text(said, 'departureAt')),
        arrivalAt: maybe(said, 'arrivalAt', () => text(said, 'arrivalAt')),
      };
    }),
    out: maybe(fields, 'out', (out) => {
      const leg = record(out, 'out');
      return {
        mode: oneOf(leg, 'mode', modes),
        estimate: range(leg.estimate),
        link: maybe(leg, 'link', (link) => text({ link }, 'link')),
        departureAt: maybe(leg, 'departureAt', () => text(leg, 'departureAt')),
        arrivalAt: maybe(leg, 'arrivalAt', () => text(leg, 'arrivalAt')),
        flight: maybe(leg, 'flight', (flight) => {
          const f = record(flight, 'flight');
          return {
            number: text(f, 'number'),
            day: text(f, 'day'),
            airline: maybe(f, 'airline', () => text(f, 'airline')),
            vehicle: maybe(f, 'vehicle', () => text(f, 'vehicle')),
          };
        }),
        minutes: maybe(leg, 'minutes', () => number(leg, 'minutes')),
        fuel: maybe(leg, 'fuel', (fuel) => {
          const f = record(fuel, 'fuel');
          return {
            km: number(f, 'km'),
            tenths: number(f, 'tenths'),
            perLitre: money(f.perLitre),
          };
        }),
        from: endOf(leg.from),
        to: endOf(leg.to),
      };
    }),
    bookings: list(fields, 'bookings', booking),
    agenda: list({ agenda: fields.agenda ?? [] }, 'agenda', agendaItem),
    dayWays: list({ ways: fields.dayWays ?? [] }, 'ways', dayWay),
    openNights: number(fields, 'openNights'),
    status: oneOf(fields, 'status', statuses),
    cost: {
      paid: money(cost.paid),
      toPay: money(cost.toPay),
      estimate: range(cost.estimate),
      total: range(cost.total),
      expected: money(cost.expected),
    },
    perDay: maybe(fields, 'perDay', range),
    expectedPerDay: maybe(fields, 'expectedPerDay', money),
    // A variant's new place says none: it goes on as the variant does.
    ways:
      fields.ways === undefined
        ? []
        : list(fields, 'ways', (item) => {
            const way = record(item, 'way');
            return { mode: oneOf(way, 'mode', modes), estimate: range(way.estimate) };
          }),
  };
}

function country(value: unknown): PlanCountry {
  const fields = record(value, 'country');
  return {
    country: text(fields, 'country'),
    days: number(fields, 'days'),
    total: range(fields.total),
    expected: money(fields.expected),
    expectedPerDay: maybe(fields, 'expectedPerDay', money),
  };
}

function change(value: unknown): Change {
  const fields = record(value, 'change');
  return {
    stop: text(fields, 'stop'),
    days: maybe(fields, 'days', (days) => number({ days }, 'days')),
    remove: flag(fields, 'remove'),
  };
}

function delta(value: unknown): Delta {
  const fields = record(value, 'delta');
  return { min: money(fields.min), expected: money(fields.expected), max: money(fields.max) };
}

function place(value: unknown): { id: string; city: string } {
  const fields = record(value, 'place');
  return { id: text(fields, 'id'), city: text(fields, 'city') };
}

function scenario(value: unknown): Scenario {
  const fields = record(value, 'scenario');
  return {
    kind: oneOf(fields, 'kind', ['shift', 'skip'] as const),
    from: place(fields.from),
    to: place(fields.to),
    days: number(fields, 'days'),
    changes: list(fields, 'changes', change),
    delta: delta(fields.delta),
  };
}

function totals(value: unknown): Plan['totals'] {
  const fields = record(value, 'totals');
  return {
    days: number(fields, 'days'),
    paid: money(fields.paid),
    toPay: money(fields.toPay),
    // Not said by a server before cancellations were kept: none, in the trip's money.
    lost: money(fields.lost ?? { minor: 0, currency: money(fields.paid).currency }),
    refundsPending: money(
      fields.refundsPending ?? { minor: 0, currency: money(fields.paid).currency },
    ),
    estimate: range(fields.estimate),
    total: range(fields.total),
    expected: money(fields.expected),
    over: maybe(fields, 'over', money),
    dearPerDay: money(fields.dearPerDay),
    // Not said by a server before documents had a price: none, in the trip's money.
    documents: money(fields.documents ?? { minor: 0, currency: money(fields.paid).currency }),
  };
}

export function parseVariants(value: unknown): Variants {
  const fields = record(value, 'variants');
  const stopOf = (item: unknown) => place(item);
  return {
    totals: totals(fields.totals),
    end: maybe(fields, 'end', () => text(fields, 'end')),
    variants: list(fields, 'variants', (item) => {
      const v = record(item, 'variant');
      return {
        id: text(v, 'id'),
        name: maybe(v, 'name', () => text(v, 'name')),
        codes: list(v, 'codes', (code) => text({ code }, 'code')),
        modes: list(v, 'modes', (mode) => oneOf({ mode }, 'mode', modes)),
        nights: list(v, 'nights', (count) => number({ count }, 'count')),
        link: maybe(v, 'link', () => text(v, 'link')),
        places: list(v, 'places', airport),
        from: maybe(v, 'from', stopOf),
        rejoin: maybe(v, 'rejoin', stopOf),
        dropped: list(v, 'dropped', (gone) => {
          const d = record(gone, 'dropped');
          return { ...place(gone), bookings: number(d, 'bookings') };
        }),
        added: list(v, 'added', stop),
        countries: list(v, 'countries', country),
        into: maybe(v, 'into', stop),
        fares: list(v, 'fares', (item) => {
          const fare = record(item, 'fare');
          return {
            price: maybe(fare, 'price', (price) => cents(price)),
            departureAt: maybe(fare, 'departureAt', () => text(fare, 'departureAt')),
            arrivalAt: maybe(fare, 'arrivalAt', () => text(fare, 'arrivalAt')),
            name: maybe(fare, 'name', () => text(fare, 'name')),
            link: maybe(fare, 'link', () => text(fare, 'link')),
          };
        }),
        stays: list(v, 'stays', stayOf),
        totals: maybe(v, 'totals', totals),
        delta: maybe(v, 'delta', delta),
        end: maybe(v, 'end', () => text(v, 'end')),
      };
    }),
  };
}

/** Reads a preview answer. */
export function parsePreview(value: unknown): Preview {
  const fields = record(value, 'preview');
  return { delta: delta(fields.delta), totals: totals(fields.totals) };
}

/** Reads a plan answer, or says what is wrong with it. */
function traveller(value: unknown): Traveller {
  const fields = record(value, 'traveller');
  return {
    id: text(fields, 'id'),
    name: text(fields, 'name'),
    joins: text(fields, 'joins'),
    leaves: text(fields, 'leaves'),
    legs: list(fields, 'legs', (item) => {
      const leg = record(item, 'leg');
      return {
        id: text(leg, 'id'),
        day: text(leg, 'day'),
        way: text(leg, 'way'),
        price: maybe(leg, 'price', money),
        reference: maybe(leg, 'reference', (reference) => text({ reference }, 'reference')),
      };
    }),
    leaveDays: number(fields, 'leaveDays'),
    holidays: list(fields, 'holidays', (item) => {
      const holiday = record(item, 'holiday');
      return { day: text(holiday, 'day'), code: text(holiday, 'code') };
    }),
  };
}

export function parsePlan(value: unknown): Plan {
  const fields = record(value, 'plan');
  const trip = record(fields.trip, 'trip');
  return {
    // Not said by a server before the calendar showed them: none.
    holidays: list({ holidays: fields.holidays ?? [] }, 'holidays', (item) => {
      const holiday = record(item, 'holiday');
      return { day: text(holiday, 'day'), code: text(holiday, 'code') };
    }),
    trip: {
      id: text(trip, 'id'),
      name: text(trip, 'name'),
      purpose: purposeOf(trip),
      start: text(trip, 'start'),
      end: text(trip, 'end'),
      budget: money(trip.budget),
      currency: text(trip, 'currency'),
      // Not said by a server before it counted them: one.
      people: number({ people: trip.people ?? 1 }, 'people'),
      // Not said by a server before trips were archived: none is.
      archivedAt: maybe({ archivedAt: trip.archivedAt ?? null }, 'archivedAt', (archivedAt) =>
        text({ archivedAt }, 'archivedAt'),
      ),
    },
    stops: list(fields, 'stops', stop),
    totals: totals(fields.totals),
    countries: list(fields, 'countries', country),
    car: carOf(fields.car),
    biggestShare: maybe(fields, 'biggestShare', (share) => {
      const shareFields = record(share, 'biggestShare');
      return {
        country: text(shareFields, 'country'),
        basisPoints: number(shareFields, 'basisPoints'),
      };
    }),
    travellers: list(fields, 'travellers', traveller),
    whatIf: list(fields, 'whatIf', scenario),
    role: oneOf(fields, 'role', roles),
  };
}

/** Reads a way's track. */
function track(value: unknown): Track {
  const fields = record(value, 'track');
  const whole = (key: string) => maybe(fields, key, () => number(fields, key));
  return {
    name: text(fields, 'name'),
    format: oneOf(fields, 'format', [
      'gpx',
      'kml',
      'kmz',
      'geojson',
      'csv',
      'fit',
      'tcx',
      'drawn',
    ] as const),
    path: list(fields, 'path', pathPoint),
    heights: list(fields, 'heights', (height) =>
      height === null ? null : number({ height }, 'height'),
    ),
    // Not said by a server before a track kept its times.
    times: list({ times: fields.times ?? [] }, 'times', (time) =>
      time === null ? null : number({ time }, 'time'),
    ),
    metres: number(fields, 'metres'),
    ascent: whole('ascent'),
    descent: whole('descent'),
    lowest: whole('lowest'),
    highest: whole('highest'),
    seconds: whole('seconds'),
    anchors: maybe({ anchors: fields.anchors ?? null }, 'anchors', (value) => {
      const anchors = record(value, 'anchors');
      return {
        mode: oneOf(anchors, 'mode', ['foot', 'bike', 'car', 'straight'] as const),
        points: list(anchors, 'points', (one) => {
          const point = record(one, 'point');
          return {
            lon: number(point, 'lon'),
            lat: number(point, 'lat'),
            name: text(point, 'name'),
          };
        }),
      };
    }),
  };
}

/** Reads a draft booking. */
export function parseDraft(value: unknown): Draft {
  const fields = record(value, 'draft');
  return {
    stop: maybe(fields, 'stop', (stop) => text({ stop }, 'stop')),
    kind: oneOf(fields, 'kind', ['stay', 'ticket'] as const),
    name: text(fields, 'name'),
    day: maybe(fields, 'day', (day) => text({ day }, 'day')),
    nights: number(fields, 'nights'),
    price: maybe(fields, 'price', (price) => number({ price }, 'price')),
    currency: maybe(fields, 'currency', (currency) => text({ currency }, 'currency')),
    pay: oneOf(fields, 'pay', ['paid', 'later'] as const),
    reference: maybe(fields, 'reference', (reference) => text({ reference }, 'reference')),
    link: maybe(fields, 'link', (link) => text({ link }, 'link')),
    // A file's draft only says its ride.
    ...('ticket' in fields ? { ticket: maybe(fields, 'ticket', draftTicket) } : {}),
  };
}

function draftTicket(value: unknown): DraftTicket {
  const fields = record(value, 'ticket');
  const said = (leg: Fields, key: string) => maybe(leg, key, (v) => text({ [key]: v }, key));
  return {
    mode: maybe(fields, 'mode', (mode) =>
      oneOf({ mode }, 'mode', ['bus', 'train', 'ferry', 'flight'] as const),
    ),
    legs: list(fields, 'legs', (item) => {
      const leg = record(item, 'leg');
      return {
        origin: text(leg, 'origin'),
        destination: text(leg, 'destination'),
        departure: text(leg, 'departure'),
        arrival: text(leg, 'arrival'),
        number: said(leg, 'number'),
        operator: said(leg, 'operator'),
        vehicle: said(leg, 'vehicle'),
        platform: said(leg, 'platform'),
        seat: said(leg, 'seat'),
        passenger: said(leg, 'passenger'),
        ticket: said(leg, 'ticket'),
      };
    }),
  };
}

/** Reads the trip list. */
function airport(value: unknown): Airport {
  const fields = record(value, 'airport');
  return {
    code: text(fields, 'code'),
    city: text(fields, 'city'),
    name: text(fields, 'name'),
    country: text(fields, 'country'),
    lat: number(fields, 'lat'),
    lon: number(fields, 'lon'),
    // A comparison's airports never are towns, and do not say so.
    town: fields.town === true,
  };
}

/** A point of a path, `[lon, lat]`. */
function pathPoint(pair: unknown): [number, number] {
  if (!Array.isArray(pair) || typeof pair[0] !== 'number' || typeof pair[1] !== 'number') {
    throw new ApiError('path: expected [lon, lat]');
  }
  return [pair[0], pair[1]];
}

export function parseRoad(value: unknown): Road | null {
  const fields = record(value, 'road');
  return maybe(fields, 'road', (item) => {
    const road = record(item, 'road');
    return {
      km: number(road, 'km'),
      metres: number({ metres: road.metres ?? Number(road.km) * 1000 }, 'metres'),
      minutes: number(road, 'minutes'),
      ferryKm: number(road, 'ferryKm'),
      path: list(road, 'path', pathPoint),
      turns: list({ turns: road.turns ?? [] }, 'turns', (one) => {
        const turn = record(one, 'turn');
        return {
          at: pathPoint(turn.at),
          kind: text(turn, 'kind'),
          towards: text(turn, 'towards'),
          street: text(turn, 'street'),
          metres: number(turn, 'metres'),
        };
      }),
    };
  });
}

export function parseRide(value: unknown): Ride | null {
  const fields = record(value, 'transit');
  return maybe(fields, 'ride', (item) => {
    const ride = record(item, 'ride');
    return {
      minutes: number(ride, 'minutes'),
      legs: list(ride, 'legs', (one) => {
        const leg = record(one, 'leg');
        return {
          mode: text(leg, 'mode'),
          line: maybe(leg, 'line', () => text(leg, 'line')),
          color: maybe(leg, 'color', () => text(leg, 'color')),
          headsign: maybe(leg, 'headsign', () => text(leg, 'headsign')),
          from: text(leg, 'from'),
          to: text(leg, 'to'),
          minutes: number(leg, 'minutes'),
          path: list({ path: leg.path ?? [] }, 'path', pathPoint),
        };
      }),
      path: list(ride, 'path', pathPoint),
    };
  });
}

/** A town's metro, light rail and monorail lines in their own colours, and their stations. */
export interface Metro {
  lines: {
    ref: string;
    name: string;
    /** `#FF9500`, or a colour's name. */
    colour: string;
    /** Each track of it, `[lon, lat]` along it. */
    paths: [number, number][][];
  }[];
  stations: { name: string; lat: number; lon: number }[];
}

export function parseMetro(value: unknown): Metro | null {
  const fields = record(value, 'metro');
  return maybe(fields, 'metro', (item) => {
    const metro = record(item, 'metro');
    return {
      lines: list(metro, 'lines', (one) => {
        const line = record(one, 'line');
        return {
          ref: text(line, 'ref'),
          name: text(line, 'name'),
          colour: text(line, 'colour'),
          paths: list(line, 'paths', (path) => list({ path }, 'path', pathPoint)),
        };
      }),
      stations: list(metro, 'stations', (one) => {
        const station = record(one, 'station');
        return {
          name: text(station, 'name'),
          lat: number(station, 'lat'),
          lon: number(station, 'lon'),
        };
      }),
    };
  });
}

export function parseRoute(value: unknown): RouteSearch {
  const fields = record(value, 'route');
  return {
    typed: text(fields, 'typed'),
    flights: list(fields, 'flights', (item) => {
      const flight = record(item, 'flight');
      return {
        from: text(flight, 'from'),
        to: text(flight, 'to'),
        day: text(flight, 'day'),
        flight: maybe(flight, 'flight', () => text(flight, 'flight')),
      };
    }),
    price: maybe(fields, 'price', money),
    parts: list(fields, 'parts', (item) => {
      const part = record(item, 'part');
      return {
        text: text(part, 'text'),
        kind: oneOf(part, 'kind', partKinds),
        place: maybe(part, 'place', airport),
        options: list(part, 'options', airport),
      };
    }),
    legs: list(fields, 'legs', (item) => {
      const leg = record(item, 'leg');
      return {
        from: airport(leg.from),
        to: airport(leg.to),
        km: number(leg, 'km'),
        direct: flag(leg, 'direct'),
        minutes: number(leg, 'minutes'),
        estimate: range(leg.estimate),
        via: list(leg, 'via', (stop) => {
          const via = record(stop, 'via');
          return { place: airport(via.place), detour: number(via, 'detour') };
        }),
      };
    }),
    total: maybe(fields, 'total', range),
  };
}

export function parseTowns(value: unknown): TownSearch {
  const fields = record(value, 'towns');
  return {
    towns: list(fields, 'towns', (item) => {
      const town = record(item, 'town');
      return {
        name: text(town, 'name'),
        country: text(town, 'country'),
        lat: number(town, 'lat'),
        lon: number(town, 'lon'),
        detail: text(town, 'detail'),
        airport: flag(town, 'airport'),
      };
    }),
    osm: oneOf(fields, 'osm', osmStates),
  };
}

export function parseComparison(value: unknown): Comparison {
  const fields = record(value, 'comparison');
  return {
    stop: text(fields, 'stop'),
    from: text(fields, 'from'),
    to: text(fields, 'to'),
    estimate: maybe(fields, 'estimate', range),
    perDay: maybe(fields, 'perDay', money),
    text: text(fields, 'text'),
    maxLegs: number(fields, 'maxLegs'),
    chosen: maybe(fields, 'chosen', (chosen) => text({ chosen }, 'chosen')),
    flights: list(fields, 'flights', (item) => {
      const flight = record(item, 'flight');
      return {
        from: airport(flight.from),
        via: list(flight, 'via', airport),
        to: airport(flight.to),
        price: money(flight.price),
        paid: flag(flight, 'paid'),
        line: number(flight, 'line'),
        link: maybe(flight, 'link', (link) => text({ link }, 'link')),
      };
    }),
    hotels: list(fields, 'hotels', (item) => {
      const hotel = record(item, 'hotel');
      return {
        at: airport(hotel.at),
        price: money(hotel.price),
        nights: number(hotel, 'nights'),
        line: number(hotel, 'line'),
      };
    }),
    problems: list(fields, 'problems', (item) => {
      const problem = record(item, 'problem');
      return {
        line: number(problem, 'line'),
        text: text(problem, 'text'),
        problem: oneOf(problem, 'problem', ['unread', 'code'] as const),
      };
    }),
    paths: list(fields, 'paths', (item) => {
      const path = record(item, 'path');
      return {
        codes: list(path, 'codes', (code) => text({ code }, 'code')),
        flights: list(path, 'flights', (at) => number({ at }, 'at')),
        stopovers: list(path, 'stopovers', (stop) => {
          const stopover = record(stop, 'stopover');
          return {
            at: airport(stopover.at),
            nights: number(stopover, 'nights'),
            cost: money(stopover.cost),
          };
        }),
        flightsCost: money(path.flightsCost),
        hotelsCost: money(path.hotelsCost),
        lost: money(path.lost),
        total: money(path.total),
        nights: number(path, 'nights'),
        skipsPaid: flag(path, 'skipsPaid'),
        cheapest: flag(path, 'cheapest'),
        fewest: flag(path, 'fewest'),
        change: money(path.change),
        co2: number(path, 'co2'),
      };
    }),
  };
}

export function parseKept(value: unknown): KeptLeg[] {
  return list({ kept: value }, 'kept', (item) => {
    const kept = record(item, 'kept');
    return {
      stop: text(kept, 'stop'),
      options: number(kept, 'options'),
      chosen: maybe(kept, 'chosen', (chosen) => text({ chosen }, 'chosen')),
    };
  });
}

/** An account. */
export interface User {
  id: string;
  email: string;
  name: string;
  language: string;
  /** It runs the instance: the first account, which refreshes its reference data. */
  owner: boolean;
}

/** A trip as a viewer sees it: the route and the dates, no money. */
export interface TripView {
  role: Role;
  trip: { id: string; name: string; start: string; end: string };
  stops: {
    id: string;
    city: string;
    country: string;
    days: number;
    start: string;
    end: string;
    unresolved: boolean;
    mode: Mode | null;
    place: Placed | null;
  }[];
  /** Each fellow traveller; a name left out when the link's owner chose so. */
  travellers: { name: string | null; joins: string; leaves: string }[];
  /** The budget and what is expected, when a shared link shows amounts. */
  amounts: { budget: Money; expected: Money; stops: { id: string; expected: Money }[] } | null;
  /** The files a shared link shows, each fetched at its address. */
  files: { id: string; name: string; type: string; size: number; url: string }[];
  /** The journal's days a shared link shows: those written or with photos. */
  journal: JournalDay[];
}

/** Where a stop is drawn: a point, guessed from its airport or picked. */
function spot(value: unknown): Placed {
  const fields = record(value, 'place');
  return {
    lat: number(fields, 'lat'),
    lon: number(fields, 'lon'),
    guessed: flag(fields, 'guessed'),
    name: maybe(fields, 'name', () => text(fields, 'name')),
  };
}

export function parseUser(value: unknown): User {
  const answer = record(value, 'answer');
  const fields = record(answer.user, 'user');
  return {
    id: text(fields, 'id'),
    email: text(fields, 'email'),
    name: text(fields, 'name'),
    language: text(fields, 'language'),
    owner: answer.owner === true,
  };
}

export function parseView(value: unknown): TripView {
  const fields = record(value, 'view');
  const trip = record(fields.trip, 'trip');
  return {
    role: oneOf(fields, 'role', roles),
    trip: {
      id: text(trip, 'id'),
      name: text(trip, 'name'),
      start: text(trip, 'start'),
      end: text(trip, 'end'),
    },
    stops: list(fields, 'stops', (item) => {
      const stop = record(item, 'stop');
      return {
        id: text(stop, 'id'),
        city: text(stop, 'city'),
        country: text(stop, 'country'),
        days: number(stop, 'days'),
        start: text(stop, 'start'),
        end: text(stop, 'end'),
        unresolved: flag(stop, 'unresolved'),
        mode: maybe(stop, 'mode', (mode) => oneOf({ mode }, 'mode', modes)),
        place: maybe(stop, 'place', spot),
      };
    }),
    travellers: list(fields, 'travellers', (item) => {
      const traveller = record(item, 'traveller');
      return {
        name: maybe(traveller, 'name', () => text(traveller, 'name')),
        joins: text(traveller, 'joins'),
        leaves: text(traveller, 'leaves'),
      };
    }),
    // A member's view says neither.
    amounts: maybe({ amounts: fields.amounts ?? null }, 'amounts', (value) => {
      const amounts = record(value, 'amounts');
      return {
        budget: money(amounts.budget),
        expected: money(amounts.expected),
        stops: list(amounts, 'stops', (item) => {
          const stop = record(item, 'stop');
          return { id: text(stop, 'id'), expected: money(stop.expected) };
        }),
      };
    }),
    files: list({ files: fields.files ?? [] }, 'files', (item) => {
      const file = record(item, 'file');
      return {
        id: text(file, 'id'),
        name: text(file, 'name'),
        type: text(file, 'type'),
        size: number(file, 'size'),
        url: text(file, 'url'),
      };
    }),
    journal: list({ journal: fields.journal ?? [] }, 'journal', journalDay),
  };
}

/** A member of a trip. */
export interface Member {
  userId: string;
  name: string;
  email: string;
  role: Role;
}

/** An invitation not accepted yet. */
export interface Invitation {
  id: string;
  email: string;
  role: Role;
  invitedBy: string | null;
  expires: string;
}

/** Who is on a trip, who is invited, and since when it has a read-only link. */
export interface People {
  members: Member[];
  invites: Invitation[];
  shared: string | null;
  /** The server can e-mail invitations. */
  mail: boolean;
  role: Role;
}

/** An invitation as its link shows it. */
export interface InvitationView {
  tripName: string;
  invitedBy: string | null;
  email: string;
  role: Role;
  /** The e-mail already has an account: sign in to accept. */
  account: boolean;
}

function optionalText(fields: Fields, key: string): string | null {
  return maybe(fields, key, (value) => text({ value }, 'value'));
}

function invitation(value: unknown): Invitation {
  const fields = record(value, 'invite');
  return {
    id: text(fields, 'id'),
    email: text(fields, 'email'),
    role: oneOf(fields, 'role', roles),
    invitedBy: optionalText(fields, 'invitedBy'),
    expires: text(fields, 'expires'),
  };
}

export function parsePeople(value: unknown): People {
  const fields = record(value, 'people');
  return {
    members: list(fields, 'members', (item) => {
      const member = record(item, 'member');
      return {
        userId: text(member, 'userId'),
        name: text(member, 'name'),
        email: text(member, 'email'),
        role: oneOf(member, 'role', roles),
      };
    }),
    invites: list(fields, 'invites', invitation),
    shared: optionalText(fields, 'shared'),
    mail: flag(fields, 'mail'),
    role: oneOf(fields, 'role', roles),
  };
}

export function parseInvited(value: unknown): {
  invite: Invitation;
  link: string;
  sent: boolean;
} {
  const fields = record(value, 'invited');
  return {
    invite: invitation(fields.invite),
    link: text(fields, 'link'),
    sent: flag(fields, 'sent'),
  };
}

export function parseInvitation(value: unknown): InvitationView {
  const fields = record(value, 'invitation');
  return {
    tripName: text(fields, 'tripName'),
    invitedBy: optionalText(fields, 'invitedBy'),
    email: text(fields, 'email'),
    role: oneOf(fields, 'role', roles),
    account: flag(fields, 'account'),
  };
}

/** One person's part of an expense. */
export interface Share {
  userId: string;
  name: string;
  amount: Money;
}

/** Something paid for others, or a debt paid back (`settlement`). */
export interface Expense {
  id: string;
  day: string;
  item: string;
  /** What was paid, in the money it was paid in: ¥51,000. */
  paid: Money;
  /** What it came to in the trip's currency: what is shared. */
  amount: Money;
  paidBy: string;
  payer: string;
  shares: Share[];
  settlement: boolean;
  /** It has a photo of its bill. */
  photo: boolean;
}

/** Where a member stands: paid, shares, and paid less shares. */
export interface Balance {
  userId: string;
  name: string;
  paid: Money;
  share: Money;
  net: Money;
}

/** A trip's shared costs. */
export interface Ledger {
  currency: string;
  /** The account asking. */
  me: string;
  expenses: Expense[];
  balances: Balance[];
  /** The payments that would settle up. */
  transfers: {
    from: { userId: string; name: string };
    to: { userId: string; name: string };
    amount: Money;
  }[];
  /** Expenses without a bill photo. */
  missingPhotos: number;
  /** Who travels along with no account, by id: they pay and share too. */
  fellows: { userId: string; name: string }[];
  /** Each stop's days, `until` the day after its last, and the money of its country. */
  stays: { from: string; until: string; currency: string | null }[];
  /** Every money a cost can be paid in. */
  currencies: string[];
}

/** An expense as sent. */
export interface ExpenseInput {
  day: string;
  item: string;
  /** What was paid, in minor units of `currency`. */
  paid: number;
  currency: string;
  /** What the bank charged in the trip's currency; `null` for the trip's rate of the day. */
  amount: number | null;
  paidBy: string;
  sharedWith: string[];
}

function person(value: unknown): { userId: string; name: string } {
  const fields = record(value, 'person');
  return { userId: text(fields, 'userId'), name: text(fields, 'name') };
}

export function parseLedger(value: unknown): Ledger {
  const fields = record(value, 'ledger');
  return {
    currency: text(fields, 'currency'),
    me: text(fields, 'me'),
    expenses: list(fields, 'expenses', (item) => {
      const expense = record(item, 'expense');
      return {
        id: text(expense, 'id'),
        day: text(expense, 'day'),
        item: text(expense, 'item'),
        paid: money(expense.paid),
        amount: money(expense.amount),
        paidBy: text(expense, 'paidBy'),
        payer: text(expense, 'payer'),
        shares: list(expense, 'shares', (share) => ({
          ...person(share),
          amount: money(record(share, 'share').amount),
        })),
        settlement: flag(expense, 'settlement'),
        photo: flag(expense, 'photo'),
      };
    }),
    balances: list(fields, 'balances', (item) => {
      const balance = record(item, 'balance');
      return {
        ...person(balance),
        paid: money(balance.paid),
        share: money(balance.share),
        net: money(balance.net),
      };
    }),
    transfers: list(fields, 'transfers', (item) => {
      const transfer = record(item, 'transfer');
      return {
        from: person(transfer.from),
        to: person(transfer.to),
        amount: money(transfer.amount),
      };
    }),
    missingPhotos: number(fields, 'missingPhotos'),
    fellows: list({ fellows: fields.fellows ?? [] }, 'fellows', person),
    stays: list(fields, 'stays', (item) => {
      const stay = record(item, 'stay');
      return {
        from: text(stay, 'from'),
        until: text(stay, 'until'),
        currency: optionalText(stay, 'currency'),
      };
    }),
    currencies: list(fields, 'currencies', (item) => text({ currency: item }, 'currency')),
  };
}

/** Something done on a trip: by whom (none for the read-only link), what, when. */
export interface ActivityEntry {
  id: string;
  who: { userId: string; name: string } | null;
  /** `costs.add`, `route.change`, `share.open`. */
  code: string;
  at: string;
  /** The assistant it was done through: `Claude`. */
  via: string | null;
  /** What it is about: an area's name. */
  about: string | null;
}

/** Who did it, as said: `Kadri`, `Claude (Kadri)` through an assistant, else `someone`. */
export function doneBy(entry: ActivityEntry, someone: string): string {
  const name = entry.who ? entry.who.name : someone;
  return entry.via === null ? name : `${entry.via} (${name})`;
}

/** A trip's latest activity, and who has it open now. */
export interface Activity {
  entries: ActivityEntry[];
  online: { userId: string; name: string }[];
}

export function parseEntry(value: unknown): ActivityEntry {
  const fields = record(value, 'entry');
  return {
    id: text(fields, 'id'),
    who: maybe(fields, 'who', person),
    code: text(fields, 'code'),
    at: text(fields, 'at'),
    via: optionalText(fields, 'via'),
    about: optionalText(fields, 'about'),
  };
}

export function parseOnline(value: unknown): { userId: string; name: string }[] {
  return list({ online: value }, 'online', person);
}

export function parseActivity(value: unknown): Activity {
  const fields = record(value, 'activity');
  return { entries: list(fields, 'entries', parseEntry), online: parseOnline(fields.online) };
}

export function parseTrips(value: unknown): TripSummary[] {
  return list({ trips: value }, 'trips', summary);
}

function problems(value: unknown): FieldProblem[] {
  return list(record(value, 'error'), 'fields', (item) => {
    const fields = record(item, 'field');
    return { field: text(fields, 'field'), problem: text(fields, 'problem') };
  });
}

// ---- calling the API ----

async function call<T>(
  path: string,
  init: RequestInit,
  read: (body: unknown) => T,
  contentType = 'application/json',
): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { accept: 'application/json', 'content-type': contentType },
  });
  if (response.status === 422) {
    throw new InvalidInput(problems(await response.json()));
  }
  if (response.status === 404) {
    throw new NotFound();
  }
  if (response.status === 401) {
    window.dispatchEvent(new Event(SIGNED_OUT));
    throw new SignedOut();
  }
  if (response.status === 403) {
    throw new Forbidden();
  }
  if (response.status === 409 && (await response.clone().text()).includes('"archived"')) {
    throw new Archived();
  }
  if (!response.ok) {
    throw new ApiError(`${init.method ?? 'GET'} ${path} answered ${response.status}`);
  }
  return read(response.status === 204 ? null : await response.json());
}

const json = (method: string, body: unknown): RequestInit => ({
  method,
  body: JSON.stringify(body),
});

/** Every trip. */
export function listTrips(): Promise<TripSummary[]> {
  return call('/_/api/trips', {}, parseTrips);
}

/** Creates a trip and answers with its plan. */
export function createTrip(input: TripInput): Promise<Plan> {
  return call('/_/api/trips', json('POST', input), parsePlan);
}

/** A trip's plan. */
export function getPlan(trip: string): Promise<Plan> {
  return call(`/_/api/trips/${encodeURIComponent(trip)}`, {}, parsePlan);
}

/** Replaces a trip's fields. */
export function updateTrip(trip: string, input: TripInput): Promise<Plan> {
  return call(`/_/api/trips/${encodeURIComponent(trip)}`, json('PUT', input), parsePlan);
}

/** Deletes a trip. */
export function deleteTrip(trip: string): Promise<null> {
  return call(`/_/api/trips/${encodeURIComponent(trip)}`, { method: 'DELETE' }, () => null);
}

function weatherParts(fields: Record<string, unknown>): WeatherPart[] {
  // An older answer kept on the page says none.
  return list({ parts: fields.parts ?? [] }, 'parts', (item) => {
    const part = record(item, 'part');
    return {
      sky: oneOf(part, 'sky', skies),
      temp: number(part, 'temp'),
      rainChance: maybe(part, 'rainChance', () => number(part, 'rainChance')),
      rainMm: number(part, 'rainMm'),
    };
  });
}

function usualWeather(value: unknown): UsualWeather {
  const fields = record(value, 'usual');
  return {
    high: number(fields, 'high'),
    low: number(fields, 'low'),
    highFrom: number(fields, 'highFrom'),
    highTo: number(fields, 'highTo'),
    lowFrom: number(fields, 'lowFrom'),
    lowTo: number(fields, 'lowTo'),
    rainy: number(fields, 'rainy'),
    rainMm: number(fields, 'rainMm'),
    wind: number(fields, 'wind'),
    sky: oneOf(fields, 'sky', skies),
    days: number(fields, 'days'),
    parts: weatherParts(fields),
  };
}

function dayWeather(value: unknown): DayWeather {
  const fields = record(value, 'day');
  const day = text(fields, 'day');
  if (fields.kind === null) return { day, kind: null };
  const clock = (key: string) => maybe(fields, key, () => text(fields, key));
  return {
    day,
    kind: oneOf(fields, 'kind', ['was', 'forecast', 'usual'] as const),
    sky: oneOf(fields, 'sky', skies),
    high: number(fields, 'high'),
    low: number(fields, 'low'),
    rainChance: maybe(fields, 'rainChance', () => number(fields, 'rainChance')),
    rainMm: number(fields, 'rainMm'),
    wind: number(fields, 'wind'),
    sunrise: clock('sunrise'),
    sunset: clock('sunset'),
    parts: weatherParts(fields),
    usual: maybe(fields, 'usual', usualWeather),
  };
}

/** A trip's weather as the server sends it. */
export function parseWeather(value: unknown): TripWeather {
  const fields = record(value, 'weather');
  const years = list(fields, 'years', (year) => number({ year }, 'year'));
  return {
    weather: oneOf(fields, 'weather', ['ok', 'failed', 'off'] as const),
    today: text(fields, 'today'),
    forecastDays: number(fields, 'forecastDays'),
    years: [years[0] ?? 0, years[1] ?? 0],
    stops: list(fields, 'stops', (item) => {
      const stop = record(item, 'stop');
      return {
        stop: text(stop, 'stop'),
        city: text(stop, 'city'),
        days: list(stop, 'days', dayWeather),
      };
    }),
    ways: list({ ways: fields.ways ?? [] }, 'ways', (item) => {
      const way = record(item, 'way');
      return {
        stop: text(way, 'stop'),
        cells: list(way, 'cells', (one) => {
          const cell = record(one, 'cell');
          return {
            from: text(cell, 'from'),
            to: text(cell, 'to'),
            day: text(cell, 'day'),
            lat: number(cell, 'lat'),
            lon: number(cell, 'lon'),
            zone: maybe(cell, 'zone', () => text(cell, 'zone')),
            kind: oneOf(cell, 'kind', ['was', 'forecast'] as const),
            sky: oneOf(cell, 'sky', skies),
            temp: number(cell, 'temp'),
            rainChance: maybe(cell, 'rainChance', () => number(cell, 'rainChance')),
            night: flag(cell, 'night'),
          };
        }),
      };
    }),
  };
}

/** Each stop's days with their weather. */
export function getWeather(trip: string): Promise<TripWeather> {
  return call(`/_/api/trips/${encodeURIComponent(trip)}/weather`, {}, parseWeather);
}

/** The trip planned again from another first day; answers the copy's plan. */
export function copyTrip(trip: string, input: CopyInput): Promise<Plan> {
  return call(`/_/api/trips/${encodeURIComponent(trip)}/copy`, json('POST', input), parsePlan);
}

/** Archives a trip (`true`) or restores it (`false`). */
export function setArchived(trip: string, archived: boolean): Promise<null> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/archive`,
    { method: archived ? 'PUT' : 'DELETE' },
    () => null,
  );
}

/** Adds a stop at the end of the route. */
export function addStop(trip: string, input: StopInput): Promise<Plan> {
  return call(`/_/api/trips/${encodeURIComponent(trip)}/stops`, json('POST', input), parsePlan);
}

const variantsPath = (trip: string) => `/_/api/trips/${encodeURIComponent(trip)}/variants`;

/** The plan's totals and its variants, each as the trip would be with it. */
export function getVariants(trip: string): Promise<Variants> {
  return call(variantsPath(trip), {}, parseVariants);
}

/** Keeps a drafted route as a variant, as [`addRoute`] would add it. */
export function keepVariant(
  trip: string,
  codes: string[],
  modes: Mode[],
  nights: number[],
  link: string | null,
  name: string | null,
): Promise<Variants> {
  return call(
    variantsPath(trip),
    json('POST', { codes, modes, nights, link, name }),
    parseVariants,
  );
}

/** Forgets a variant. */
export function forgetVariant(trip: string, id: string): Promise<Variants> {
  return call(
    `${variantsPath(trip)}/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    parseVariants,
  );
}

/** The trip with these variants, one after another: its totals, what they change, its end. */
export interface Together {
  totals: Plan['totals'];
  delta: Delta;
  end: string | null;
}

export function parseTogether(value: unknown): Together {
  const fields = record(value, 'together');
  return {
    totals: totals(fields.totals),
    delta: delta(fields.delta),
    end: maybe(fields, 'end', () => text(fields, 'end')),
  };
}

/** The trip with these variants together, the plan untouched. */
export function getTogether(trip: string, ids: readonly string[]): Promise<Together> {
  const q = ids.map(encodeURIComponent).join(',');
  return call(`${variantsPath(trip)}/together?ids=${q}`, {}, parseTogether);
}

/** What a trip's car uses and its prices of a litre. */
function carOf(value: unknown): Plan['car'] {
  const car = record(value, 'car');
  return {
    electric: flag(car, 'electric'),
    own: flag(car, 'own'),
    useTenths: number(car, 'useTenths'),
    prices: list(car, 'prices', (item) => {
      const price = record(item, 'price');
      return {
        country: text(price, 'country'),
        perLitre: money(price.perLitre),
        own: flag(price, 'own'),
      };
    }),
  };
}

/**
 * Keeps what the trip's car uses, in tenths of a litre per 100 km, and its own price of a litre
 * by country; each way by car not bought yet is priced again.
 */
export function updateCar(
  trip: string,
  car: { electric: boolean; own: boolean; useTenths: number },
  prices: { country: string; perLitre: number }[],
): Promise<Plan> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/car`,
    json('PUT', { ...car, prices }),
    parsePlan,
  );
}

/**
 * Keeps what is said of a variant's places and ways: the nights at each place after the first,
 * each leg's price and times, each stay's costs.
 */
export function adjustVariant(
  trip: string,
  id: string,
  said: { nights: number[]; fares: Fare[]; stays: (Stay | null)[] },
): Promise<Variants> {
  return call(
    `${variantsPath(trip)}/${encodeURIComponent(id)}`,
    json('PUT', said),
    parseVariants,
  );
}

/** What a hotel's link and its page say of it. */
export interface LinkReading {
  name: string | null;
  /** Its own address, without the search's parameters. */
  link: string | null;
  image: string | null;
  city: string | null;
  nights: number | null;
  /** A night's price for the nights the link searched, in cents. */
  nightly: number | null;
  /** Whether the page itself was read, not only the link. */
  read: boolean;
}

/** What a hotel's link says: its name, its own address, a night's price. */
export function readLink(url: string): Promise<LinkReading> {
  return call(`/_/api/links?url=${encodeURIComponent(url)}`, {}, (value) => {
    const f = record(value, 'link');
    const maybeText = (key: string) => maybe(f, key, () => text(f, key));
    return {
      name: maybeText('name'),
      link: maybeText('link'),
      image: maybeText('image'),
      city: maybeText('city'),
      nights: maybe(f, 'nights', () => number(f, 'nights')),
      nightly: maybe(f, 'nightly', () => number(f, 'nightly')),
      read: flag(f, 'read'),
    };
  });
}

/** Puts these variants into the plan, one after another. */
export function applyVariants(trip: string, ids: readonly string[]): Promise<Plan> {
  return call(`${variantsPath(trip)}/apply`, json('POST', { ids }), parsePlan);
}

/** Keeps a GPX, KML, KMZ, `GeoJSON`, CSV, FIT or TCX file as the track of a stop's way on. */
export function setTrack(trip: string, stop: string, file: File): Promise<Plan> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/stops/${encodeURIComponent(stop)}/track?name=${encodeURIComponent(file.name)}`,
    { method: 'PUT', body: file },
    parsePlan,
    file.type || 'application/octet-stream',
  );
}

/** Takes the track off a stop's way on: drawn as worked out again. */
export function removeTrack(trip: string, stop: string): Promise<Plan> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/stops/${encodeURIComponent(stop)}/track`,
    { method: 'DELETE' },
    parsePlan,
  );
}

export function setTransfer(trip: string, stop: string, transfer: boolean): Promise<Plan> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/stops/${encodeURIComponent(stop)}/transfer`,
    json('PUT', { transfer }),
    parsePlan,
  );
}

/** Keeps a ticket's legs and the people on it, in place of what it had. */
export function setTicket(trip: string, booking: string, ticket: TicketInput): Promise<Plan> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/bookings/${encodeURIComponent(booking)}/ticket`,
    json('PUT', ticket),
    parsePlan,
  );
}

/** Says where a stop's way on leaves from and arrives at, each `null` to work it out again. */
export function setEnds(
  trip: string,
  stop: string,
  ends: { from: Spot | null; to: Spot | null },
): Promise<Plan> {
  const end = (spot: Spot | null) => spot && { lat: spot.lat, lon: spot.lon, name: spot.name };
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/stops/${encodeURIComponent(stop)}/ends`,
    json('PUT', { from: end(ends.from), to: end(ends.to) }),
    parsePlan,
  );
}

/** Takes back the variant last used: its stops back with their bookings, and the variants. */
export function undoVariant(trip: string): Promise<Plan> {
  return call(`/_/api/trips/${encodeURIComponent(trip)}/undo`, json('POST', {}), parsePlan);
}

/** Puts a variant into the plan, the stops it leaves out removed with their bookings. */
export function applyVariant(trip: string, id: string): Promise<Plan> {
  return call(
    `${variantsPath(trip)}/${encodeURIComponent(id)}/apply`,
    json('POST', {}),
    parsePlan,
  );
}

/** Replaces a stop's fields. */
export function updateStop(trip: string, stop: string, input: StopInput): Promise<Plan> {
  const path = `/_/api/trips/${encodeURIComponent(trip)}/stops/${encodeURIComponent(stop)}`;
  return call(path, json('PUT', input), parsePlan);
}

/** Removes a stop. */
export function deleteStop(trip: string, stop: string): Promise<Plan> {
  const path = `/_/api/trips/${encodeURIComponent(trip)}/stops/${encodeURIComponent(stop)}`;
  return call(path, { method: 'DELETE' }, parsePlan);
}

/** Reads a typed line into a draft booking for the trip; nothing is saved. */
export function readLine(trip: string, line: string): Promise<Draft> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/bookings/read`,
    json('POST', { text: line }),
    parseDraft,
  );
}

/** Reads a confirmation PDF into a draft booking for the trip; nothing is saved. */
export function readPdf(trip: string, file: Blob): Promise<Draft> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/bookings/pdf`,
    { method: 'POST', body: file },
    parseDraft,
    'application/pdf',
  );
}

/**
 * Reads a photo of a confirmation or receipt, or a scanned PDF's page drawn as one, into a draft
 * booking for the trip: its words are read by the server's OCR. Nothing is saved.
 */
export function readImage(trip: string, picture: Blob): Promise<Draft> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/bookings/image`,
    { method: 'POST', body: picture },
    parseDraft,
    picture.type || 'image/png',
  );
}

/** Adds a booking to a stop. */
export function addBooking(trip: string, input: BookingInput): Promise<Plan> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/bookings`,
    json('POST', input),
    parsePlan,
  );
}

/** Replaces a booking's fields, possibly moving it to another stop. */
export function updateBooking(
  trip: string,
  booking: string,
  input: BookingInput,
): Promise<Plan> {
  const path = `/_/api/trips/${encodeURIComponent(trip)}/bookings/${encodeURIComponent(booking)}`;
  return call(path, json('PUT', input), parsePlan);
}

/** Says what happened to a booking: moved, late or cancelled, and what came of its money. */
export function setIncident(
  trip: string,
  booking: string,
  input: IncidentInput,
): Promise<Plan> {
  const path = `/_/api/trips/${encodeURIComponent(trip)}/bookings/${encodeURIComponent(booking)}/incident`;
  return call(path, json('PUT', input), parsePlan);
}

/** The trips of a trip's family the caller sees. */
export function getFamily(trip: string): Promise<Family> {
  return call(`/_/api/trips/${encodeURIComponent(trip)}/family`, {}, parseFamily);
}

/** Makes a branch of a trip, parting at its stop; answers the new trip's plan. */
export function addBranch(trip: string, input: BranchInput): Promise<Plan> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/branches`,
    json('POST', input),
    parsePlan,
  );
}

/** Says where a trip meets another of its family, and whether its other branches see it. */
export function setKin(
  trip: string,
  input: { meets: string | null; shared: boolean },
): Promise<Family> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/family`,
    json('PUT', input),
    parseFamily,
  );
}

/** Says nothing happened to a booking after all. */
export function clearIncident(trip: string, booking: string): Promise<Plan> {
  const path = `/_/api/trips/${encodeURIComponent(trip)}/bookings/${encodeURIComponent(booking)}/incident`;
  return call(path, { method: 'DELETE' }, parsePlan);
}

/** Removes a booking. */
export function deleteBooking(trip: string, booking: string): Promise<Plan> {
  const path = `/_/api/trips/${encodeURIComponent(trip)}/bookings/${encodeURIComponent(booking)}`;
  return call(path, { method: 'DELETE' }, parsePlan);
}

/** Adds a thing done at a stop of a trip. */
export function addAgendaItem(trip: string, input: AgendaInput): Promise<Plan> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/activities`,
    json('POST', input),
    parsePlan,
  );
}

/** Replaces a thing done's fields, possibly moving it to another stop or day. */
export function updateAgendaItem(trip: string, id: string, input: AgendaInput): Promise<Plan> {
  const path = `/_/api/trips/${encodeURIComponent(trip)}/activities/${encodeURIComponent(id)}`;
  return call(path, json('PUT', input), parsePlan);
}

/** Puts a day's things done in the order given: the way the day is walked. */
export function orderAgenda(trip: string, ids: readonly string[]): Promise<Plan> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/agenda`,
    json('PUT', { ids }),
    parsePlan,
  );
}

/** Says how one gets about one of a day's own ways at a stop; nothing said, walked again. */
export function setDayWay(
  trip: string,
  stop: string,
  [day, leg]: [string, DayLeg],
  way: WaySaid,
): Promise<Plan> {
  const at = (part: string) => encodeURIComponent(part);
  const path = `/_/api/trips/${at(trip)}/stops/${at(stop)}/ways/${at(day)}/${leg}`;
  return call(path, json('PUT', way), parsePlan);
}

/** Removes a thing done. */
export function deleteAgendaItem(trip: string, id: string): Promise<Plan> {
  const path = `/_/api/trips/${encodeURIComponent(trip)}/activities/${encodeURIComponent(id)}`;
  return call(path, { method: 'DELETE' }, parsePlan);
}

/** What `changes` would do, without saving them. */
export function previewChanges(trip: string, changes: Change[]): Promise<Preview> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/what-if`,
    json('POST', { changes }),
    parsePreview,
  );
}

/** Saves `changes` in one go and answers with the new plan. */
export function applyChanges(trip: string, changes: Change[]): Promise<Plan> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/apply`,
    json('POST', { changes }),
    parsePlan,
  );
}

/** Whether a booking holds what it was bought for: a cancelled one holds no nights, no way. */
export function live(booking: Booking): boolean {
  return booking.incident?.kind !== 'cancelled';
}

/** Whether a ticket is booked for a stop's way on: the next stop then stays after it. */
export function ticketed(stop: PlanStop): boolean {
  return stop.bookings.some((b) => b.kind === 'ticket' && live(b));
}

/** The stop whose booked ticket goes to `stop`, which then stays where it is; null if none. */
export function heldBy(plan: Plan, stop: PlanStop): PlanStop | null {
  const before = plan.stops[plan.stops.findIndex((s) => s.id === stop.id) - 1];
  return before !== undefined && ticketed(before) ? before : null;
}

/** Whether a stop's days are bought: a ticket there and a ticket on from it. */
export function daysHeld(plan: Plan, stop: PlanStop): boolean {
  return ticketed(stop) && heldBy(plan, stop) !== null;
}

/** The first booked way an order of the stops would part, as the stop it leaves; null if none. */
export function partedBy(plan: Plan, order: string[]): PlanStop | null {
  return (
    plan.stops.find((stop, at) => {
      const next = plan.stops[at + 1];
      return (
        next !== undefined && ticketed(stop) && order[order.indexOf(stop.id) + 1] !== next.id
      );
    }) ?? null
  );
}

/** Puts the stops in a new order. */
export function reorderStops(trip: string, stops: string[]): Promise<Plan> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/order`,
    json('PUT', { stops }),
    parsePlan,
  );
}

/** Reads a typed route, such as `HEL-Sydney`; `signal` drops an answer no longer wanted. */
export function searchPlaces(
  query: string,
  signal: AbortSignal,
  pins: readonly string[] = [],
): Promise<RouteSearch> {
  // Places picked on the map, each by its name and point: that town, though an airport's city
  // is named so.
  const at = pins.length > 0 ? `&at=${encodeURIComponent(pins.join(';'))}` : '';
  return call(`/_/api/places?q=${encodeURIComponent(query)}${at}`, { signal }, parseRoute);
}

/**
 * Adds a searched route by airport code: each leg by its way, each place for its nights, and the
 * Google Flights link it was read from, kept on the legs it flies with its fare.
 */
export function addRoute(
  trip: string,
  codes: string[],
  modes: Mode[],
  nights: number[],
  link: string | null,
): Promise<Plan> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/route`,
    json('POST', { codes, modes, nights, link }),
    parsePlan,
  );
}

export function parseTypical(value: unknown): Typical {
  const fields = record(value, 'typical');
  const pair = (key: string) => {
    const r = record(fields[key], key);
    return { min: number(r, 'min'), max: number(r, 'max') };
  };
  return {
    bed: pair('bed'),
    food: pair('food'),
    local: pair('local'),
    fun: pair('fun'),
    basis: oneOf(fields, 'basis', typicalBases),
  };
}

/** What a day typically costs in a country (`TH`), in euro cents; `NotFound` for one unknown. */
export function getTypical(country: string): Promise<Typical> {
  return call(`/_/api/places/typical/${encodeURIComponent(country)}`, {}, parseTypical);
}

/**
 * The way by road from one point to another, from OpenStreetMap's router; by `sea`, one that
 * crosses on a ferry line, else the shortest way across the sea. `null` when there is none, or
 * none could be asked, and the way is drawn straight.
 */
export function getRoad(from: Point, to: Point, sea = false): Promise<Road | null> {
  const at = (p: Point) => `${String(p.lat)},${String(p.lon)}`;
  const by = sea ? '&by=sea' : '';
  return call(`/_/api/places/road?from=${at(from)}&to=${at(to)}${by}`, {}, parseRoad);
}

/** The way on foot from one point to another, along streets and paths; `null` as a road's. */
export function getWalk(from: Point, to: Point): Promise<Road | null> {
  const at = (p: Point) => `${String(p.lat)},${String(p.lon)}`;
  return call(`/_/api/places/road?from=${at(from)}&to=${at(to)}&by=foot`, {}, parseRoad);
}

/**
 * A train from one point to another along its rails: by its timetable where it is known, else
 * along the railways from the station nearest each; `null` when there are none, or none could be
 * asked, and the way is drawn straight — never along a road.
 */
export function getRail(from: Point, to: Point): Promise<Road | null> {
  const at = (p: Point) => `${String(p.lat)},${String(p.lon)}`;
  return call(`/_/api/places/road?from=${at(from)}&to=${at(to)}&by=rail`, {}, parseRoad);
}

/** A town's metro lines and stations, near a point in it; `null` when they could not be had. */
export function getMetro(at: Point): Promise<Metro | null> {
  return call(`/_/api/places/metro?at=${String(at.lat)},${String(at.lon)}`, {}, parseMetro);
}

/** The ride across town by its transport from one point to another, leaving at `at`, an
 * instant; `null` when there is none, or no router. */
export function getRide(from: Point, to: Point, at: string): Promise<Ride | null> {
  const point = (p: Point) => `${String(p.lat)},${String(p.lon)}`;
  return call(
    `/_/api/places/transit?from=${point(from)}&to=${point(to)}&at=${encodeURIComponent(at)}`,
    {},
    parseRide,
  );
}

export function parseSpots(value: unknown): { spots: Spot[]; osm: (typeof osmStates)[number] } {
  const fields = record(value, 'spots');
  return {
    spots: list(fields, 'spots', (item) => {
      const found = record(item, 'spot');
      return {
        name: text(found, 'name'),
        detail: text(found, 'detail'),
        lat: number(found, 'lat'),
        lon: number(found, 'lon'),
      };
    }),
    osm: oneOf(fields, 'osm', osmStates),
  };
}

/** Exact places named like `query` near a point: an address, a station, an airport. */
export function findSpots(
  query: string,
  near: Point,
  signal: AbortSignal,
): Promise<{ spots: Spot[]; osm: (typeof osmStates)[number] }> {
  const q = encodeURIComponent(query);
  const at = `${String(near.lat)},${String(near.lon)}`;
  return call(`/_/api/places/spots?q=${q}&near=${at}`, { signal }, parseSpots);
}

/** A place's picture from Wikipedia, and the page it is from, to credit. */
export interface Photo {
  image: string;
  title: string;
  page: string;
}

/**
 * A picture of a place from Wikipedia: the page nearest it with one, else the one its name
 * finds; `null` when there is none.
 */
export function getPhoto(name: string, near: Point): Promise<Photo | null> {
  const at = `${String(near.lat)},${String(near.lon)}`;
  return call(
    `/_/api/places/photo?name=${encodeURIComponent(name)}&near=${at}`,
    {},
    (value) => {
      const fields = record(value, 'photo');
      return {
        image: text(fields, 'image'),
        title: text(fields, 'title'),
        page: text(fields, 'page'),
      };
    },
  ).catch((error: unknown) => {
    if (error instanceof NotFound) return null;
    throw error;
  });
}

/** Towns named like `query`: cities with an airport, then OpenStreetMap's. */
/** An airline known: `{code: 'EY', name: 'Etihad Airways', icao: 'ETD'}`. */
export interface Airline {
  code: string;
  name: string;
  icao: string;
}

/** An aircraft type known: `{icao: 'A388', iata: '388', name: 'Airbus A380-800'}`. */
export interface Aircraft {
  icao: string;
  iata: string;
  name: string;
}

/** An airport known: `{code: 'AUH', name: 'Zayed International Airport', city: 'Abu Dhabi', country: 'AE'}`. */
export interface KnownAirport {
  code: string;
  name: string;
  city: string;
  country: string;
}

/** One of a list of the ground and the sea found by what is typed: a ship, a company, a train type, a station. */
export interface FoundItem {
  /** The list it is of: `ships`, `stations`. */
  list: string;
  name: string;
  /** What is said beside it: a ship's operator, a train type's maker. */
  detail: string;
  /** A ship's IMO number, a station's UIC code. */
  code: string;
  country: string;
  /** A station's place and zone. */
  lat: number | null;
  lon: number | null;
  zone: string;
}

/** Those of some lists that fit what is typed, best first: `findReference('rail_operators,transit', 'elr')`. */
export function findReference(lists: string, typed: string): Promise<FoundItem[]> {
  const query = new URLSearchParams({ in: lists, q: typed });
  return call(`/_/api/reference/find?${query.toString()}`, {}, (value) =>
    list({ found: value }, 'found', (one) => {
      const item = record(one, 'item');
      const at = (key: string) => maybe(item, key, () => number(item, key));
      return {
        list: text(item, 'list'),
        name: text(item, 'name'),
        detail: text(item, 'detail'),
        code: text(item, 'code'),
        country: text(item, 'country'),
        lat: at('lat'),
        lon: at('lon'),
        zone: text(item, 'zone'),
      };
    }),
  );
}

let airportsKept: Promise<KnownAirport[]> | null = null;
let airlinesKept: Promise<Airline[]> | null = null;
let aircraftKept: Promise<Aircraft[]> | null = null;

/** Every airport known, asked once. */
export function getAirports(): Promise<KnownAirport[]> {
  airportsKept ??= call('/_/api/reference/airports', {}, (value) =>
    list({ airports: value }, 'airports', (item) => {
      const airport = record(item, 'airport');
      return {
        code: text(airport, 'code'),
        name: text(airport, 'name'),
        city: text(airport, 'city'),
        country: text(airport, 'country'),
      };
    }),
  ).catch((error: unknown) => {
    airportsKept = null;
    throw error;
  });
  return airportsKept;
}

/** Every airline known, asked once. */
export function getAirlines(): Promise<Airline[]> {
  airlinesKept ??= call('/_/api/reference/airlines', {}, (value) =>
    list({ airlines: value }, 'airlines', (item) => {
      const airline = record(item, 'airline');
      return {
        code: text(airline, 'code'),
        name: text(airline, 'name'),
        icao: text(airline, 'icao'),
      };
    }),
  ).catch((error: unknown) => {
    airlinesKept = null;
    throw error;
  });
  return airlinesKept;
}

/** Every aircraft type known, asked once. */
export function getAircraft(): Promise<Aircraft[]> {
  aircraftKept ??= call('/_/api/reference/aircraft', {}, (value) =>
    list({ aircraft: value }, 'aircraft', (item) => {
      const type = record(item, 'aircraft');
      return { icao: text(type, 'icao'), iata: text(type, 'iata'), name: text(type, 'name') };
    }),
  ).catch((error: unknown) => {
    aircraftKept = null;
    throw error;
  });
  return aircraftKept;
}

export function findTowns(query: string, signal: AbortSignal): Promise<TownSearch> {
  return call(`/_/api/places/towns?q=${encodeURIComponent(query)}`, { signal }, parseTowns);
}

const legPath = (trip: string, stop: string) =>
  `/_/api/trips/${encodeURIComponent(trip)}/stops/${encodeURIComponent(stop)}/routes`;

/** The options kept for the way on from `stop`, compared. */
export function getComparison(trip: string, stop: string): Promise<Comparison> {
  return call(legPath(trip, stop), {}, parseComparison);
}

/** Keeps the options for the way on from `stop` and answers with them compared. */
export function keepComparison(
  trip: string,
  stop: string,
  text: string,
  maxLegs: number,
): Promise<Comparison> {
  return call(legPath(trip, stop), json('PUT', { text, maxLegs }), parseComparison);
}

/** Puts the path of `flights` (indexes into the comparison's flights) into the plan. */
export function choosePath(trip: string, stop: string, flights: number[]): Promise<Plan> {
  return call(`${legPath(trip, stop)}/choose`, json('POST', { flights }), parsePlan);
}

/** The legs of a trip with options kept. */
export function keptLegs(trip: string): Promise<KeptLeg[]> {
  return call(`/_/api/trips/${encodeURIComponent(trip)}/routes`, {}, parseKept);
}

/** Adds a fellow traveller to the trip. */
export function addTraveller(trip: string, input: TravellerInput): Promise<Plan> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/travellers`,
    json('POST', input),
    parsePlan,
  );
}

/** Replaces a fellow traveller and their legs. */
export function updateTraveller(
  trip: string,
  traveller: string,
  input: TravellerInput,
): Promise<Plan> {
  const path = `/_/api/trips/${encodeURIComponent(trip)}/travellers/${encodeURIComponent(traveller)}`;
  return call(path, json('PUT', input), parsePlan);
}

/** Removes a fellow traveller. */
export function deleteTraveller(trip: string, traveller: string): Promise<Plan> {
  const path = `/_/api/trips/${encodeURIComponent(trip)}/travellers/${encodeURIComponent(traveller)}`;
  return call(path, { method: 'DELETE' }, parsePlan);
}

/** Whether passkeys sign in at the app's address. */
export function passkeysOffered(): Promise<boolean> {
  return call('/_/api/setup', {}, (body) => record(body, 'setup').passkeys === true);
}

/** Whether the server still waits for its first account. */
export function getSetup(): Promise<boolean> {
  return call('/_/api/setup', {}, (body) => flag(record(body, 'setup'), 'firstAccount'));
}

/** Makes the first account and signs it in. */
export function createAccount(input: {
  email: string;
  name: string;
  password: string;
  language: string;
}): Promise<User> {
  return call('/_/api/account', json('POST', input), parseUser);
}

/** Signed in, or a second step to take with its ticket. */
export type SignInStep = { kind: 'in'; user: User } | { kind: 'second'; ticket: string };

export function parseSignIn(value: unknown): SignInStep {
  const fields = record(value, 'answer');
  return fields.second === true
    ? { kind: 'second', ticket: text(fields, 'ticket') }
    : { kind: 'in', user: parseUser(value) };
}

/** Signs in; a wrong e-mail or password is `InvalidInput` on `password`. An account with a
 * second step, on a device not trusted for it, gets its ticket. */
export function signIn(email: string, password: string): Promise<SignInStep> {
  return call('/_/api/session', json('POST', { email, password }), parseSignIn);
}

/** Finishes a sign-in with an app's code or a backup code; `trust` skips the step on this device
 * for thirty days. A wrong code is `InvalidInput` on `code`, a ticket ended `NotFound`. */
export function finishSignIn(ticket: string, code: string, trust: boolean): Promise<User> {
  return call('/_/api/session/second', json('POST', { ticket, code, trust }), parseUser);
}

/** What the browser signs in with a passkey with, as the server says it. */
export function passkeySignInOptions(): Promise<Record<string, unknown>> {
  return call('/_/api/session/passkey/options', { method: 'POST' }, (body) =>
    record(body, 'options'),
  );
}

/** A passkey's answer when signing in, its bytes in base64url. */
export interface PasskeyAnswer {
  id: string;
  clientData: string;
  authenticator: string;
  signature: string;
}

/** Signs in with a passkey; one that does not check out is `InvalidInput` on `passkey`. */
export function passkeySignIn(answer: PasskeyAnswer): Promise<User> {
  return call('/_/api/session/passkey', json('POST', answer), parseUser);
}

/** Signs out. */
export function signOut(): Promise<null> {
  return call('/_/api/me/session', { method: 'DELETE' }, () => null);
}

/** The signed-in account; `SignedOut` when there is none. */
export function getMe(): Promise<User> {
  return call('/_/api/me', {}, parseUser);
}

/** Changes one's name and language. */
export function updateMe(name: string, language: string): Promise<User> {
  return call('/_/api/me', json('PUT', { name, language }), parseUser);
}

// ---- one's own security ----

/** A passkey of the account. */
export interface Passkey {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt: string | null;
}

/** A session of the account. */
export interface AccountSession {
  id: string;
  userAgent: string | null;
  /** `code`: a password and a second step; `passkey`; `null`: a password alone. */
  signedInWith: 'code' | 'passkey' | null;
  createdAt: string;
  lastSeenAt: string | null;
  /** The one asking. */
  current: boolean;
}

/** An account's security: its password, second step, passkeys and sessions. */
export interface Security {
  passwordChangedAt: string;
  /** When the second step was turned on; `null` while it is off. */
  twoStepSince: string | null;
  backupLeft: number;
  passkeys: Passkey[];
  /** Whether a browser makes passkeys for the app at its address. */
  passkeysWork: boolean;
  sessions: AccountSession[];
}

function passkey(value: unknown): Passkey {
  const fields = record(value, 'passkey');
  return {
    id: text(fields, 'id'),
    name: text(fields, 'name'),
    createdAt: text(fields, 'createdAt'),
    lastUsedAt: optionalText(fields, 'lastUsedAt'),
  };
}

function accountSession(value: unknown): AccountSession {
  const fields = record(value, 'session');
  const way = optionalText(fields, 'signedInWith');
  return {
    id: text(fields, 'id'),
    userAgent: optionalText(fields, 'userAgent'),
    signedInWith: way === 'code' || way === 'passkey' ? way : null,
    createdAt: text(fields, 'createdAt'),
    lastSeenAt: optionalText(fields, 'lastSeenAt'),
    current: flag(fields, 'current'),
  };
}

export function parseSecurity(value: unknown): Security {
  const fields = record(value, 'security');
  const twoStep = record(fields.twoStep, 'twoStep');
  return {
    passwordChangedAt: text(fields, 'passwordChangedAt'),
    twoStepSince: optionalText(twoStep, 'since'),
    backupLeft: number(twoStep, 'backupLeft'),
    passkeys: list(fields, 'passkeys', passkey),
    passkeysWork: flag(fields, 'passkeysWork'),
    sessions: list(fields, 'sessions', accountSession),
  };
}

/** The account's security. */
export function getSecurity(): Promise<Security> {
  return call('/_/api/me/security', {}, parseSecurity);
}

/** Changes the password, asked with the one it has (`InvalidInput` on `current`); every other
 * session ends. */
export function changePassword(current: string, password: string): Promise<null> {
  return call('/_/api/me/password', json('PUT', { current, password }), () => null);
}

/** An authenticator app's setup: its secret, its link and the link as a QR code (SVG). */
export interface TotpSetup {
  secret: string;
  uri: string;
  svg: string;
}

/** Starts an authenticator app for the account. */
export function startTotp(): Promise<TotpSetup> {
  return call('/_/api/me/totp', { method: 'POST' }, (body) => {
    const fields = record(body, 'setup');
    return {
      secret: text(fields, 'secret'),
      uri: text(fields, 'uri'),
      svg: text(fields, 'svg'),
    };
  });
}

const codes = (body: unknown) =>
  list(record(body, 'codes'), 'codes', (code) => text({ code }, 'code'));

/** Turns the second step on with the app's first code: the backup codes, said once. */
export function confirmTotp(code: string): Promise<string[]> {
  return call('/_/api/me/totp/confirm', json('POST', { code }), codes);
}

/** Turns the second step off, asked with the password and a code. */
export function turnTotpOff(password: string, code: string): Promise<null> {
  return call('/_/api/me/totp/off', json('POST', { password, code }), () => null);
}

/** New backup codes in place of the old. */
export function newBackupCodes(): Promise<string[]> {
  return call('/_/api/me/backup-codes', { method: 'POST' }, codes);
}

/** What the browser makes a passkey with, as the server says it. */
export function passkeyOptions(): Promise<Record<string, unknown>> {
  return call('/_/api/me/passkeys/options', { method: 'POST' }, (body) =>
    record(body, 'options'),
  );
}

/** A passkey the browser made, its bytes in base64url, and the name it is given. */
export interface PasskeyMade {
  id: string;
  clientData: string;
  attestation: string;
  name: string;
}

/** Keeps a passkey the browser made. */
export function addPasskey(made: PasskeyMade): Promise<Passkey> {
  return call('/_/api/me/passkeys', json('POST', made), passkey);
}

const passkeyPath = (id: string) => `/_/api/me/passkeys/${encodeURIComponent(id)}`;

/** Renames a passkey. */
export function renamePasskey(id: string, name: string): Promise<null> {
  return call(passkeyPath(id), json('PUT', { name }), () => null);
}

/** Removes a passkey. */
export function removePasskey(id: string): Promise<null> {
  return call(passkeyPath(id), { method: 'DELETE' }, () => null);
}

/** Ends one of the account's sessions; `null` for every one but this. */
export function endSessions(id: string | null): Promise<null> {
  const path =
    id === null ? '/_/api/me/sessions' : `/_/api/me/sessions/${encodeURIComponent(id)}`;
  return call(path, { method: 'DELETE' }, () => null);
}

/** A trip's route and dates, for any member. */
export function getView(trip: string): Promise<TripView> {
  return call(`/_/api/trips/${encodeURIComponent(trip)}/view`, {}, parseView);
}

const people = (trip: string) => `/_/api/trips/${encodeURIComponent(trip)}`;

/** Who is on a trip. */
export function getPeople(trip: string): Promise<People> {
  return call(`${people(trip)}/members`, {}, parsePeople);
}

/** Gives a member another role. */
export function setRole(trip: string, user: string, role: Role): Promise<People> {
  return call(
    `${people(trip)}/members/${encodeURIComponent(user)}`,
    json('PUT', { role }),
    parsePeople,
  );
}

/** Takes a member off the trip. */
export function removeMember(trip: string, user: string): Promise<People> {
  return call(
    `${people(trip)}/members/${encodeURIComponent(user)}`,
    { method: 'DELETE' },
    parsePeople,
  );
}

/** Invites `email`: the invitation, its link, and whether it was e-mailed. */
export function invite(
  trip: string,
  email: string,
  role: Role,
): Promise<{ invite: Invitation; link: string; sent: boolean }> {
  return call(`${people(trip)}/invites`, json('POST', { email, role }), parseInvited);
}

/** Withdraws an invitation. */
export function revokeInvite(trip: string, id: string): Promise<People> {
  return call(
    `${people(trip)}/invites/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    parsePeople,
  );
}

/** Turns the read-only link off. */
export function removeShareLink(trip: string): Promise<null> {
  return call(`${people(trip)}/share`, { method: 'DELETE' }, () => null);
}

/** What an invitation is to. */
export function getInvitation(token: string): Promise<InvitationView> {
  return call(`/_/api/invites/${encodeURIComponent(token)}`, {}, parseInvitation);
}

/** Accepts an invitation: as the account signed in, or with a name and password as a new one. */
export function acceptInvitation(
  token: string,
  account: { name: string; password: string; language: string } | null,
): Promise<{ trip: string; user: User | null }> {
  return call(
    `/_/api/invites/${encodeURIComponent(token)}/accept`,
    json('POST', account ?? {}),
    (body) => {
      const fields = record(body, 'accepted');
      return {
        trip: text(fields, 'trip'),
        user: fields.user === undefined ? null : parseUser(body),
      };
    },
  );
}

/** A trip through its read-only link. */
export async function getShared(token: string, phrase?: string): Promise<TripView> {
  const response = await fetch(`/_/api/share/${encodeURIComponent(token)}`, {
    headers: { accept: 'application/json', ...(phrase ? { 'x-share-phrase': phrase } : {}) },
  });
  if (response.ok) return parseView(await response.json());
  const said = (await response.json().catch(() => ({}))) as { error?: unknown };
  const reasons = ['ended', 'locked', 'phrase', 'wrong'] as const;
  const reason = reasons.find((one) => one === said.error);
  throw new ShareRefused(response.status === 404 ? 'gone' : (reason ?? 'failed'));
}

/** Why a shared link shows nothing: gone, ended, locked, a phrase asked or wrong, or failing. */
export class ShareRefused extends Error {
  readonly reason: 'gone' | 'ended' | 'locked' | 'phrase' | 'wrong' | 'failed';
  constructor(reason: ShareRefused['reason']) {
    super(reason);
    this.name = 'ShareRefused';
    this.reason = reason;
  }
}

/** A read-only link as its owner sees it. */
export interface ShareScreen {
  link: string | null;
  on: boolean;
  createdAt: string | null;
  expires: boolean;
  endsAt: string | null;
  /** When it would end, were it to end by itself. */
  endsIfOn: string;
  phrase: string | null;
  amounts: boolean;
  files: boolean;
  names: boolean;
  /** Whether it shows the journal. */
  journal: boolean;
  views: number;
  lastAt: string | null;
  log: {
    at: string;
    city: string | null;
    country: string | null;
    device: 'phone' | 'tablet' | 'computer';
    outcome: 'open' | 'ok' | 'wrong';
    tries: number;
  }[];
}

function parseShareScreen(value: unknown): ShareScreen {
  const fields = record(value, 'share');
  const said = (key: string) => maybe(fields, key, () => text(fields, key));
  return {
    link: said('link'),
    on: flag(fields, 'on'),
    createdAt: said('createdAt'),
    expires: flag(fields, 'expires'),
    endsAt: said('endsAt'),
    endsIfOn: text(fields, 'endsIfOn'),
    phrase: said('phrase'),
    amounts: flag(fields, 'amounts'),
    files: flag(fields, 'files'),
    names: flag(fields, 'names'),
    // Not said by a server before the journal: not shown.
    journal: flag({ journal: fields.journal ?? false }, 'journal'),
    views: number(fields, 'views'),
    lastAt: said('lastAt'),
    log: list(fields, 'log', (item) => {
      const view = record(item, 'view');
      return {
        at: text(view, 'at'),
        city: maybe(view, 'city', () => text(view, 'city')),
        country: maybe(view, 'country', () => text(view, 'country')),
        device: oneOf(view, 'device', ['phone', 'tablet', 'computer'] as const),
        outcome: oneOf(view, 'outcome', ['open', 'ok', 'wrong'] as const),
        tries: number(view, 'tries'),
      };
    }),
  };
}

/** A trip's read-only link, as its owner sees it. */
export function getShareScreen(trip: string): Promise<ShareScreen> {
  return call(`/_/api/trips/${encodeURIComponent(trip)}/share`, {}, parseShareScreen);
}

/** A new read-only link, the one before working no more. */
export function renewShare(trip: string): Promise<ShareScreen> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/share`,
    { method: 'POST' },
    parseShareScreen,
  );
}

/** What a read-only link shows and asks. */
export function setShareChoices(
  trip: string,
  choices: {
    expires: boolean;
    phrase: boolean;
    amounts: boolean;
    files: boolean;
    names: boolean;
    journal: boolean;
  },
): Promise<ShareScreen> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/share`,
    json('PUT', choices),
    parseShareScreen,
  );
}

/** A link that sets a new password mailed, when the address has an account: whether this server
 * sends mail. */
export function forgotPassword(email: string): Promise<boolean> {
  return call('/_/api/password/forgot', json('POST', { email }), (value) =>
    flag(record(value, 'forgot'), 'mail'),
  );
}

/** A new password set by a mailed link; every session signed out when `others`. */
export function resetPassword(token: string, password: string, others: boolean): Promise<null> {
  return call('/_/api/password/reset', json('POST', { token, password, others }), () => null);
}

const costs = (trip: string) => `/_/api/trips/${encodeURIComponent(trip)}/costs`;

/** The address of an expense's bill photo. */
export function billUrl(trip: string, expense: string): string {
  return `${costs(trip)}/${encodeURIComponent(expense)}/bill`;
}

/** A trip's shared costs. */
export function getLedger(trip: string): Promise<Ledger> {
  return call(costs(trip), {}, parseLedger);
}

/** Adds an expense. */
export function addExpense(trip: string, input: ExpenseInput): Promise<Ledger> {
  return call(costs(trip), json('POST', input), parseLedger);
}

/** Removes an expense and its photo. */
export function removeExpense(trip: string, expense: string): Promise<Ledger> {
  return call(
    `${costs(trip)}/${encodeURIComponent(expense)}`,
    { method: 'DELETE' },
    parseLedger,
  );
}

/** Records that `from` paid `to` back. */
export function settleUp(
  trip: string,
  transfer: { from: string; to: string; amount: number; day: string },
): Promise<Ledger> {
  return call(`${costs(trip)}/settle`, json('POST', transfer), parseLedger);
}

/** Keeps a photo of an expense's bill. */
export function uploadBill(trip: string, expense: string, file: Blob): Promise<Ledger> {
  return call(billUrl(trip, expense), { method: 'PUT', body: file }, parseLedger, file.type);
}

/** Deletes an expense's bill photo; the expense stays. */
export function removeBill(trip: string, expense: string): Promise<Ledger> {
  return call(billUrl(trip, expense), { method: 'DELETE' }, parseLedger);
}

/** A trip's latest activity and who has it open. */
export function getActivity(trip: string): Promise<Activity> {
  return call(`/_/api/trips/${encodeURIComponent(trip)}/activity`, {}, parseActivity);
}

/** The address of a trip's live activity, for an `EventSource`. */
export function eventsUrl(trip: string): string {
  return `/_/api/trips/${encodeURIComponent(trip)}/events`;
}

// ---- on the road (docs/01 §5 v0.6) ----

export const categories = ['food', 'transport', 'stay', 'fun', 'other'] as const;
export type Category = (typeof categories)[number];

/** Something spent on the road, in its money and the trip's at the rate of its day. */
export interface Spend {
  id: string;
  who: { userId: string; name: string } | null;
  day: string;
  category: Category;
  note: string;
  amount: Money;
  tripAmount: Money;
  /** Billionths of the spend's currency to one unit of the trip's. */
  perTrip: number;
  photo: boolean;
}

/** A spend as the phone sends it; its `id` is made on the phone, so a resend is kept once. */
export interface SpendInput {
  id: string;
  day: string;
  category: Category;
  note: string;
  amount: number;
  currency: string;
}

/** How many of a currency one unit of the trip's buys, and where that comes from. */
export interface Rate {
  currency: string;
  /** In billionths: 30 500 dong to the euro is `30_500_000_000_000`. */
  perTrip: number;
  /** The ECB's rate names its day; the others have none (`''`). */
  /** Whose rate: the trip's currency, the trip's own, the ECB's or the market's of a day. */
  source: { kind: 'same' | 'own' | 'ecb' | 'market'; day: string };
}

/** The Today screen for one day. */
export interface TodayScreen {
  day: string;
  currency: string;
  tripDay: { day: number; of: number } | null;
  startsIn: number | null;
  here: {
    index: number;
    city: string;
    country: string;
    currency: string | null;
    night: number;
    nights: number;
  } | null;
  allowance: Money | null;
  spent: Money;
  left: Money | null;
  /** Above nothing is under the plan. */
  soFar: Money;
  next: {
    to: string;
    mode: Mode | null;
    inDays: number;
    booked: Money | null;
    estimate: MoneyRange;
  } | null;
  spends: Spend[];
  rates: Rate[];
  ratesDay: string | null;
  currencies: { code: string; digits: number }[];
  /** The borders whose task is due and not done, for the signed-in traveller's passport. */
  reminders: Crossing[];
}

export function parseSpend(value: unknown): Spend {
  const fields = record(value, 'spend');
  return {
    id: text(fields, 'id'),
    who: maybe(fields, 'who', person),
    day: text(fields, 'day'),
    category: oneOf(fields, 'category', categories),
    note: text(fields, 'note'),
    amount: money(fields.amount),
    tripAmount: money(fields.tripAmount),
    perTrip: number(fields, 'perTrip'),
    photo: flag(fields, 'photo'),
  };
}

function rate(value: unknown): Rate {
  const fields = record(value, 'rate');
  const source = record(fields.source, 'source');
  return {
    currency: text(fields, 'currency'),
    perTrip: number(fields, 'perTrip'),
    source: {
      kind: oneOf(source, 'kind', ['same', 'own', 'ecb', 'market'] as const),
      day: 'day' in source ? text(source, 'day') : '',
    },
  };
}

export function parseToday(value: unknown): TodayScreen {
  const fields = record(value, 'today');
  return {
    day: text(fields, 'day'),
    currency: text(fields, 'currency'),
    tripDay: maybe(fields, 'tripDay', (item) => {
      const day = record(item, 'tripDay');
      return { day: number(day, 'day'), of: number(day, 'of') };
    }),
    startsIn: maybe(fields, 'startsIn', () => number(fields, 'startsIn')),
    here: maybe(fields, 'here', (item) => {
      const here = record(item, 'here');
      return {
        index: number(here, 'index'),
        city: text(here, 'city'),
        country: text(here, 'country'),
        currency: maybe(here, 'currency', () => text(here, 'currency')),
        night: number(here, 'night'),
        nights: number(here, 'nights'),
      };
    }),
    allowance: maybe(fields, 'allowance', money),
    spent: money(fields.spent),
    left: maybe(fields, 'left', money),
    soFar: money(fields.soFar),
    next: maybe(fields, 'next', (item) => {
      const next = record(item, 'next');
      return {
        to: text(next, 'to'),
        mode: maybe(next, 'mode', () => oneOf(next, 'mode', modes)),
        inDays: number(next, 'inDays'),
        booked: maybe(next, 'booked', money),
        estimate: range(next.estimate),
      };
    }),
    spends: list(fields, 'spends', parseSpend),
    rates: list(fields, 'rates', rate),
    ratesDay: maybe(fields, 'ratesDay', () => text(fields, 'ratesDay')),
    currencies: list(fields, 'currencies', (item) => {
      const currency = record(item, 'currency');
      return { code: text(currency, 'code'), digits: number(currency, 'digits') };
    }),
    reminders: list(fields, 'reminders', parseCrossing),
  };
}

const road = (trip: string) => `/_/api/trips/${encodeURIComponent(trip)}`;

/** The Today screen for `day` (`2026-11-14`, the phone's own day). */
export function getToday(trip: string, day: string): Promise<TodayScreen> {
  return call(`${road(trip)}/today?day=${encodeURIComponent(day)}`, {}, parseToday);
}

/** Keeps a spend; one already kept under its id is answered as it was. */
export function addSpend(trip: string, input: SpendInput): Promise<Spend> {
  return call(`${road(trip)}/spends`, json('POST', input), parseSpend);
}

/** Removes a spend and its photo. */
export function removeSpend(trip: string, spend: string): Promise<null> {
  return call(
    `${road(trip)}/spends/${encodeURIComponent(spend)}`,
    { method: 'DELETE' },
    () => null,
  );
}

/** The address of a spend's bill photo. */
export function spendBillUrl(trip: string, spend: string): string {
  return `${road(trip)}/spends/${encodeURIComponent(spend)}/bill`;
}

/** Keeps a photo of a spend's bill. */
export function uploadSpendBill(trip: string, spend: string, file: Blob): Promise<null> {
  return call(spendBillUrl(trip, spend), { method: 'PUT', body: file }, () => null, file.type);
}

/** A currency's rate on a day: the trip's own, else the ECB's; `null` when there is none. */
export function getRate(trip: string, currency: string, day: string): Promise<Rate | null> {
  return call(
    `${road(trip)}/rates/${encodeURIComponent(currency)}?day=${encodeURIComponent(day)}`,
    {},
    rate,
  ).catch((error: unknown) => {
    if (error instanceof NotFound) return null;
    throw error;
  });
}

/** Sets the trip's own rate for a currency: `rate` of it to one unit of the trip's money. */
export function setRate(trip: string, currency: string, rate: string): Promise<null> {
  return call(
    `${road(trip)}/rates/${encodeURIComponent(currency)}`,
    json('PUT', { rate }),
    () => null,
  );
}

/** Goes back to the ECB's rate for a currency. */
export function removeRate(trip: string, currency: string): Promise<null> {
  return call(
    `${road(trip)}/rates/${encodeURIComponent(currency)}`,
    { method: 'DELETE' },
    () => null,
  );
}

// ---- documents (docs/01 §5 v0.7) ----

export const entryKinds = ['free', 'card', 'eta', 'evisa', 'arrival', 'visa'] as const;
export type EntryKind = (typeof entryKinds)[number];

/** What a passport meets at a border: indicative, with the official page. */
export interface EntryRule {
  kind: EntryKind;
  /** The longest stay per entry. */
  days: number;
  /** `TDAC`, `e-Visa`; `''` when there is nothing to fill. */
  form: string;
  /** How many days before the border to do it. */
  lead: number;
  url: string;
  /** TripPath's list with its official page, Wikipedia's table, or the traveller's own. */
  source: 'list' | 'wikipedia' | 'own';
}

/** A stay in a country, from its border to the day it is left. */
export interface Crossing {
  country: string;
  /** Which entry to that country on the route, from 1. */
  entry: number;
  border: string;
  leaves: string;
  /** Days in the country, the border's and the leaving day both counted. */
  days: number;
  unresolved: boolean;
  /** `null` without a passport, or when the rules do not know the country. */
  rule: EntryRule | null;
  /** The day to have done what the rule asks by. */
  due: string | null;
  done: boolean;
}

/** A document's kinds, in the order offered: who one is, driving, insurances, the rest. */
export const documentKinds = [
  'passport',
  'id_card',
  'visa',
  'driving_licence',
  'permit',
  'vehicle',
  'insurance',
  'motor_insurance',
  'casco_insurance',
  'accident_insurance',
  'bike_insurance',
  'electronics_insurance',
  'liability_insurance',
  'vaccination',
  'ticket',
  'booking',
  'other',
] as const;
export type DocumentKind = (typeof documentKinds)[number];

/** A document of the trip: shared with it, or private to whoever added it. */
export interface TripDocument {
  id: string;
  owner: { userId: string; name: string } | null;
  /** Whether the signed-in account added it. */
  mine: boolean;
  kind: DocumentKind;
  name: string;
  note: string;
  /** The first day it is valid: a policy's, from the day one leaves. */
  validFrom: string | null;
  validTo: string | null;
  stop: string | null;
  private: boolean;
  file: { type: string; size: number } | null;
  /** The fellow traveller it is of; `null` for whoever added it. */
  traveller: string | null;
  /** The thing done it belongs to: a photo of the house, a guide's map. */
  activity: string | null;
  /** The booking it is the confirmation or ticket of. */
  booking: string | null;
  /** What it cost in the trip's money — the insurance, the visa — and in its own when paid
   * in another. */
  price: Money | null;
  paid: Money | null;
}

/** A document as typed. */
export interface DocumentInput {
  kind: DocumentKind;
  name: string;
  note: string;
  validFrom: string | null;
  validTo: string | null;
  stop: string | null;
  private: boolean;
  /** The fellow traveller it is of; `null` for whoever adds it. */
  traveller: string | null;
  /** The thing done it belongs to; left out for none. */
  activity?: string | null;
  /** The booking it is of; left out for none. */
  booking?: string | null;
  /** What it cost, in minor units of `currency`; left out for none. */
  price?: number | null;
  /** The money it was paid in; left out for the trip's. */
  currency?: string | null;
}

/** Something to do before leaving. */
export interface BeforeItem {
  id: string;
  text: string;
  done: boolean;
}

/** The Docs screen. */
export interface DocsScreen {
  /** The passport the signed-in account travels on, `EE`. */
  passport: string | null;
  start: string;
  end: string;
  /** The trip's money, a document's price said in it unless in another. */
  currency: string;
  /** Its stops, each with the money its country pays in, when known. */
  stops: { id: string; city: string; country: string; currency: string | null }[];
  /** The fellow travellers, whose documents are kept here too. */
  travellers: { id: string; name: string }[];
  crossings: Crossing[];
  documents: TripDocument[];
  before: BeforeItem[];
}

export function parseCrossing(value: unknown): Crossing {
  const fields = record(value, 'crossing');
  return {
    country: text(fields, 'country'),
    entry: number(fields, 'entry'),
    border: text(fields, 'border'),
    leaves: text(fields, 'leaves'),
    days: number(fields, 'days'),
    unresolved: flag(fields, 'unresolved'),
    rule: maybe(fields, 'rule', (item) => {
      const rule = record(item, 'rule');
      return {
        kind: oneOf(rule, 'kind', entryKinds),
        days: number(rule, 'days'),
        form: text(rule, 'form'),
        lead: number(rule, 'lead'),
        url: text(rule, 'url'),
        source: oneOf(rule, 'source', ['list', 'wikipedia', 'own'] as const),
      };
    }),
    due: maybe(fields, 'due', () => text(fields, 'due')),
    done: flag(fields, 'done'),
  };
}

export function parseDocument(value: unknown): TripDocument {
  const fields = record(value, 'document');
  return {
    id: text(fields, 'id'),
    owner: maybe(fields, 'owner', person),
    mine: flag(fields, 'mine'),
    kind: oneOf(fields, 'kind', documentKinds),
    name: text(fields, 'name'),
    note: text(fields, 'note'),
    // Not said by a server before documents had one: none.
    validFrom: maybe({ validFrom: fields.validFrom ?? null }, 'validFrom', () =>
      text(fields, 'validFrom'),
    ),
    validTo: maybe(fields, 'validTo', () => text(fields, 'validTo')),
    stop: maybe(fields, 'stop', () => text(fields, 'stop')),
    private: flag(fields, 'private'),
    file: maybe(fields, 'file', (item) => {
      const file = record(item, 'file');
      return { type: text(file, 'type'), size: number(file, 'size') };
    }),
    traveller: maybe(fields, 'traveller', () => text(fields, 'traveller')),
    // Not said by a server before a document could be a thing's.
    activity: maybe({ activity: fields.activity ?? null }, 'activity', (id) =>
      text({ id }, 'id'),
    ),
    // Not said by a server before a document could be a booking's.
    booking: maybe({ booking: fields.booking ?? null }, 'booking', (id) => text({ id }, 'id')),
    // Not said by a server before a document had a price.
    price: maybe({ price: fields.price ?? null }, 'price', money),
    paid: maybe({ paid: fields.paid ?? null }, 'paid', money),
  };
}

export function parseBeforeItem(value: unknown): BeforeItem {
  const fields = record(value, 'item');
  return { id: text(fields, 'id'), text: text(fields, 'text'), done: flag(fields, 'done') };
}

export function parseDocs(value: unknown): DocsScreen {
  const fields = record(value, 'docs');
  return {
    passport: maybe(fields, 'passport', () => text(fields, 'passport')),
    start: text(fields, 'start'),
    end: text(fields, 'end'),
    currency: text(fields, 'currency'),
    stops: list(fields, 'stops', (item) => {
      const stop = record(item, 'stop');
      return {
        id: text(stop, 'id'),
        city: text(stop, 'city'),
        country: text(stop, 'country'),
        currency: maybe(stop, 'currency', (code) => text({ code }, 'code')),
      };
    }),
    travellers: list(fields, 'travellers', (item) => {
      const traveller = record(item, 'traveller');
      return { id: text(traveller, 'id'), name: text(traveller, 'name') };
    }),
    crossings: list(fields, 'crossings', parseCrossing),
    documents: list(fields, 'documents', parseDocument),
    before: list(fields, 'before', parseBeforeItem),
  };
}

const docs = (trip: string) => `/_/api/trips/${encodeURIComponent(trip)}`;

/** The Docs screen: entry rules for the account's passport, documents, the list before leaving. */
export function getDocs(trip: string): Promise<DocsScreen> {
  return call(`${docs(trip)}/docs`, {}, parseDocs);
}

/** Sets the passport the signed-in account travels on, or forgets it with `null`. */
/** Says one's own rule at a country's border, or forgets it with `null`. */
export function setOwnRule(
  country: string,
  rule: { kind: EntryKind; days: number } | null,
): Promise<null> {
  return call(
    `/_/api/me/rules/${encodeURIComponent(country)}`,
    json('PUT', rule ?? { kind: null }),
    () => null,
  );
}

export function setPassport(passport: string | null): Promise<null> {
  return call('/_/api/me/passport', json('PUT', { passport }), () => null);
}

/** Ticks a border's task done for the signed-in account, or takes the tick back. */
export function tickEntry(
  trip: string,
  country: string,
  entry: number,
  done: boolean,
): Promise<null> {
  return call(
    `${docs(trip)}/entry/${encodeURIComponent(country)}/${String(entry)}`,
    json('PUT', { done }),
    () => null,
  );
}

/** Adds a document, without a file. */
export function addDocument(trip: string, input: DocumentInput): Promise<TripDocument> {
  return call(`${docs(trip)}/documents`, json('POST', input), parseDocument);
}

/** Changes a document. */
export function updateDocument(
  trip: string,
  id: string,
  input: DocumentInput,
): Promise<TripDocument> {
  return call(
    `${docs(trip)}/documents/${encodeURIComponent(id)}`,
    json('PUT', input),
    parseDocument,
  );
}

/** Removes a document and its file. */
export function removeDocument(trip: string, id: string): Promise<null> {
  return call(
    `${docs(trip)}/documents/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    () => null,
  );
}

/** The address of a document's file. */
export function documentFileUrl(trip: string, id: string): string {
  return `${docs(trip)}/documents/${encodeURIComponent(id)}/file`;
}

/** Keeps a document's file: a PDF, or a photo. */
export function uploadDocumentFile(trip: string, id: string, file: Blob): Promise<null> {
  return call(documentFileUrl(trip, id), { method: 'PUT', body: file }, () => null, file.type);
}

/** Removes a document's file; the document stays. */
export function removeDocumentFile(trip: string, id: string): Promise<null> {
  return call(documentFileUrl(trip, id), { method: 'DELETE' }, () => null);
}

/** Adds something to do before leaving. */
export function addBeforeItem(trip: string, itemText: string): Promise<BeforeItem> {
  return call(`${docs(trip)}/before`, json('POST', { text: itemText }), parseBeforeItem);
}

/** Changes an item before leaving: its text, and whether it is done. */
export function updateBeforeItem(
  trip: string,
  id: string,
  itemText: string,
  done: boolean,
): Promise<BeforeItem> {
  return call(
    `${docs(trip)}/before/${encodeURIComponent(id)}`,
    json('PUT', { text: itemText, done }),
    parseBeforeItem,
  );
}

/** Removes an item before leaving. */
export function removeBeforeItem(trip: string, id: string): Promise<null> {
  return call(
    `${docs(trip)}/before/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    () => null,
  );
}

/** A reference list: the airlines, the aircraft types, the aircraft by registration, the types' dimensions. */
export const referenceSets = [
  'airlines',
  'aircraft',
  'registrations',
  'dimensions',
  'ships',
  'shipping_lines',
  'rail_operators',
  'bus_operators',
  'train_types',
  'stations',
  'transit',
  'price_levels',
  'europe_price_levels',
  'car_models',
  'car_rentals',
  'bike_shares',
  'banknotes',
] as const;
export type ReferenceSet = (typeof referenceSets)[number];

/** A list as it is: how many, from where, since when — `null` while it is the one built in. */
export interface ReferenceState {
  name: ReferenceSet;
  records: number;
  source: string | null;
  updatedAt: string | null;
  /** Where it is refreshed from. */
  origin: string;
}

/** A step of a refresh, as logged: what it was, and its words or number. */
export interface ReferenceStep {
  step: 'fetch' | 'fetched' | 'file' | 'read' | 'kept' | 'failed' | 'stopped';
  /** The source fetched, the file's name, or what went wrong. */
  text: string | null;
  /** Bytes fetched or sent, records read, or seconds taken. */
  count: number | null;
}

/** A refresh: how it began and went; `ok` is `null` while it runs. */
export interface ReferenceRun {
  id: number;
  name: ReferenceSet;
  way: 'source' | 'file';
  startedAt: string;
  finishedAt: string | null;
  ok: boolean | null;
  records: number | null;
  log: ReferenceStep[];
}

export interface ReferenceOverview {
  sets: ReferenceState[];
  runs: ReferenceRun[];
}

const steps = ['fetch', 'fetched', 'file', 'read', 'kept', 'failed', 'stopped'] as const;

function parseStep(value: unknown): ReferenceStep {
  const fields = record(value, 'step');
  const words = fields.source ?? fields.name ?? fields.error;
  const count = fields.bytes ?? fields.records ?? fields.seconds;
  return {
    step: oneOf(fields, 'step', steps),
    text: typeof words === 'string' ? words : null,
    count: typeof count === 'number' ? count : null,
  };
}

export function parseReference(value: unknown): ReferenceOverview {
  const fields = record(value, 'reference');
  return {
    sets: list(fields, 'sets', (item) => {
      const set = record(item, 'set');
      return {
        name: oneOf(set, 'name', referenceSets),
        records: number(set, 'records'),
        source: maybe(set, 'source', () => text(set, 'source')),
        updatedAt: maybe(set, 'updatedAt', () => text(set, 'updatedAt')),
        origin: text(set, 'origin'),
      };
    }),
    runs: list(fields, 'runs', (item) => {
      const run = record(item, 'run');
      return {
        id: number(run, 'id'),
        name: oneOf(run, 'name', referenceSets),
        way: oneOf(run, 'way', ['source', 'file'] as const),
        startedAt: text(run, 'startedAt'),
        finishedAt: maybe(run, 'finishedAt', () => text(run, 'finishedAt')),
        ok: maybe(run, 'ok', () => flag(run, 'ok')),
        records: maybe(run, 'records', () => number(run, 'records')),
        log: list(run, 'log', parseStep),
      };
    }),
  };
}

/** Each reference list and the latest refreshes; `Forbidden` but for the instance's runner. */
export function getReference(): Promise<ReferenceOverview> {
  return call('/_/api/admin/reference', {}, parseReference);
}

/** Refreshes a list from its source; it runs on its own, its log in [getReference]. */
export function refreshReference(name: ReferenceSet): Promise<null> {
  return call(`/_/api/admin/reference/${name}/refresh`, json('POST', {}), () => null);
}

/** Reads a list from a file, gzip or not; it runs on its own, its log in [getReference]. */
export function uploadReference(name: ReferenceSet, file: File): Promise<null> {
  return call(
    `/_/api/admin/reference/${name}/upload?name=${encodeURIComponent(file.name)}`,
    { method: 'POST', body: file },
    () => null,
    'application/octet-stream',
  );
}

/** An aircraft known by its registration. */
export interface KnownRegistration {
  registration: string;
  typeCode: string;
  description: string;
  operator: string;
  /** Its type as a ticket says it, `Boeing 737-800 (B738)`, when the type is known. */
  aircraft: string | null;
}

/** The aircraft of a registration; `null` when it is not known. */
export function getRegistration(registration: string): Promise<KnownRegistration | null> {
  return call(
    `/_/api/reference/registrations/${encodeURIComponent(registration.trim())}`,
    {},
    (value) => {
      const fields = record(value, 'registration');
      return {
        registration: text(fields, 'registration'),
        typeCode: text(fields, 'typeCode'),
        description: text(fields, 'description'),
        operator: text(fields, 'operator'),
        aircraft: maybe(fields, 'aircraft', () => text(fields, 'aircraft')),
      };
    },
  ).catch((error: unknown) => {
    if (error instanceof NotFound) return null;
    throw error;
  });
}

/** A flight data service the instance may ask, as its runner sees it. */
export interface FlightService {
  name: string;
  label: string;
  /** Where one signs up for a key. */
  signup: string;
  position: number;
  enabled: boolean;
  /** Among the services the instance asks; one not added is only a choice. */
  added: boolean;
  /** Its key's last four characters; `null` without a key. */
  keyEnd: string | null;
  monthlyLimit: number;
  used: { day: number; month: number; year: number };
  /** What went wrong when it was last asked and failed. */
  lastError: string;
}

/** A request for a flight, as logged. */
export interface FlightRequest {
  service: string;
  at: string;
  number: string;
  day: string;
  outcome: 'found' | 'none' | 'failed' | 'cached';
  error: string;
}

export interface FlightsOverview {
  services: FlightService[];
  requests: FlightRequest[];
}

/** A flight as a service said it: each time a wall clock at its place, `2027-01-09T15:50`. */
export interface LookedUpFlight {
  number: string | null;
  airline: string | null;
  origin: string | null;
  destination: string | null;
  departure: string | null;
  arrival: string | null;
  actualDeparture: string | null;
  actualArrival: string | null;
  terminalFrom: string | null;
  terminalTo: string | null;
  gate: string | null;
  arrivalGate: string | null;
  aircraft: string | null;
  registration: string | null;
  service: string;
}

/** What each service said when a flight was looked up. */
export interface FlightTry {
  service: string;
  outcome: 'found' | 'none' | 'failed' | 'cached' | 'spent';
  error: string;
}

const outcomes = ['found', 'none', 'failed', 'cached'] as const;

function parseFlightsOverview(value: unknown): FlightsOverview {
  const fields = record(value, 'flights');
  return {
    services: list(fields, 'services', (item) => {
      const service = record(item, 'service');
      const used = record(service.used, 'used');
      return {
        name: text(service, 'name'),
        label: text(service, 'label'),
        signup: text(service, 'signup'),
        position: number(service, 'position'),
        enabled: flag(service, 'enabled'),
        added: flag(service, 'added'),
        keyEnd: maybe(service, 'keyEnd', () => text(service, 'keyEnd')),
        monthlyLimit: number(service, 'monthlyLimit'),
        used: {
          day: number(used, 'day'),
          month: number(used, 'month'),
          year: number(used, 'year'),
        },
        lastError: text(service, 'lastError'),
      };
    }),
    requests: list(fields, 'requests', (item) => {
      const request = record(item, 'request');
      return {
        service: text(request, 'service'),
        at: text(request, 'at'),
        number: text(request, 'number'),
        day: text(request, 'day'),
        outcome: oneOf(request, 'outcome', outcomes),
        error: text(request, 'error'),
      };
    }),
  };
}

/** The flight data services and the latest requests; `Forbidden` but for the instance's runner. */
export function getFlightServices(): Promise<FlightsOverview> {
  return call('/_/api/admin/flights', {}, parseFlightsOverview);
}

/** Changes a flight data service: its key (`''` takes it off), allowance, use or place. */
export function setFlightService(
  name: string,
  change: {
    apiKey?: string;
    monthlyLimit?: number;
    enabled?: boolean;
    position?: number;
    added?: boolean;
  },
): Promise<null> {
  return call(
    `/_/api/admin/flights/${encodeURIComponent(name)}`,
    json('PUT', change),
    () => null,
  );
}

const tries = ['found', 'none', 'failed', 'cached', 'spent'] as const;

/** A flight by its number and day, as kept or as the services say it, asked in turn. */
export function lookupFlight(
  flightNumber: string,
  day: string,
): Promise<{ found: LookedUpFlight | null; tried: FlightTry[] }> {
  return call(
    `/_/api/reference/flights/${encodeURIComponent(flightNumber.trim())}/${day}`,
    {},
    (value) => {
      const fields = record(value, 'lookup');
      const said = (found: Fields, key: string) => maybe(found, key, () => text(found, key));
      return {
        found: maybe(fields, 'found', (item) => {
          const found = record(item, 'found');
          return {
            number: said(found, 'number'),
            airline: said(found, 'airline'),
            origin: said(found, 'origin'),
            destination: said(found, 'destination'),
            departure: said(found, 'departure'),
            arrival: said(found, 'arrival'),
            actualDeparture: said(found, 'actualDeparture'),
            actualArrival: said(found, 'actualArrival'),
            terminalFrom: said(found, 'terminalFrom'),
            terminalTo: said(found, 'terminalTo'),
            gate: said(found, 'gate'),
            arrivalGate: said(found, 'arrivalGate'),
            aircraft: said(found, 'aircraft'),
            registration: said(found, 'registration'),
            service: text(found, 'service'),
          };
        }),
        tried: list(fields, 'tried', (item) => {
          const tried = record(item, 'tried');
          return {
            service: text(tried, 'service'),
            outcome: oneOf(tried, 'outcome', tries),
            error: text(tried, 'error'),
          };
        }),
      };
    },
  );
}

/** An airline's layout of an aircraft type: its seats by class, `C12 Y168`, and where it is from. */
export interface SeatLayout {
  airline: string | null;
  classes: string;
  pitchCm: number | null;
  skipRows: string;
  exitRows: string;
  source: 'own' | 'wikipedia' | 'typical';
}

/** A type's cabin; places along it are fractions of its length from the nose. */
export interface CabinGeometry {
  widthM: number;
  frameM: number;
  cabin: [number, number];
  wing: [number, number, number, number];
  doors: number[];
  exits: number[];
  /** Seats across a row, aisles as dashes: `ABC-DEF`. */
  economy: string;
  premium: string | null;
  business: string | null;
}

/** What a type's seat map is drawn from, each part null when not known. */
export interface SeatMapData {
  icao: string;
  model: string | null;
  dimensions: {
    lengthM: number | null;
    wingspanM: number | null;
    engines: number | null;
  } | null;
  cabin: CabinGeometry | null;
  layout: SeatLayout | null;
}

const layoutSources = ['own', 'wikipedia', 'typical'] as const;

function parseLayout(value: unknown): SeatLayout {
  const fields = record(value, 'layout');
  return {
    airline: maybe(fields, 'airline', () => text(fields, 'airline')),
    classes: text(fields, 'classes'),
    pitchCm: maybe(fields, 'pitchCm', () => number(fields, 'pitchCm')),
    skipRows: text(fields, 'skipRows'),
    exitRows: text(fields, 'exitRows'),
    source: oneOf(fields, 'source', layoutSources),
  };
}

function numbers(fields: Fields, key: string): number[] {
  return list(fields, key, (item) => {
    if (typeof item !== 'number') throw new ApiError(`${key}: expected numbers`);
    return item;
  });
}

function nth(list: number[], at: number, key: string): number {
  const value = list[at];
  if (value === undefined) throw new ApiError(`${key}: too few numbers`);
  return value;
}

function parseSeatMap(value: unknown): SeatMapData {
  const fields = record(value, 'seatMap');
  return {
    icao: text(fields, 'icao'),
    model: maybe(fields, 'model', () => text(fields, 'model')),
    dimensions: maybe(fields, 'dimensions', (item) => {
      const d = record(item, 'dimensions');
      return {
        lengthM: maybe(d, 'lengthM', () => number(d, 'lengthM')),
        wingspanM: maybe(d, 'wingspanM', () => number(d, 'wingspanM')),
        engines: maybe(d, 'engines', () => number(d, 'engines')),
      };
    }),
    cabin: maybe(fields, 'cabin', (item) => {
      const c = record(item, 'cabin');
      const cabin = numbers(c, 'cabin');
      const wing = numbers(c, 'wing');
      return {
        widthM: number(c, 'widthM'),
        frameM: number(c, 'frameM'),
        cabin: [nth(cabin, 0, 'cabin'), nth(cabin, 1, 'cabin')],
        wing: [
          nth(wing, 0, 'wing'),
          nth(wing, 1, 'wing'),
          nth(wing, 2, 'wing'),
          nth(wing, 3, 'wing'),
        ],
        doors: numbers(c, 'doors'),
        exits: numbers(c, 'exits'),
        economy: text(c, 'economy'),
        premium: maybe(c, 'premium', () => text(c, 'premium')),
        business: maybe(c, 'business', () => text(c, 'business')),
      };
    }),
    layout: maybe(fields, 'layout', parseLayout),
  };
}

/** What a type's seat map is drawn from, as an airline lays it out when one is said. */
export function getSeatMap(icao: string, airline: string | null): Promise<SeatMapData> {
  const query = airline === null ? '' : `?airline=${encodeURIComponent(airline)}`;
  return call(`/_/api/reference/seatmap/${encodeURIComponent(icao)}${query}`, {}, parseSeatMap);
}

/** A traveller's own layout of a type, kept over the airline's page; `InvalidInput` naming what does not read. */
export function setLayout(
  airline: string,
  icao: string,
  layout: { classes: string; pitchCm: number | null; skipRows: string; exitRows: string },
): Promise<SeatLayout> {
  return call(
    `/_/api/reference/layouts/${encodeURIComponent(airline)}/${encodeURIComponent(icao)}`,
    json('PUT', layout),
    parseLayout,
  );
}

/** Takes a traveller's own layout off: the airline's page is read again. */
export function forgetLayout(airline: string, icao: string): Promise<null> {
  return call(
    `/_/api/reference/layouts/${encodeURIComponent(airline)}/${encodeURIComponent(icao)}`,
    { method: 'DELETE' },
    () => null,
  );
}

/** A day a country keeps while the trip is there. */
export interface CountryHoliday {
  day: string;
  /** As the country's calendar names it: `Chulalongkorn Day`. */
  name: string;
  /** Offices and banks shut, rather than an observance or a festival. */
  public: boolean;
}

/** What is said of a country of the trip beyond its days and money. */
export interface CountryFacts {
  country: string;
  /** Its currency, `THB`; none for a country without one. */
  currency: string | null;
  digits: number | null;
  /** How many of its currency the trip's buys today; none without a rate. */
  rate: Rate | null;
  /** Its plugs by the IEC's letters, `ABCF`; `''` when not known. */
  plugs: string;
  volts: number | null;
  /** The mains frequency, `[50]`, Japan's `[50, 60]`; none when not known. */
  hertz: number[];
  /** Its money in hand, each amount in its own unit; `null` while not known. */
  money: { notes: number[]; rareNotes: number[]; coins: number[]; rareCoins: number[] } | null;
  holidays: CountryHoliday[];
}

/** The trip's countries, in route order, each with its money, plugs and holidays. */
export interface CountriesFacts {
  currency: string;
  /** The day of the newest ECB rates kept. */
  ratesDay: string | null;
  countries: CountryFacts[];
}

export function parseCountryFacts(value: unknown): CountriesFacts {
  const fields = record(value, 'countries');
  return {
    currency: text(fields, 'currency'),
    ratesDay: maybe(fields, 'ratesDay', () => text(fields, 'ratesDay')),
    countries: list(fields, 'countries', (item) => {
      const one = record(item, 'country');
      return {
        country: text(one, 'country'),
        currency: maybe(one, 'currency', () => text(one, 'currency')),
        digits: maybe(one, 'digits', () => number(one, 'digits')),
        rate: maybe(one, 'rate', rate),
        plugs: text(one, 'plugs'),
        volts: maybe(one, 'volts', () => number(one, 'volts')),
        hertz: numbers(one, 'hertz'),
        money: maybe(one, 'money', (value) => {
          const money = record(value, 'money');
          return {
            notes: numbers(money, 'notes'),
            rareNotes: numbers(money, 'rareNotes'),
            coins: numbers(money, 'coins'),
            rareCoins: numbers(money, 'rareCoins'),
          };
        }),
        holidays: list(one, 'holidays', (said) => {
          const holiday = record(said, 'holiday');
          return {
            day: text(holiday, 'day'),
            name: text(holiday, 'name'),
            public: flag(holiday, 'public'),
          };
        }),
      };
    }),
  };
}

/** A trip's countries as the Countries page says them, holidays in `lang`. */
export function getCountryFacts(trip: string, lang: string): Promise<CountriesFacts> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/countries?lang=${encodeURIComponent(lang)}`,
    {},
    parseCountryFacts,
  );
}

/** What a day is for a person: on leave, sick, or working while away. */
export const dayKinds = ['leave', 'sick', 'work'] as const;
export type DayKind = (typeof dayKinds)[number];

/** A person on the trip, their working week and the days they marked (the Leave mockup). */
export interface LeavePerson {
  /** `u:{user}` for a member, `t:{traveller}` for a fellow traveller. */
  person: string;
  name: string;
  /** Their working days, Monday first: `1111100`. */
  workdays: string;
  leavePerYear: number;
  /** The days of leave the trip takes: those marked on a working day not a public holiday. */
  used: number;
  days: { day: string; kind: DayKind }[];
}

export function parseLeave(value: unknown): LeavePerson[] {
  return list(record(value, 'leave'), 'people', (item) => {
    const one = record(item, 'person');
    return {
      person: text(one, 'person'),
      name: text(one, 'name'),
      workdays: text(one, 'workdays'),
      leavePerYear: number(one, 'leavePerYear'),
      used: number(one, 'used'),
      days: list(one, 'days', (said) => {
        const day = record(said, 'day');
        return { day: text(day, 'day'), kind: oneOf(day, 'kind', dayKinds) };
      }),
    };
  });
}

const leavePath = (trip: string, person?: string) =>
  `/_/api/trips/${encodeURIComponent(trip)}/leave${
    person === undefined ? '' : `/${encodeURIComponent(person)}`
  }`;

/** Each person on the trip with their week and the days they marked. */
export function getLeave(trip: string): Promise<LeavePerson[]> {
  return call(leavePath(trip), {}, parseLeave);
}

/** Keeps a person's working days, `1111100`, and days of leave a year. */
export function setWeek(
  trip: string,
  person: string,
  workdays: string,
  leavePerYear: number,
): Promise<LeavePerson[]> {
  return call(leavePath(trip, person), json('PUT', { workdays, leavePerYear }), parseLeave);
}

/** Marks a person's day as one kind, or clears it with `null`. */
export function markDay(
  trip: string,
  person: string,
  day: string,
  kind: DayKind | null,
): Promise<LeavePerson[]> {
  const path = `${leavePath(trip, person)}/${encodeURIComponent(day)}`;
  return kind === null
    ? call(path, { method: 'DELETE' }, parseLeave)
    : call(path, json('PUT', { kind }), parseLeave);
}

/** The hours an account works at home, in minutes after midnight: 540 to 1020 is 09:00–17:00. */
export interface WorkHours {
  from: number;
  to: number;
}

function parseWork(value: unknown): WorkHours {
  const fields = record(value, 'work');
  return { from: number(fields, 'from'), to: number(fields, 'to') };
}

/** The hours the account works at home. */
export function getWork(): Promise<WorkHours> {
  return call('/_/api/me/work', {}, parseWork);
}

/** Keeps the hours the account works at home. */
export function setWork(work: WorkHours): Promise<WorkHours> {
  return call('/_/api/me/work', json('PUT', work), parseWork);
}

/** A file a trip is taken elsewhere as. */
export type ExportKind = 'pdf' | 'gpx' | 'kml' | 'kmz' | 'geojson' | 'xlsx' | 'csv';

/** What an export holds: each part on (`1`) or off (`0`), and its language and paper. */
export type ExportChoices = Record<string, boolean | string>;

/** The address of a trip's file of `kind`, holding what `choices` say. */
export function exportPath(
  trip: string,
  kind: ExportKind | 'book' | 'rows',
  choices: ExportChoices,
): string {
  const query = Object.entries(choices)
    .map(([key, value]) => {
      const said = typeof value === 'string' ? value : value ? '1' : '0';
      return `${key}=${encodeURIComponent(said)}`;
    })
    .join('&');
  return `/_/api/trips/${encodeURIComponent(trip)}/export/${kind}?${query}`;
}

/** The trip book as it comes out: its pages, its size, and the page each part starts on. */
export interface BookPreview {
  pages: number;
  bytes: number;
  starts: Record<'cover' | 'days' | 'bookings' | 'entry' | 'money', number | null>;
}

function parseBookPreview(value: unknown): BookPreview {
  const fields = record(value, 'book');
  const starts = record(fields.starts, 'starts');
  const page = (key: string) => maybe(starts, key, (at) => number({ at }, 'at'));
  return {
    pages: number(fields, 'pages'),
    bytes: number(fields, 'bytes'),
    starts: {
      cover: page('cover'),
      days: page('days'),
      bookings: page('bookings'),
      entry: page('entry'),
      money: page('money'),
    },
  };
}

/** How the trip book with `choices` comes out, before it is downloaded. */
export function getBookPreview(trip: string, choices: ExportChoices): Promise<BookPreview> {
  return call(exportPath(trip, 'book', choices), {}, parseBookPreview);
}

/** A table's first rows, its heads first, as the export window shows them. */
export interface TableRows {
  name: string;
  rows: string[][];
}

function parseTableRows(value: unknown): TableRows[] {
  return list(record(value, 'tables'), 'sheets', (sheet) => {
    const fields = record(sheet, 'sheet');
    return {
      name: text(fields, 'name'),
      rows: list(fields, 'rows', (row) =>
        list({ row }, 'row', (cell) => text({ cell }, 'cell')),
      ),
    };
  });
}

/** The first rows of the tables `choices` hold. */
export function getTableRows(trip: string, choices: ExportChoices): Promise<TableRows[]> {
  return call(exportPath(trip, 'rows', choices), {}, parseTableRows);
}

/** The trip's calendar link, which a calendar app reads by itself; `null` until one is made. */
export function getCalendarFeed(trip: string): Promise<string | null> {
  return call(`/_/api/trips/${encodeURIComponent(trip)}/calendar/feed`, {}, (value) =>
    optionalText(record(value, 'feed'), 'url'),
  );
}

/** A new calendar link for the trip; the old one stops working. */
export function renewCalendarFeed(trip: string): Promise<string> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/calendar/feed`,
    json('POST', {}),
    (value) => text(record(value, 'feed'), 'url'),
  );
}

/** Where a place read is, beside the trip: the stop within 30 km it goes to, the nearest stop
 * and how far, the town it is in. */
export interface PlaceNear {
  stop: string | null;
  nearest: { stop: string; km: number } | null;
  town: { city: string; country: string } | null;
}

/** A place read from a list or a file. */
export interface ReadPlace {
  name: string;
  address: string | null;
  kind: string | null;
  lat: number;
  lon: number;
  near: PlaceNear;
}

/** A list read from a link: its name, where it is from, its places. */
export interface ReadList {
  name: string;
  source: 'google' | 'naver';
  places: ReadPlace[];
}

/** A file's trail: its name, length, climb, the days it is walked, its points and its ends. */
export interface ReadTrail {
  name: string | null;
  km: number;
  ascent: number | null;
  descent: number | null;
  days: number;
  points: [number, number, number | null][];
  start: PlaceNear | null;
  end: PlaceNear | null;
}

function placeNear(value: unknown): PlaceNear {
  const fields = record(value, 'near');
  return {
    stop: optionalText(fields, 'stop'),
    nearest: maybe(fields, 'nearest', (nearest) => {
      const at = record(nearest, 'nearest');
      return { stop: text(at, 'stop'), km: number(at, 'km') };
    }),
    town: maybe(fields, 'town', (town) => {
      const at = record(town, 'town');
      return { city: text(at, 'city'), country: text(at, 'country') };
    }),
  };
}

function readPlace(value: unknown): ReadPlace {
  const fields = record(value, 'place');
  return {
    name: text(fields, 'name'),
    address: optionalText(fields, 'address'),
    kind: optionalText(fields, 'kind'),
    lat: number(fields, 'lat'),
    lon: number(fields, 'lon'),
    near: placeNear(fields.near),
  };
}

/** Reads the list a Google Maps or Naver Map link shares (`InvalidInput` on `url` for another
 * link, `NotFound` for a list not had). */
export function readList(trip: string, url: string): Promise<ReadList> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/import/list`,
    json('POST', { url }),
    (value) => {
      const fields = record(value, 'list');
      return {
        name: text(fields, 'name'),
        source: oneOf(fields, 'source', ['google', 'naver'] as const),
        places: list(fields, 'places', readPlace),
      };
    },
  );
}

/** Reads a GPX, KML or KMZ file: its trail and its named places (`InvalidInput` on `file`). */
export function readPlacesFile(
  trip: string,
  file: Blob,
): Promise<{ trail: ReadTrail | null; places: ReadPlace[] }> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/import/file`,
    { method: 'POST', body: file },
    (value) => {
      const fields = record(value, 'file');
      return {
        trail: maybe(fields, 'trail', (trail) => {
          const at = record(trail, 'trail');
          return {
            name: optionalText(at, 'name'),
            km: number(at, 'km'),
            ascent: maybe(at, 'ascent', (n) => number({ n }, 'n')),
            descent: maybe(at, 'descent', (n) => number({ n }, 'n')),
            days: number(at, 'days'),
            points: list(at, 'points', (point) => {
              const [lon, lat, height] = list({ point }, 'point', (n) => n);
              return [
                number({ lon }, 'lon'),
                number({ lat }, 'lat'),
                height === null ? null : number({ height }, 'height'),
              ];
            }),
            start: maybe(at, 'start', placeNear),
            end: maybe(at, 'end', placeNear),
          };
        }),
        places: list(fields, 'places', readPlace),
      };
    },
  );
}

/** Where a chosen place goes: a stop, on a day or waiting; a new town after a stop; or none. */
export type PlaceTo =
  | { stop: string; day: string | null }
  | { after: string; city: string; country: string }
  | null;

/** What is taken into a trip at once: its places, each where chosen, and a trail. */
export interface TakeIn {
  source: 'google' | 'naver' | 'file';
  sourceUrl: string | null;
  places: {
    name: string;
    address: string | null;
    kind: string | null;
    lat: number;
    lon: number;
    to: PlaceTo;
  }[];
  trail: {
    name: string;
    after: string;
    city: string;
    country: string;
    days: number;
    points: [number, number, number | null][];
  } | null;
}

/** Takes chosen places and a trail into a trip at once → its plan. */
export function takeIn(trip: string, input: TakeIn): Promise<Plan> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/import`,
    json('POST', input),
    parsePlan,
  );
}

/** A place on a stop waiting for a day. */
export interface WaitingPlace {
  stop: string;
  id: string;
  name: string;
  address: string | null;
  kind: string | null;
  lat: number;
  lon: number;
}

/** The places on a trip's stops waiting for a day. */
export function getWaitingPlaces(trip: string): Promise<WaitingPlace[]> {
  return call(`/_/api/trips/${encodeURIComponent(trip)}/places`, {}, (value) => {
    if (!Array.isArray(value)) throw new ApiError('places: expected a list');
    return value.map((item) => {
      const fields = record(item, 'place');
      return {
        stop: text(fields, 'stop'),
        id: text(fields, 'id'),
        name: text(fields, 'name'),
        address: optionalText(fields, 'address'),
        kind: optionalText(fields, 'kind'),
        lat: number(fields, 'lat'),
        lon: number(fields, 'lon'),
      };
    });
  });
}

/** A waiting place a thing to do at its stop on `day` → the plan. */
export function placeOnDay(
  trip: string,
  place: string,
  stop: string,
  day: string,
): Promise<Plan> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/places/${encodeURIComponent(place)}/day`,
    json('POST', { stop, day }),
    parsePlan,
  );
}

/** A waiting place taken off its stop; it stays among one's own places. */
export function takePlaceOff(trip: string, place: string, stop: string): Promise<null> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/places/${encodeURIComponent(place)}?stop=${encodeURIComponent(stop)}`,
    { method: 'DELETE' },
    () => null,
  );
}

/** A line of a trip's footprint: a way on, the nights in a country, or the rides in town. */
export type FootprintLine =
  | {
      kind: 'way';
      name: string;
      km: number;
      kg: number;
      at: number;
      mode: Mode;
      cabin: 'economy' | 'premium' | 'business' | 'first' | null;
      via: string[];
    }
  | {
      kind: 'stay';
      name: string;
      km: number;
      kg: number;
      country: string;
      nights: number;
      perNight: number;
      sharing: number;
      estimated: boolean;
    }
  | { kind: 'towns'; name: string; km: number; kg: number; rides: number };

/** A flight that could go on the ground instead. */
export interface FootprintSwap {
  at: number;
  /** The stop it leaves from. */
  stop: string;
  from: string;
  to: string;
  mode: Mode;
  saves: number;
  longer: number;
  /** In minor units of the trip's money. */
  dearer: number;
}

/** A trip's carbon footprint for one traveller, in kilograms of CO₂e. */
export interface Footprint {
  kg: number;
  days: number;
  km: number;
  /** Whether a flight's effect high up is counted. */
  rf: boolean;
  lines: FootprintLine[];
  swaps: FootprintSwap[];
}

function footprintLine(value: unknown): FootprintLine {
  const fields = record(value, 'line');
  const common = {
    name: text(fields, 'name'),
    km: number(fields, 'km'),
    kg: number(fields, 'kg'),
  };
  const kind = oneOf(fields, 'kind', ['way', 'stay', 'towns'] as const);
  if (kind === 'way') {
    return {
      ...common,
      kind,
      at: number(fields, 'at'),
      mode: oneOf(fields, 'mode', modes),
      cabin:
        fields.cabin === null
          ? null
          : oneOf(fields, 'cabin', ['economy', 'premium', 'business', 'first'] as const),
      via: list(fields, 'via', (city) => text({ city }, 'city')),
    };
  }
  if (kind === 'stay') {
    return {
      ...common,
      kind,
      country: text(fields, 'country'),
      nights: number(fields, 'nights'),
      perNight: number(fields, 'perNight'),
      sharing: number(fields, 'sharing'),
      estimated: flag(fields, 'estimated'),
    };
  }
  return { ...common, kind, rides: number(fields, 'rides') };
}

export function parseFootprint(value: unknown): Footprint {
  const fields = record(value, 'footprint');
  return {
    kg: number(fields, 'kg'),
    days: number(fields, 'days'),
    km: number(fields, 'km'),
    rf: flag(fields, 'rf'),
    lines: list(fields, 'lines', footprintLine),
    swaps: list(fields, 'swaps', (swap) => {
      const at = record(swap, 'swap');
      return {
        at: number(at, 'at'),
        stop: text(at, 'stop'),
        from: text(at, 'from'),
        to: text(at, 'to'),
        mode: oneOf(at, 'mode', modes),
        saves: number(at, 'saves'),
        longer: number(at, 'longer'),
        dearer: number(at, 'dearer'),
      };
    }),
  };
}

/** A trip's carbon footprint for one traveller; `rf` counts a flight's effect high up. */
export function getFootprint(trip: string, rf: boolean): Promise<Footprint> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/emissions?rf=${rf ? '1' : '0'}`,
    {},
    parseFootprint,
  );
}

/** A bike station near a way's end: how far, and what it has now when the way is today. */
export interface ShareStation extends Point {
  name: string;
  metres: number;
  bikes: number | null;
  docks: number | null;
  /** When it was said, seconds since 1970. */
  at: number | null;
}

/** A way across town by a shared bike or scooter, from a system's public GBFS feeds. */
export interface ShareWay {
  system: string;
  kind: 'bike' | 'scooter';
  /** From a station to a station; else from a vehicle left anywhere. */
  docked: boolean;
  minutes: number;
  /** What the ride costs in whole units of its money, when the system says. */
  price: { currency: string; amount: number } | null;
  from: ShareStation | null;
  to: ShareStation | null;
  /** The stations near the start, the nearest first. */
  stations: ShareStation[];
  /** How many are left within 500 m, when the way is today. */
  near: number | null;
}

function shareStation(value: unknown): ShareStation {
  const fields = record(value, 'station');
  const count = (key: string) => maybe(fields, key, () => number(fields, key));
  return {
    name: text(fields, 'name'),
    lat: number(fields, 'lat'),
    lon: number(fields, 'lon'),
    metres: number(fields, 'metres'),
    bikes: count('bikes'),
    docks: count('docks'),
    at: count('at'),
  };
}

export function parseShares(value: unknown): { ways: ShareWay[]; rides: 'ok' | 'off' } {
  const fields = record(value, 'rides');
  return {
    ways: list(fields, 'ways', (item) => {
      const way = record(item, 'way');
      return {
        system: text(way, 'system'),
        kind: oneOf(way, 'kind', ['bike', 'scooter'] as const),
        docked: flag(way, 'docked'),
        minutes: number(way, 'minutes'),
        price: maybe(way, 'price', (price) => {
          const said = record(price, 'price');
          return { currency: text(said, 'currency'), amount: number(said, 'amount') };
        }),
        from: maybe(way, 'from', shareStation),
        to: maybe(way, 'to', shareStation),
        stations: list(way, 'stations', shareStation),
        near: maybe(way, 'near', () => number(way, 'near')),
      };
    }),
    rides: oneOf(fields, 'rides', ['ok', 'off'] as const),
  };
}

/**
 * The town's shared bikes and scooters from one point to another, the quickest first; `live`
 * when the way is today, and what each station and vehicle has now is said.
 */
export function getShares(
  from: Point,
  to: Point,
  town: { country: string; city: string },
  live: boolean,
): Promise<{ ways: ShareWay[]; rides: 'ok' | 'off' }> {
  const at = (p: Point) => `${String(p.lat)},${String(p.lon)}`;
  const query = new URLSearchParams({
    from: at(from),
    to: at(to),
    country: town.country,
    city: town.city,
    live: live ? '1' : '0',
  });
  return call(`/_/api/places/rides?${query.toString()}`, {}, parseShares);
}

/** A car park to leave a car at and ride on from, as OpenStreetMap tags it. */
export interface Parking extends Point {
  name: string;
  metres: number;
  /** What it costs, as tagged: `¥800/day`. */
  charge: string | null;
  fee: boolean | null;
  /** What one rides on by: `train`, `bus`, or `yes`. */
  by: string;
}

export function parseParking(value: unknown): Parking[] {
  const fields = record(value, 'parking');
  return list(fields, 'parking', (item) => {
    const park = record(item, 'park');
    return {
      name: text(park, 'name'),
      lat: number(park, 'lat'),
      lon: number(park, 'lon'),
      metres: number(park, 'metres'),
      charge: maybe(park, 'charge', () => text(park, 'charge')),
      fee: maybe(park, 'fee', () => flag(park, 'fee')),
      by: text(park, 'by'),
    };
  });
}

/** The car parks to ride on from within fifteen kilometres of a point, the nearest first. */
export function getParking(at: Point): Promise<Parking[]> {
  return call(`/_/api/places/parking?at=${String(at.lat)},${String(at.lon)}`, {}, parseParking);
}

/** How a mail's booking was read: from the schema.org data in it, a Wallet pass, a calendar
 * event, an attached PDF or picture, or its words. */
export const mailReads = ['schema', 'wallet', 'calendar', 'pdf', 'picture', 'text'] as const;
export type MailRead = (typeof mailReads)[number];

/** A confirmation forwarded to the trip's address or dropped on its page, as the list has it. */
export interface MailItem {
  id: string;
  way: 'mail' | 'drop';
  subject: string;
  /** Who sent the confirmation: `Booking.com <noreply@booking.com>`. */
  sender: string;
  /** A file dropped by its name; empty for a mail or text pasted. */
  fileName: string;
  summary: {
    kind: 'stay' | 'ticket';
    name: string;
    day: string | null;
    read: MailRead;
    forwardedBy: string | null;
  };
  /** To be looked at, its booking added, left out, or none found in it. */
  status: 'review' | 'added' | 'left' | 'none';
  bookingId: string | null;
  receivedAt: string;
}

/** One part of a mail's booking and the line of the mail it was read from. */
export interface MailMark {
  part: 'name' | 'reference' | 'price' | 'checkIn' | 'checkOut' | 'pay' | 'address' | 'flight';
  line: string;
}

/** A mail in full: what was read in it and its words, while it is kept. */
export interface MailOpened extends MailItem {
  kept: boolean;
  text: string | null;
  forwardedBy: string | null;
  reading: {
    draft: Draft;
    found: boolean;
    read: MailRead;
    address: string | null;
    seat: string | null;
    marks: MailMark[];
  } | null;
}

function mailItem(value: unknown): MailItem {
  const fields = record(value, 'mail');
  const summary = record(fields.summary, 'summary');
  return {
    id: text(fields, 'id'),
    way: oneOf(fields, 'way', ['mail', 'drop'] as const),
    subject: text(fields, 'subject'),
    sender: text(fields, 'sender'),
    fileName: text(fields, 'fileName'),
    summary: {
      kind: oneOf(summary, 'kind', ['stay', 'ticket'] as const),
      name: text(summary, 'name'),
      day: maybe(summary, 'day', () => text(summary, 'day')),
      read: oneOf(summary, 'read', mailReads),
      forwardedBy: maybe(summary, 'forwardedBy', () => text(summary, 'forwardedBy')),
    },
    status: oneOf(fields, 'status', ['review', 'added', 'left', 'none'] as const),
    bookingId: maybe(fields, 'bookingId', () => text(fields, 'bookingId')),
    receivedAt: text(fields, 'receivedAt'),
  };
}

export function parseMails(value: unknown): { address: string | null; mails: MailItem[] } {
  const fields = record(value, 'mails');
  return {
    address: maybe(fields, 'address', () => text(fields, 'address')),
    mails: list(fields, 'mails', mailItem),
  };
}

export function parseMail(value: unknown): MailOpened {
  const fields = record(value, 'mail');
  const optional = (from: Record<string, unknown>, key: string) =>
    maybe(from, key, () => text(from, key));
  return {
    ...mailItem(value),
    kept: flag(fields, 'kept'),
    text: optional(fields, 'text'),
    forwardedBy: optional(fields, 'forwardedBy'),
    reading: maybe(fields, 'reading', (item) => {
      const reading = record(item, 'reading');
      return {
        draft: {
          ...parseDraft(reading.draft),
          checkIn: optional(reading, 'checkIn'),
          checkOut: optional(reading, 'checkOut'),
          flight: optional(reading, 'flight'),
        },
        found: flag(reading, 'found'),
        read: oneOf(reading, 'read', mailReads),
        address: optional(reading, 'address'),
        seat: optional(reading, 'seat'),
        marks: list(reading, 'marks', (mark) => {
          const said = record(mark, 'mark');
          return {
            part: oneOf(said, 'part', [
              'name',
              'reference',
              'price',
              'checkIn',
              'checkOut',
              'pay',
              'address',
              'flight',
            ] as const),
            line: text(said, 'line'),
          };
        }),
      };
    }),
  };
}

/** The trip's address to forward confirmations to (`null` when this server receives no mail),
 * and the mails it has, the latest first. */
export function getMails(trip: string): Promise<{ address: string | null; mails: MailItem[] }> {
  return call(`/_/api/trips/${encodeURIComponent(trip)}/mail`, {}, parseMails);
}

/** One of the trip's mails, read. */
export function getMail(trip: string, id: string): Promise<MailOpened> {
  const at = (part: string) => encodeURIComponent(part);
  return call(`/_/api/trips/${at(trip)}/mail/${at(id)}`, {}, parseMail);
}

/** A mail, a Wallet pass, a calendar file, a PDF or a picture dropped on the page (`name` its
 * file's), or a mail's words pasted (no name): kept and read. */
export function dropMail(trip: string, body: Blob | string, name = ''): Promise<MailItem> {
  const named = name === '' ? '' : `?name=${encodeURIComponent(name)}`;
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/mail${named}`,
    { method: 'POST', body },
    mailItem,
    'application/octet-stream',
  );
}

/** Says what became of a mail's booking: added as `booking`, left out, or to look at again. */
export function settleMail(
  trip: string,
  id: string,
  status: 'added' | 'left' | 'review',
  booking: string | null = null,
): Promise<null> {
  const at = (part: string) => encodeURIComponent(part);
  return call(
    `/_/api/trips/${at(trip)}/mail/${at(id)}`,
    json('PUT', { status, booking }),
    () => null,
  );
}

/** A change a flight's watch found: a delay, a gate moved, check-in's desks, a belt. */
export interface FlightEvent {
  at: string;
  kind:
    | 'delay'
    | 'ontime'
    | 'cancelled'
    | 'gate'
    | 'terminal'
    | 'checkin'
    | 'belt'
    | 'departed'
    | 'landed';
  /** What it says: `{minutes, departs}` of a delay, `{from, to}` of a move. */
  detail: Record<string, unknown>;
  service: string;
}

/** A flight of the trip as watched: what its service said last, and its events. */
export interface WatchedFlight {
  booking: string;
  leg: number;
  number: string;
  origin: string;
  destination: string;
  originZone: string | null;
  destinationZone: string | null;
  departs: string;
  arrives: string | null;
  actualDeparts: string | null;
  actualArrives: string | null;
  /** In minutes, when a time was said. */
  delay: number | null;
  gate: string | null;
  terminal: string | null;
  checkIn: string | null;
  belt: string | null;
  status: 'scheduled' | 'delayed' | 'departed' | 'landed' | 'cancelled' | null;
  vehicle: string | null;
  checkedAt: string | null;
  nextCheck: string;
  events: FlightEvent[];
}

const flightStatuses = ['scheduled', 'delayed', 'departed', 'landed', 'cancelled'] as const;
const flightEvents = [
  'delay',
  'ontime',
  'cancelled',
  'gate',
  'terminal',
  'checkin',
  'belt',
  'departed',
  'landed',
] as const;

export function parseFlights(value: unknown): WatchedFlight[] {
  const fields = record(value, 'flights');
  return list(fields, 'flights', (item) => {
    const flight = record(item, 'flight');
    const said = (key: string) => maybe(flight, key, () => text(flight, key));
    return {
      booking: text(flight, 'booking'),
      leg: number(flight, 'leg'),
      number: text(flight, 'number'),
      origin: text(flight, 'origin'),
      destination: text(flight, 'destination'),
      originZone: said('originZone'),
      destinationZone: said('destinationZone'),
      departs: text(flight, 'departs'),
      arrives: said('arrives'),
      actualDeparts: said('actualDeparts'),
      actualArrives: said('actualArrives'),
      delay: maybe(flight, 'delay', () => number(flight, 'delay')),
      gate: said('gate'),
      terminal: said('terminal'),
      checkIn: said('checkIn'),
      belt: said('belt'),
      status: maybe(flight, 'status', () => oneOf(flight, 'status', flightStatuses)),
      vehicle: said('vehicle'),
      checkedAt: said('checkedAt'),
      nextCheck: text(flight, 'nextCheck'),
      events: list(flight, 'events', (one) => {
        const event = record(one, 'event');
        return {
          at: text(event, 'at'),
          kind: oneOf(event, 'kind', flightEvents),
          detail: record(event.detail, 'detail'),
          service: text(event, 'service'),
        };
      }),
    };
  });
}

/** A trip's flights as watched, the soonest first. */
export function getFlights(trip: string): Promise<WatchedFlight[]> {
  return call(`/_/api/trips/${encodeURIComponent(trip)}/flights`, {}, parseFlights);
}

/** The kinds of event an account is told of. */
export const noticeEvents = [
  'delay',
  'gate',
  'checkin',
  'tomorrow',
  'border',
  'free',
  'pay',
  'companion',
  'before',
  'invite',
  'debt',
  'weekly',
] as const;
export type NoticeEvent = (typeof noticeEvents)[number];

/** How an account is told of one kind of event. */
export interface NoticePref {
  event: NoticeEvent;
  push: boolean;
  email: boolean;
  calendar: boolean;
  telegram: boolean;
}

/** An account's notices: its choices, the key a browser subscribes with, its devices told,
 * and whether the server sends mail. */
export interface Notices {
  prefs: NoticePref[];
  key: string | null;
  devices: number;
  mail: boolean;
  /** The address mail goes to, the zone at home, and whether a Telegram bot runs and is tied. */
  email: string;
  zone: string | null;
  telegram: { on: boolean; tied: boolean };
}

export function parseNotices(value: unknown): Notices {
  const fields = record(value, 'notices');
  return {
    prefs: list(fields, 'prefs', (item) => {
      const pref = record(item, 'pref');
      return {
        event: oneOf(pref, 'event', noticeEvents),
        push: flag(pref, 'push'),
        email: flag(pref, 'email'),
        calendar: flag(pref, 'calendar'),
        telegram: flag(pref, 'telegram'),
      };
    }),
    key: maybe(fields, 'key', () => text(fields, 'key')),
    devices: number(fields, 'devices'),
    mail: flag(fields, 'mail'),
    email: text(fields, 'email'),
    zone: maybe(fields, 'zone', () => text(fields, 'zone')),
    telegram: {
      on: flag(record(fields.telegram, 'telegram'), 'on'),
      tied: flag(record(fields.telegram, 'telegram'), 'tied'),
    },
  };
}

/** The account's notices. */
export function getNotices(): Promise<Notices> {
  return call('/_/api/me/notices', {}, parseNotices);
}

/** Keeps the account's choices. */
export function setNotices(prefs: NoticePref[]): Promise<null> {
  return call('/_/api/me/notices', json('PUT', { prefs }), () => null);
}

/** Tells the account on this device from now on: the browser's subscription as it gives it. */
export function subscribePush(subscription: {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}): Promise<null> {
  return call('/_/api/me/push', json('POST', subscription), () => null);
}

/** Stops telling the account on a device. */
export function forgetPush(endpoint: string): Promise<null> {
  return call('/_/api/me/push/forget', json('POST', { endpoint }), () => null);
}

/** A test notice to each of the account's devices: how many took it. */
export function testPush(): Promise<number> {
  return call('/_/api/me/push/test', json('POST', {}), (value) =>
    number(record(value, 'test'), 'sent'),
  );
}

// ---- one's own places (the Places mockup) ----

/** A tag of one's places: its colour `#rrggbb` and emoji, and how many places carry it. */
export interface PlaceTag {
  id: string;
  name: string;
  emoji: string;
  colour: string;
  places: number;
}

/** A trip's stop a place is on, and the day it is a thing to do there. */
export interface PlaceOnTrip {
  trip: string;
  tripName: string;
  stop: string;
  city: string;
  day: string | null;
}

/** One of one's places. */
export interface OwnPlace {
  id: string;
  name: string;
  address: string | null;
  kind: string | null;
  lat: number;
  lon: number;
  source: 'google' | 'naver' | 'file' | 'search' | 'map';
  sourceUrl: string | null;
  favourite: boolean;
  visitedOn: string | null;
  note: string | null;
  createdAt: string;
  city: string | null;
  country: string | null;
  hours: string | null;
  asked: boolean;
  tags: string[];
  /** Each photo's address. */
  photos: string[];
  trips: PlaceOnTrip[];
  /** Another place kept near with a name alike: maybe the same. */
  similar: {
    id: string;
    name: string;
    metres: number;
    createdAt: string;
    source: OwnPlace['source'];
  } | null;
}

export interface PlaceLibrary {
  places: OwnPlace[];
  tags: PlaceTag[];
}

const placeSources = ['google', 'naver', 'file', 'search', 'map'] as const;

function ownPlace(value: unknown): OwnPlace {
  const fields = record(value, 'place');
  const texts = (key: string) => list(fields, key, (one) => text({ one }, 'one'));
  return {
    id: text(fields, 'id'),
    name: text(fields, 'name'),
    address: maybe(fields, 'address', () => text(fields, 'address')),
    kind: maybe(fields, 'kind', () => text(fields, 'kind')),
    lat: number(fields, 'lat'),
    lon: number(fields, 'lon'),
    source: oneOf(fields, 'source', placeSources),
    sourceUrl: maybe(fields, 'sourceUrl', () => text(fields, 'sourceUrl')),
    favourite: flag(fields, 'favourite'),
    visitedOn: maybe(fields, 'visitedOn', () => text(fields, 'visitedOn')),
    note: maybe(fields, 'note', () => text(fields, 'note')),
    createdAt: text(fields, 'createdAt'),
    city: maybe(fields, 'city', () => text(fields, 'city')),
    country: maybe(fields, 'country', () => text(fields, 'country')),
    hours: maybe(fields, 'hours', () => text(fields, 'hours')),
    asked: flag(fields, 'asked'),
    tags: texts('tags'),
    photos: texts('photos'),
    trips: list(fields, 'trips', (one) => {
      const on = record(one, 'trip');
      return {
        trip: text(on, 'trip'),
        tripName: text(on, 'tripName'),
        stop: text(on, 'stop'),
        city: text(on, 'city'),
        day: maybe(on, 'day', () => text(on, 'day')),
      };
    }),
    similar: maybe(fields, 'similar', (one) => {
      const similar = record(one, 'similar');
      return {
        id: text(similar, 'id'),
        name: text(similar, 'name'),
        metres: number(similar, 'metres'),
        createdAt: text(similar, 'createdAt'),
        source: oneOf(similar, 'source', placeSources),
      };
    }),
  };
}

export function parsePlaceLibrary(value: unknown): PlaceLibrary {
  const fields = record(value, 'library');
  return {
    places: list(fields, 'places', ownPlace),
    tags: list(fields, 'tags', (one) => {
      const tag = record(one, 'tag');
      return {
        id: text(tag, 'id'),
        name: text(tag, 'name'),
        emoji: text(tag, 'emoji'),
        colour: text(tag, 'colour'),
        places: number(tag, 'places'),
      };
    }),
  };
}

/** A stop of one's trips near a place, to put it on. */
export interface PlaceStop {
  trip: string;
  tripName: string;
  stop: string;
  city: string;
  start: string;
  end: string;
  km: number;
}

/** What OpenStreetMap says of a place, and the stops near it. */
export interface PlaceMore {
  city: string | null;
  country: string | null;
  hours: string | null;
  stops: PlaceStop[];
}

export function parsePlaceMore(value: unknown): PlaceMore {
  const fields = record(value, 'more');
  return {
    city: maybe(fields, 'city', () => text(fields, 'city')),
    country: maybe(fields, 'country', () => text(fields, 'country')),
    hours: maybe(fields, 'hours', () => text(fields, 'hours')),
    stops: list(fields, 'stops', (one) => {
      const stop = record(one, 'stop');
      return {
        trip: text(stop, 'trip'),
        tripName: text(stop, 'tripName'),
        stop: text(stop, 'stop'),
        city: text(stop, 'city'),
        start: text(stop, 'start'),
        end: text(stop, 'end'),
        km: number(stop, 'km'),
      };
    }),
  };
}

const placePath = (place: string) => `/_/api/me/places/${encodeURIComponent(place)}`;
const idOf = (body: unknown) => text(record(body, 'made'), 'id');

/** One's places and tags. */
export function getPlaces(): Promise<PlaceLibrary> {
  return call('/_/api/me/places', {}, parsePlaceLibrary);
}

/** A place kept from a search or the map → its id; the one kept already when it is the same. */
export function addPlace(place: {
  name: string;
  address?: string | null;
  kind?: string | null;
  lat: number;
  lon: number;
  source: 'search' | 'map';
  sourceUrl?: string | null;
}): Promise<string> {
  return call('/_/api/me/places', json('POST', place), idOf);
}

/** What a change to a place says; each left out stays, `null` takes it off. */
export interface PlaceChange {
  name?: string;
  note?: string | null;
  favourite?: boolean;
  visitedOn?: string | null;
  tags?: string[];
}

export function changePlace(place: string, change: PlaceChange): Promise<null> {
  return call(placePath(place), json('PATCH', change), () => null);
}

export function removePlace(place: string): Promise<null> {
  return call(placePath(place), { method: 'DELETE' }, () => null);
}

/** `other` merged into `place`. */
export function mergePlaces(place: string, other: string): Promise<null> {
  return call(`${placePath(place)}/merge`, json('POST', { other }), () => null);
}

/** Two places said not to be the same. */
export function keepPlacesApart(place: string, other: string): Promise<null> {
  return call(`${placePath(place)}/apart`, json('POST', { other }), () => null);
}

/** A place's town and hours, asked once, and the stops near it. */
export function getPlaceMore(place: string): Promise<PlaceMore> {
  return call(`${placePath(place)}/more`, {}, parsePlaceMore);
}

/** A place put on a trip's stop, to wait for a day there. */
export function putPlaceOnTrip(place: string, trip: string, stop: string): Promise<null> {
  return call(`${placePath(place)}/trip`, json('POST', { trip, stop }), () => null);
}

/** A photo of a place kept → its address. */
export function addPlacePhoto(place: string, file: Blob): Promise<string> {
  return call(
    `${placePath(place)}/photos`,
    { method: 'POST', body: file },
    (body) => text(record(body, 'photo'), 'photo'),
    file.type || 'image/jpeg',
  );
}

/** A photo let go of, by its address. */
export function removePlacePhoto(address: string): Promise<null> {
  return call(address, { method: 'DELETE' }, () => null);
}

export interface PlaceTagInput {
  name: string;
  emoji: string;
  colour: string;
}

export function addPlaceTag(tag: PlaceTagInput): Promise<string> {
  return call('/_/api/me/place-tags', json('POST', tag), idOf);
}

export function changePlaceTag(id: string, tag: PlaceTagInput): Promise<null> {
  return call(`/_/api/me/place-tags/${encodeURIComponent(id)}`, json('PUT', tag), () => null);
}

export function removePlaceTag(id: string): Promise<null> {
  return call(
    `/_/api/me/place-tags/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    () => null,
  );
}

// ---- a place added from the map (the MapAdd mockup) ----

/** A named thing near a point, with its emoji. */
export interface NearThing {
  name: string;
  emoji: string;
  lat: number;
  lon: number;
  metres: number;
}

/** What is at a point the map was held on. */
export interface Here {
  lat: number;
  lon: number;
  address: string | null;
  plusCode: string;
}

export function parseHere(value: unknown): Here {
  const fields = record(value, 'here');
  return {
    lat: number(fields, 'lat'),
    lon: number(fields, 'lon'),
    address: maybe(fields, 'address', () => text(fields, 'address')),
    plusCode: text(fields, 'plusCode'),
  };
}

/** The named things near a point, and how OpenStreetMap answered. */
export interface Nearby {
  near: NearThing[];
  osm: 'ok' | 'off' | 'failed';
}

export function parseNearby(value: unknown): Nearby {
  const fields = record(value, 'nearby');
  return {
    near: list(fields, 'near', (one) => {
      const near = record(one, 'near');
      return {
        name: text(near, 'name'),
        emoji: text(near, 'emoji'),
        lat: number(near, 'lat'),
        lon: number(near, 'lon'),
        metres: number(near, 'metres'),
      };
    }),
    osm: oneOf(fields, 'osm', ['ok', 'off', 'failed'] as const),
  };
}

/** What is at a point. */
export function getHere(at: Point): Promise<Here> {
  return call(`/_/api/places/here?at=${String(at.lat)},${String(at.lon)}`, {}, parseHere);
}

/** The named things within 150 m of a point. */
export function getNearby(at: Point): Promise<Nearby> {
  return call(`/_/api/places/near?at=${String(at.lat)},${String(at.lon)}`, {}, parseNearby);
}

/** How a pasted place was read. */
export const readingKinds = [
  'decimal',
  'seconds',
  'minutes',
  'plusCode',
  'shortCode',
  'link',
] as const;

/** A place read from a link, coordinates or a Plus Code. */
export interface Reading {
  kind: (typeof readingKinds)[number];
  lat: number;
  lon: number;
  name: string | null;
  address: string | null;
  plusCode: string;
}

export function parseReading(value: unknown): Reading {
  const fields = record(value, 'reading');
  return {
    kind: oneOf(fields, 'kind', readingKinds),
    lat: number(fields, 'lat'),
    lon: number(fields, 'lon'),
    name: maybe(fields, 'name', () => text(fields, 'name')),
    address: maybe(fields, 'address', () => text(fields, 'address')),
    plusCode: text(fields, 'plusCode'),
  };
}

/** A link, coordinates or a Plus Code read, a short one near `near`; `null` when it is a name. */
export function readPasted(
  textPasted: string,
  near: Point,
  signal: AbortSignal,
): Promise<Reading | null> {
  const q = encodeURIComponent(textPasted);
  return call(
    `/_/api/places/read?text=${q}&near=${String(near.lat)},${String(near.lon)}`,
    { signal },
    parseReading,
  ).catch((error: unknown) => {
    if (error instanceof NotFound) return null;
    throw error;
  });
}

/** A place kept among one's own and put on a stop to wait for a day → its id. */
export function addStopPlace(
  trip: string,
  stop: string,
  place: {
    name: string;
    address: string | null;
    lat: number;
    lon: number;
    source: 'search' | 'map' | 'google';
  },
): Promise<string> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/stops/${encodeURIComponent(stop)}/places`,
    json('POST', place),
    idOf,
  );
}

// ---- a way drawn on the map, its climb and photos (the WayDraw mockup) ----

/** The way from one point to another on foot, by bike or by car; `null` as a road's. */
export function getWay(
  from: Point,
  to: Point,
  by: 'foot' | 'bike' | 'car',
): Promise<Road | null> {
  const at = (p: Point) => `${String(p.lat)},${String(p.lon)}`;
  return call(`/_/api/places/road?from=${at(from)}&to=${at(to)}&by=${by}`, {}, parseRoad);
}

/** The ground's height at each point, in metres; `null` where it could not be read. */
export function getHeights(
  path: readonly [number, number][],
): Promise<{ heights: (number | null)[]; ground: 'ok' | 'off' }> {
  return call('/_/api/places/heights', json('POST', { path }), (value) => {
    const fields = record(value, 'heights');
    return {
      heights: list(fields, 'heights', (height) =>
        height === null ? null : number({ height }, 'height'),
      ),
      ground: oneOf(fields, 'ground', ['ok', 'off'] as const),
    };
  });
}

/** A recorded track on the roads and paths it went along; `path` null where none matched. */
export function snapPath(
  path: readonly [number, number][],
  by: 'foot' | 'bike' | 'car',
): Promise<{ path: [number, number][] | null; osm: 'ok' | 'off' | 'failed' }> {
  return call('/_/api/places/snap', json('POST', { path, by }), (value) => {
    const fields = record(value, 'snap');
    return {
      path: maybe(fields, 'path', () => list(fields, 'path', pathPoint)),
      osm: oneOf(fields, 'osm', ['ok', 'off', 'failed'] as const),
    };
  });
}

/** A way drawn on the map kept as a stop's way on, with its anchors → the plan. */
export function saveDrawnWay(
  trip: string,
  stop: string,
  way: {
    name: string;
    mode: DrawMode;
    points: Anchor[];
    path: [number, number][];
    heights: (number | null)[];
  },
): Promise<Plan> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/stops/${encodeURIComponent(stop)}/track/drawn`,
    json('PUT', way),
    parsePlan,
  );
}

/** A photo of a way: where its camera placed it, when it was taken, its address. */
export interface WayPhoto {
  id: string;
  lat: number | null;
  lon: number | null;
  takenAt: string | null;
  image: string;
}

function wayPhoto(value: unknown): WayPhoto {
  const fields = record(value, 'photo');
  return {
    id: text(fields, 'id'),
    lat: maybe(fields, 'lat', () => number(fields, 'lat')),
    lon: maybe(fields, 'lon', () => number(fields, 'lon')),
    takenAt: maybe(fields, 'takenAt', () => text(fields, 'takenAt')),
    image: text(fields, 'image'),
  };
}

const wayPhotos = (trip: string, stop: string) =>
  `/_/api/trips/${encodeURIComponent(trip)}/stops/${encodeURIComponent(stop)}/photos`;

/** The photos of a stop's way on, in the order they were taken. */
export function getWayPhotos(trip: string, stop: string): Promise<WayPhoto[]> {
  return call(wayPhotos(trip, stop), {}, (value) =>
    list(record(value, 'photos'), 'photos', wayPhoto),
  );
}

/** A photo kept with a stop's way on, placed where it was taken. */
export function addWayPhoto(trip: string, stop: string, file: Blob): Promise<WayPhoto> {
  return call(
    wayPhotos(trip, stop),
    { method: 'POST', body: file },
    wayPhoto,
    file.type || 'image/jpeg',
  );
}

/** A way's photo let go of, by its address. */
export function removeWayPhoto(address: string): Promise<null> {
  return call(address, { method: 'DELETE' }, () => null);
}

/** Days from `start` to `end`, the first counted and the last not. */
export interface ActualSpan {
  start: string;
  end: string;
  nights: number;
}

/** A file added after a trip: a Google Timeline or a recorded track. */
export interface ActualSource {
  id: string;
  name: string;
  kind: string;
  firstAt: string;
  lastAt: string;
  count: number;
}

/** A stop beside the plan: one planned, or one the files found that the plan did not have. */
export interface ActualRow {
  planned: boolean;
  city: string;
  country: string;
  plan: ActualSpan | null;
  actual: ActualSpan | null;
  /** Whether a file said where one was, rather than the plan standing. */
  seen: boolean;
}

/** How a way went, as its points' speeds say. */
export type ActualGoing = 'flight' | 'train' | 'road' | 'foot';

/** A way gone from one stop to the next. */
export interface ActualWay {
  from: string;
  to: string;
  plan: Mode | null;
  /** Where the plan went on to, when not `to`. */
  plannedTo: string | null;
  going: ActualGoing | null;
  km: number;
  flights: number;
  estimate: { min: number; max: number } | null;
  paid: number | null;
}

/** Schengen's 90 days in any 180 for one member whose passport counts them. */
export interface SchengenWindow {
  name: string;
  passport: string;
  until: string;
  used: number;
  planned: number;
  frees: { day: string; days: number } | null;
  days: string[];
}

/** A figure as planned, and as it was. */
export interface ActualPair {
  plan: number;
  actual: number;
}

/** The plan beside what the files added say. */
export interface ActualScreen {
  currency: string;
  written: string | null;
  sources: ActualSource[];
  /** Each as planned, then as it was. */
  summary: { nights: ActualPair; stops: ActualPair; km: ActualPair; flights: ActualPair };
  rows: ActualRow[];
  ways: ActualWay[];
  passport: string | null;
  countries: {
    country: string;
    days: number;
    free: boolean;
    rule: { kind: string; days: number } | null;
  }[];
  schengen: SchengenWindow[];
  year: { year: number; home: string | null; countries: { country: string; days: number }[] };
}

function actualPair(fields: Fields, key: string): ActualPair {
  const said = numbers(fields, key);
  return { plan: nth(said, 0, key), actual: nth(said, 1, key) };
}

function actualSpan(fields: Fields, key: string): ActualSpan | null {
  return maybe(fields, key, (value) => {
    const span = record(value, key);
    return {
      start: text(span, 'start'),
      end: text(span, 'end'),
      nights: number(span, 'nights'),
    };
  });
}

function actualSource(value: unknown): ActualSource {
  const fields = record(value, 'source');
  return {
    id: text(fields, 'id'),
    name: text(fields, 'name'),
    kind: text(fields, 'kind'),
    firstAt: text(fields, 'firstAt'),
    lastAt: text(fields, 'lastAt'),
    count: number(fields, 'count'),
  };
}

function actualScreen(value: unknown): ActualScreen {
  const fields = record(value, 'actual');
  const summary = record(fields.summary, 'summary');
  const year = record(fields.year, 'year');
  return {
    currency: text(fields, 'currency'),
    written: maybe(fields, 'written', () => text(fields, 'written')),
    sources: list(fields, 'sources', actualSource),
    summary: {
      nights: actualPair(summary, 'nights'),
      stops: actualPair(summary, 'stops'),
      km: actualPair(summary, 'km'),
      flights: actualPair(summary, 'flights'),
    },
    rows: list(fields, 'rows', (item) => {
      const row = record(item, 'row');
      return {
        planned: flag(row, 'planned'),
        city: text(row, 'city'),
        country: text(row, 'country'),
        plan: actualSpan(row, 'plan'),
        actual: actualSpan(row, 'actual'),
        seen: flag(row, 'seen'),
      };
    }),
    ways: list(fields, 'ways', (item) => {
      const way = record(item, 'way');
      return {
        from: text(way, 'from'),
        to: text(way, 'to'),
        plan: maybe(way, 'plan', () => oneOf(way, 'plan', modes)),
        plannedTo: maybe(way, 'plannedTo', () => text(way, 'plannedTo')),
        going: maybe(way, 'going', () =>
          oneOf(way, 'going', ['flight', 'train', 'road', 'foot'] as const),
        ),
        km: number(way, 'km'),
        flights: number(way, 'flights'),
        estimate: maybe(way, 'estimate', (said) => {
          const estimate = record(said, 'estimate');
          return { min: number(estimate, 'min'), max: number(estimate, 'max') };
        }),
        paid: maybe(way, 'paid', () => number(way, 'paid')),
      };
    }),
    passport: maybe(fields, 'passport', () => text(fields, 'passport')),
    countries: list(fields, 'countries', (item) => {
      const country = record(item, 'country');
      return {
        country: text(country, 'country'),
        days: number(country, 'days'),
        free: flag(country, 'free'),
        rule: maybe(country, 'rule', (said) => {
          const rule = record(said, 'rule');
          return { kind: text(rule, 'kind'), days: number(rule, 'days') };
        }),
      };
    }),
    schengen: list(fields, 'schengen', (item) => {
      const window = record(item, 'window');
      return {
        name: text(window, 'name'),
        passport: text(window, 'passport'),
        until: text(window, 'until'),
        used: number(window, 'used'),
        planned: number(window, 'planned'),
        frees: maybe(window, 'frees', (said) => {
          const frees = record(said, 'frees');
          return { day: text(frees, 'day'), days: number(frees, 'days') };
        }),
        days: list(window, 'days', (day) => text({ day }, 'day')),
      };
    }),
    year: {
      year: number(year, 'year'),
      home: maybe(year, 'home', () => text(year, 'home')),
      countries: list(year, 'countries', (item) => {
        const country = record(item, 'country');
        return { country: text(country, 'country'), days: number(country, 'days') };
      }),
    },
  };
}

const actualOf = (trip: string) => `/_/api/trips/${encodeURIComponent(trip)}/actual`;

/** The plan beside what the files added say, for the phone's day. */
export function getActual(trip: string, day: string): Promise<ActualScreen> {
  return call(`${actualOf(trip)}?day=${encodeURIComponent(day)}`, {}, actualScreen);
}

/** A file added: kept, or found already there under the name it was kept as. */
export function addActualSource(
  trip: string,
  file: File,
): Promise<{ added: ActualSource } | { again: string }> {
  return call(
    `${actualOf(trip)}/sources?name=${encodeURIComponent(file.name)}`,
    { method: 'POST', body: file },
    (value) => {
      const fields = record(value, 'added');
      return 'again' in fields
        ? { again: text(fields, 'again') }
        : { added: actualSource(fields.added) };
    },
    'application/octet-stream',
  );
}

/** A file let go of. */
export function removeActualSource(trip: string, id: string): Promise<null> {
  return call(
    `${actualOf(trip)}/sources/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    () => null,
  );
}

/** What happened written into the trip, the plan kept to compare with → the plan. */
export function writeActual(trip: string): Promise<Plan> {
  return call(`${actualOf(trip)}/write`, { method: 'POST' }, parsePlan);
}

/** What was written taken back → the plan as it was. */
export function unwriteActual(trip: string): Promise<Plan> {
  return call(`${actualOf(trip)}/unwrite`, { method: 'POST' }, parsePlan);
}

/** The longest or highest of something: its name and how much. */
export interface StatMost {
  name: string;
  value: number;
}

/** An achievement: done, or how far along. */
export interface Badge {
  key: string;
  have: number;
  need: number;
  done: boolean;
  by: string | null;
}

/** Statistics across one's finished trips, of a year or of all. */
export interface Stats {
  /** Where one has been: a grid's cells, each `[lon, lat, count]`, and a cell's side in degrees. */
  explored: { cell: number; cells: [number, number, number][] };
  years: number[];
  year: number | null;
  /** The trip counted alone, when one is. */
  trip: string | null;
  trips: StatTrip[];
  /** Every trip of the account, the latest first, to count one of alone. */
  choices: StatTrip[];
  km: number;
  modes: { mode: Mode; km: number }[];
  nights: number;
  tripCount: number;
  countries: string[];
  newCountries: number;
  towns: number;
  longestStay: StatMost | null;
  kg: number;
  kgFlown: number;
  longestFlight: StatMost | null;
  longestTrain: { name: string; minutes: number | null; km: number } | null;
  highest: StatMost | null;
  stays: { country: string; start: string; end: string }[];
  badges: Badge[];
}

export interface StatTrip {
  id: string;
  name: string;
  start: string;
  end: string;
}

function statTrip(item: unknown): StatTrip {
  const trip = record(item, 'trip');
  return {
    id: text(trip, 'id'),
    name: text(trip, 'name'),
    start: text(trip, 'start'),
    end: text(trip, 'end'),
  };
}

function statMost(fields: Fields, key: string, unit: string): StatMost | null {
  return maybe(fields, key, (value) => {
    const most = record(value, key);
    return { name: text(most, 'name'), value: number(most, unit) };
  });
}

function parseStats(value: unknown): Stats {
  const fields = record(value, 'stats');
  const explored = record(fields.explored, 'explored');
  return {
    explored: {
      cell: number(explored, 'cell'),
      cells: list(explored, 'cells', (item): [number, number, number] => {
        const three = numbers({ cell: item }, 'cell');
        return [nth(three, 0, 'cell'), nth(three, 1, 'cell'), nth(three, 2, 'cell')];
      }),
    },
    years: numbers(fields, 'years'),
    year: maybe(fields, 'year', () => number(fields, 'year')),
    trip: maybe(fields, 'trip', () => text(fields, 'trip')),
    trips: list(fields, 'trips', statTrip),
    choices: list(fields, 'choices', statTrip),
    km: number(fields, 'km'),
    modes: list(fields, 'modes', (item) => {
      const mode = record(item, 'mode');
      return { mode: oneOf(mode, 'mode', modes), km: number(mode, 'km') };
    }),
    nights: number(fields, 'nights'),
    tripCount: number(fields, 'tripCount'),
    countries: list(fields, 'countries', (code) => text({ code }, 'code')),
    newCountries: number(fields, 'newCountries'),
    towns: number(fields, 'towns'),
    longestStay: statMost(fields, 'longestStay', 'nights'),
    kg: number(fields, 'kg'),
    kgFlown: number(fields, 'kgFlown'),
    longestFlight: statMost(fields, 'longestFlight', 'km'),
    longestTrain: maybe(fields, 'longestTrain', (said) => {
      const train = record(said, 'longestTrain');
      return {
        name: text(train, 'name'),
        minutes: maybe(train, 'minutes', () => number(train, 'minutes')),
        km: number(train, 'km'),
      };
    }),
    highest: statMost(fields, 'highest', 'metres'),
    stays: list(fields, 'stays', (item) => {
      const stay = record(item, 'stay');
      return {
        country: text(stay, 'country'),
        start: text(stay, 'start'),
        end: text(stay, 'end'),
      };
    }),
    badges: list(fields, 'badges', (item) => {
      const badge = record(item, 'badge');
      return {
        key: text(badge, 'key'),
        have: number(badge, 'have'),
        need: number(badge, 'need'),
        done: flag(badge, 'done'),
        by: maybe(badge, 'by', () => text(badge, 'by')),
      };
    }),
  };
}

/**
 * The statistics of one's trips over by `day`: of `year`, of all (`all`), or of the latest; or
 * of one `trip` alone, planned or over.
 */
export function getStats(day: string, year?: number | 'all', trip?: string): Promise<Stats> {
  const of =
    trip === undefined
      ? year === undefined
        ? ''
        : `&year=${String(year)}`
      : `&trip=${encodeURIComponent(trip)}`;
  return call(`/_/api/me/stats?day=${encodeURIComponent(day)}${of}`, {}, parseStats);
}

/** Where a trip's poster is had as a PDF, as it is chosen to look. */
export function posterAddress(
  trip: string,
  look: { size: 'a4' | 'a3' | '50x70'; dark: boolean; names: boolean; arcs: boolean },
  language: string,
): string {
  const query = new URLSearchParams({
    size: look.size,
    theme: look.dark ? 'dark' : 'light',
    names: look.names ? '1' : '0',
    arcs: look.arcs ? '1' : '0',
    lang: language,
  });
  return `/_/api/trips/${encodeURIComponent(trip)}/export/poster?${query.toString()}`;
}

// ---- before the trip (the Lists mockup) ----

/** A thing to do before the trip: the list's own, or one the trip sets itself. */
export interface Task {
  /** `own:<id>`, `border:TH:1`, `pay:<booking>`, `checkin:<booking>:0`, `maps`. */
  key: string;
  kind: 'own' | 'border' | 'pay' | 'checkin' | 'maps';
  title: string;
  country: string | null;
  city: string | null;
  amount: Money | null;
  /** A room's free cancellation ends, on its stop's clock. */
  until: string | null;
  /** How many hours before a flight check-in opens; how long a permit takes. */
  hours: number | null;
  due: string | null;
  remindAt: string | null;
  timed: boolean;
  done: boolean;
  doneAt: string | null;
}

const optional = (fields: Fields, key: string) => maybe(fields, key, () => text(fields, key));

function parseTask(value: unknown): Task {
  const fields = record(value, 'task');
  return {
    key: text(fields, 'key'),
    kind: oneOf(fields, 'kind', ['own', 'border', 'pay', 'checkin', 'maps'] as const),
    title: text(fields, 'title'),
    country: optional(fields, 'country'),
    city: optional(fields, 'city'),
    amount: maybe(fields, 'amount', money),
    until: optional(fields, 'until'),
    hours: maybe(fields, 'hours', () => number(fields, 'hours')),
    due: optional(fields, 'due'),
    remindAt: optional(fields, 'remindAt'),
    timed: flag(fields, 'timed'),
    done: flag(fields, 'done'),
    doneAt: optional(fields, 'doneAt'),
  };
}

/** What to do before the trip. */
export function getLists(trip: string): Promise<{ tasks: Task[] }> {
  return call(`${docs(trip)}/lists`, {}, (value) => ({
    tasks: list(record(value, 'lists'), 'tasks', parseTask),
  }));
}

/** Ticks a task done, or takes the tick back. */
export function tickTask(trip: string, key: string, done: boolean): Promise<null> {
  return call(
    `${docs(trip)}/tasks/${encodeURIComponent(key)}`,
    json('PUT', { done }),
    () => null,
  );
}

/** Sets when to be reminded of an item of the list, or no reminder with `null`. */
export function setReminder(
  trip: string,
  item: string,
  remindAt: string | null,
  timed: boolean,
): Promise<null> {
  return call(
    `${docs(trip)}/before/${encodeURIComponent(item)}/reminder`,
    json('PUT', { remindAt, timed }),
    () => null,
  );
}

/** A change to several things done at once. */
export type BulkChange =
  | { change: 'day' | 'copy'; day: string }
  | { change: 'booked'; booked: boolean }
  | { change: 'tag'; tag: string }
  | { change: 'delete' };

/** Changes several things done at once → the plan. */
export function bulkActivities(trip: string, ids: string[], change: BulkChange): Promise<Plan> {
  return call(`${docs(trip)}/activities/bulk`, json('POST', { ids, ...change }), parsePlan);
}

/** Where all of a trip's files are had in one ZIP. */
export function filesZipAddress(trip: string, name: string): string {
  return `${docs(trip)}/documents.zip?name=${encodeURIComponent(name)}`;
}

/** A test mail to the account's own address: whether it was sent. */
export function testMail(): Promise<boolean> {
  return call('/_/api/me/notices/mail', json('POST', {}), (value) =>
    flag(record(value, 'test'), 'sent'),
  );
}

/** A link that ties a Telegram chat to the account. */
export function telegramLink(): Promise<string> {
  return call('/_/api/me/telegram', json('POST', {}), (value) =>
    text(record(value, 'telegram'), 'link'),
  );
}

/** Unties the account's Telegram chat. */
export function untieTelegram(): Promise<null> {
  return call('/_/api/me/telegram', { method: 'DELETE' }, () => null);
}

/** Keeps the time zone the account is at home in. */
export function setZone(zone: string): Promise<null> {
  return call('/_/api/me/zone', json('PUT', { zone }), () => null);
}

// ---- the journal (the Journal mockup) ----

/** A photo of a day: when, where and how that is known, and the place's name. */
export interface JournalPhoto {
  id: string;
  /** On the local clock, `09:42`, when its camera said. */
  at: string | null;
  lat: number | null;
  lon: number | null;
  /** Its own camera's place, the plan's at its hour, or none. */
  source: 'photo' | 'plan' | 'none';
  place: string | null;
  image: string;
}

/** A place said by "I am here". */
export interface JournalHere {
  id: string;
  at: string;
  lat: number;
  lon: number;
  place: string;
}

/** A day as written. */
export interface JournalEntry {
  title: string;
  body: string;
  writtenAt: string;
  writtenBy: string | null;
}

/** A day of the journal: its stop, its words, photos and places said, what the plan had, how it
 * was got about, and what was spent (none in a shared view). */
export interface JournalDay {
  day: string;
  stop: string | null;
  city: string | null;
  entry: JournalEntry | null;
  photos: JournalPhoto[];
  here: JournalHere[];
  plan: string[];
  ways: { by: string; minutes: number }[];
  spent: Money | null;
}

/** A trip's journal: every day of it, and whether it has begun. */
export interface Journal {
  started: boolean;
  days: JournalDay[];
}

function journalPhoto(value: unknown): JournalPhoto {
  const fields = record(value, 'photo');
  return {
    id: text(fields, 'id'),
    at: maybe(fields, 'at', () => text(fields, 'at')),
    lat: maybe(fields, 'lat', () => number(fields, 'lat')),
    lon: maybe(fields, 'lon', () => number(fields, 'lon')),
    source: oneOf(fields, 'source', ['photo', 'plan', 'none'] as const),
    place: maybe(fields, 'place', () => text(fields, 'place')),
    image: text(fields, 'image'),
  };
}

function journalHere(value: unknown): JournalHere {
  const fields = record(value, 'here');
  return {
    id: text(fields, 'id'),
    at: text(fields, 'at'),
    lat: number(fields, 'lat'),
    lon: number(fields, 'lon'),
    place: text(fields, 'place'),
  };
}

function journalEntry(value: unknown): JournalEntry {
  const fields = record(value, 'entry');
  return {
    title: text(fields, 'title'),
    body: text(fields, 'body'),
    writtenAt: text(fields, 'writtenAt'),
    writtenBy: maybe(fields, 'writtenBy', () => text(fields, 'writtenBy')),
  };
}

/** A day of the journal as the server sends it. */
export function journalDay(value: unknown): JournalDay {
  const fields = record(value, 'day');
  return {
    day: text(fields, 'day'),
    stop: maybe(fields, 'stop', () => text(fields, 'stop')),
    city: maybe(fields, 'city', () => text(fields, 'city')),
    entry: maybe(fields, 'entry', journalEntry),
    photos: list(fields, 'photos', journalPhoto),
    here: list(fields, 'here', journalHere),
    plan: list(fields, 'plan', (name) => text({ name }, 'name')),
    ways: list(fields, 'ways', (item) => {
      const way = record(item, 'way');
      return { by: text(way, 'by'), minutes: number(way, 'minutes') };
    }),
    spent: maybe(fields, 'spent', money),
  };
}

const journalPath = (trip: string) => `/_/api/trips/${encodeURIComponent(trip)}/journal`;

/** A trip's journal. */
export function getJournal(trip: string): Promise<Journal> {
  return call(journalPath(trip), {}, (value) => {
    const fields = record(value, 'journal');
    return { started: flag(fields, 'started'), days: list(fields, 'days', journalDay) };
  });
}

/** A day written; both empty, unwritten again → the day as written, or none. */
export function writeJournal(
  trip: string,
  day: string,
  title: string,
  body: string,
): Promise<JournalEntry | null> {
  return call(`${journalPath(trip)}/${day}`, json('PUT', { title, body }), (value) =>
    value === null ? null : journalEntry(value),
  );
}

/** A photo of a day, placed where its camera said, else where the plan was at its hour. */
export function addJournalPhoto(trip: string, day: string, file: Blob): Promise<JournalPhoto> {
  return call(
    `${journalPath(trip)}/${day}/photos`,
    { method: 'POST', body: file },
    journalPhoto,
    file.type || 'image/jpeg',
  );
}

/** A photo of a day let go of. */
export function removeJournalPhoto(trip: string, photo: string): Promise<null> {
  return call(
    `${journalPath(trip)}/photos/${encodeURIComponent(photo)}`,
    { method: 'DELETE' },
    () => null,
  );
}

/** A place said once by "I am here", at a local hour. */
export function sayHere(
  trip: string,
  day: string,
  here: { at: string; lat: number; lon: number },
): Promise<JournalHere> {
  return call(`${journalPath(trip)}/${day}/here`, json('POST', here), journalHere);
}

/** A place said let go of. */
export function removeHere(trip: string, here: string): Promise<null> {
  return call(
    `${journalPath(trip)}/here/${encodeURIComponent(here)}`,
    { method: 'DELETE' },
    () => null,
  );
}

/** Where a journal's book is had as a PDF: on A5 or A4, with room to cut or not. */
export function journalBookAddress(
  trip: string,
  paper: 'a5' | 'a4',
  bleed: boolean,
  language: string,
): string {
  const query = new URLSearchParams({ paper, bleed: bleed ? '1' : '0', lang: language });
  return `/_/api/trips/${encodeURIComponent(trip)}/export/journal?${query.toString()}`;
}

// ---- sights along a drive (the RoadSights mockup) ----

/** What a sight is, for its filter. */
export const sightKinds = ['view', 'nature', 'town', 'food', 'fuel'] as const;
export type SightKind = (typeof sightKinds)[number];

/** A sight near a way's road: how important open data says it is, and the detour to it. */
export interface RoadSight {
  id: string;
  name: string;
  kind: SightKind;
  about: string | null;
  source: 'wikivoyage' | 'wikipedia' | 'osm';
  lat: number;
  lon: number;
  /** There and back from the road, by car; none when no way to it was found. */
  detourMinutes: number | null;
  offKm: number;
  onWay: boolean;
  /** 1 to 5. */
  score: number;
}

/** A way's sights: the way itself, and what is near its road. */
export interface RoadSights {
  way: {
    from: string;
    to: string;
    country: string;
    mode: Mode | null;
    road: string | null;
    km: number;
    minutes: number;
    day: string | null;
    path: [number, number][];
  } | null;
  status: 'ok' | 'failed';
  sights: RoadSight[];
}

/** The sights near a stop's way on by road. */
export function getSights(trip: string, stop: string, language: string): Promise<RoadSights> {
  return call(
    `/_/api/trips/${encodeURIComponent(trip)}/stops/${encodeURIComponent(stop)}/sights?lang=${language}`,
    {},
    (value) => {
      const fields = record(value, 'sights');
      return {
        way: maybe(fields, 'way', (item) => {
          const way = record(item, 'way');
          return {
            from: text(way, 'from'),
            to: text(way, 'to'),
            country: text(way, 'country'),
            mode: maybe(way, 'mode', () => oneOf(way, 'mode', modes)),
            road: maybe(way, 'road', () => text(way, 'road')),
            km: number(way, 'km'),
            minutes: number(way, 'minutes'),
            day: maybe(way, 'day', () => text(way, 'day')),
            path: list(way, 'path', (pair): [number, number] => {
              const at = (n: number) =>
                number({ value: Array.isArray(pair) ? pair[n] : null }, 'value');
              return [at(0), at(1)];
            }),
          };
        }),
        status: oneOf(fields, 'status', ['ok', 'failed'] as const),
        sights: list(fields, 'sights', (item) => {
          const sight = record(item, 'sight');
          return {
            id: text(sight, 'id'),
            name: text(sight, 'name'),
            kind: oneOf(sight, 'kind', sightKinds),
            about: maybe(sight, 'about', () => text(sight, 'about')),
            source: oneOf(sight, 'source', ['wikivoyage', 'wikipedia', 'osm'] as const),
            lat: number(sight, 'lat'),
            lon: number(sight, 'lon'),
            detourMinutes: maybe(sight, 'detourMinutes', () => number(sight, 'detourMinutes')),
            offKm: number(sight, 'offKm'),
            onWay: flag(sight, 'onWay'),
            score: number(sight, 'score'),
          };
        }),
      };
    },
  );
}

// ---- one's own AI assistants and API keys (the `Assistant` mockup) ----

/** What an assistant or a key may do: read trips, change the plan, see bookings, money, papers. */
export const assistantScopes = ['trips', 'plan', 'bookings', 'money', 'documents'] as const;
export type AssistantScope = (typeof assistantScopes)[number];

/** An assistant or a personal key with one's leave. */
export interface Granted {
  id: string;
  name: string;
  /** An assistant connected by OAuth, else a key made here. */
  assistant: boolean;
  createdAt: string;
  lastUsedAt: string | null;
  /** How often it has been used. */
  uses: number;
  scopes: AssistantScope[];
  /** The trips it may see; none for every trip. */
  trips: string[] | null;
}

/** Where an assistant is pointed, and what has one's leave. */
export interface Assistants {
  mcp: string;
  granted: Granted[];
}

function scopesOf(fields: Fields): AssistantScope[] {
  return list(fields, 'scopes', (item) => oneOf({ scope: item }, 'scope', assistantScopes));
}

export function getAssistants(): Promise<Assistants> {
  return call('/_/api/me/assistants', {}, (body) => {
    const fields = record(body, 'assistants');
    return {
      mcp: text(fields, 'mcp'),
      granted: list(fields, 'granted', (item) => {
        const one = record(item, 'granted');
        return {
          id: text(one, 'id'),
          name: text(one, 'name'),
          assistant: flag(one, 'assistant'),
          createdAt: text(one, 'createdAt'),
          lastUsedAt: optionalText(one, 'lastUsedAt'),
          uses: number(one, 'uses'),
          scopes: scopesOf(one),
          trips: maybe(one, 'trips', () => list(one, 'trips', (id) => text({ id }, 'id'))),
        };
      }),
    };
  });
}

/** An assistant's or a key's leave taken back. */
export function revokeAssistant(id: string): Promise<void> {
  return call(`/_/api/me/assistants/${encodeURIComponent(id)}`, { method: 'DELETE' }, () => {});
}

/** A personal API key made → its token, shown only now. */
export function makeKey(
  name: string,
  scopes: AssistantScope[],
  trips: string[] | null,
): Promise<string> {
  return call('/_/api/me/keys', json('POST', { name, scopes, trips }), (body) =>
    text(record(body, 'key'), 'token'),
  );
}

/** The assistant asking leave, by its id and where it is sent back to. */
export function getOAuthClient(clientId: string, redirectUri: string): Promise<string> {
  const query = new URLSearchParams({ client_id: clientId, redirect_uri: redirectUri });
  return call(`/_/api/oauth/client?${query.toString()}`, {}, (body) =>
    text(record(body, 'client'), 'name'),
  );
}

/** What an assistant asks leave with, from its link. */
export interface Consent {
  clientId: string;
  redirectUri: string;
  codeChallenge: string;
  codeChallengeMethod: string;
  state: string | null;
}

/** Leave given → where the browser goes on to. */
export function giveConsent(
  asked: Consent,
  scopes: AssistantScope[],
  trips: string[] | null,
): Promise<string> {
  return call('/_/api/oauth/consent', json('POST', { ...asked, scopes, trips }), (body) =>
    text(record(body, 'consent'), 'redirect'),
  );
}

// ---- the instance's accounts, kept by its admins ----

/** An account as an admin sees it. */
export interface Person {
  id: string;
  name: string;
  email: string;
  createdAt: string;
  lastSeenAt: string | null;
  trips: number;
  admin: boolean;
  /** The first account, which runs the server. */
  first: boolean;
  disabledAt: string | null;
  twoStep: boolean;
  passkeys: number;
  assistants: number;
}

/** An address invited to make an account. */
export interface AccountInvite {
  id: string;
  email: string;
  createdAt: string;
  expiresAt: string;
}

/** The users page: the accounts, the invitations waiting, and what the last one sent said. */
export interface AccountsPage {
  me: string;
  mail: boolean;
  users: Person[];
  invites: AccountInvite[];
  sent: { mail: boolean; link: string } | null;
}

function parseAccounts(body: unknown): AccountsPage {
  const fields = record(body, 'accounts');
  return {
    me: text(fields, 'me'),
    mail: flag(fields, 'mail'),
    users: list(fields, 'users', (item) => {
      const one = record(item, 'user');
      return {
        id: text(one, 'id'),
        name: text(one, 'name'),
        email: text(one, 'email'),
        createdAt: text(one, 'createdAt'),
        lastSeenAt: optionalText(one, 'lastSeenAt'),
        trips: number(one, 'trips'),
        admin: flag(one, 'admin'),
        first: flag(one, 'first'),
        disabledAt: optionalText(one, 'disabledAt'),
        twoStep: flag(one, 'twoStep'),
        passkeys: number(one, 'passkeys'),
        assistants: number(one, 'assistants'),
      };
    }),
    invites: list(fields, 'invites', (item) => {
      const one = record(item, 'invite');
      return {
        id: text(one, 'id'),
        email: text(one, 'email'),
        createdAt: text(one, 'createdAt'),
        expiresAt: text(one, 'expiresAt'),
      };
    }),
    sent: maybe(fields, 'sent', (value) => {
      const one = record(value, 'sent');
      return { mail: flag(one, 'mail'), link: text(one, 'link') };
    }),
  };
}

const userPath = (id: string) => `/_/api/admin/users/${encodeURIComponent(id)}`;

export function getAccounts(): Promise<AccountsPage> {
  return call('/_/api/admin/users', {}, parseAccounts);
}

/** An account made an admin or not, turned off or on. */
export function setPerson(
  id: string,
  change: { admin?: boolean; disabled?: boolean },
): Promise<AccountsPage> {
  return call(userPath(id), json('PUT', change), parseAccounts);
}

/** Every device of an account signed out. */
export function signOutPerson(id: string): Promise<AccountsPage> {
  return call(`${userPath(id)}/sessions`, { method: 'DELETE' }, parseAccounts);
}

/** An account deleted with the trips only it is on; `InvalidInput` on `user` while it shares. */
export function deletePerson(id: string): Promise<AccountsPage> {
  return call(userPath(id), { method: 'DELETE' }, parseAccounts);
}

/** A link that sets a new password, mailed and said. */
export function resetPerson(id: string): Promise<{ mail: boolean; link: string }> {
  return call(`${userPath(id)}/reset`, json('POST', {}), (body) => {
    const fields = record(body, 'reset');
    return { mail: flag(fields, 'mail'), link: text(fields, 'link') };
  });
}

/** An address invited to make an account, the mail in `language`. */
export function invitePerson(email: string, language: string): Promise<AccountsPage> {
  return call('/_/api/admin/invites', json('POST', { email, language }), parseAccounts);
}

export function dropAccountInvite(id: string): Promise<AccountsPage> {
  return call(
    `/_/api/admin/invites/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    parseAccounts,
  );
}

/** The address an invitation to make an account is for; `NotFound` when it is not good. */
export function getJoin(token: string): Promise<string> {
  return call(`/_/api/join/${encodeURIComponent(token)}`, {}, (body) =>
    text(record(body, 'join'), 'email'),
  );
}

/** The account made by an invitation, signed in. */
export function join(
  token: string,
  input: { name: string; password: string; language: string },
): Promise<User> {
  return call(`/_/api/join/${encodeURIComponent(token)}`, json('POST', input), parseUser);
}

// ---- a trip's areas that tell when someone arrives or leaves (geofences) ----

export const areaTells = ['arrive', 'leave', 'both'] as const;
export type AreaTell = (typeof areaTells)[number];

/** A circle on a trip: crossing it, as one's phone recording its way sees it, is told. */
export interface TripArea {
  id: string;
  name: string;
  lat: number;
  lon: number;
  /** Metres, 50 to 50 000. */
  radius: number;
  tell: AreaTell;
}

export type AreaInput = Omit<TripArea, 'id'>;

function parseAreas(body: unknown): TripArea[] {
  return list(record(body, 'areas'), 'areas', (item) => {
    const area = record(item, 'area');
    return {
      id: text(area, 'id'),
      name: text(area, 'name'),
      lat: number(area, 'lat'),
      lon: number(area, 'lon'),
      radius: number(area, 'radius'),
      tell: oneOf(area, 'tell', areaTells),
    };
  });
}

const areasPath = (trip: string) => `/_/api/trips/${encodeURIComponent(trip)}/areas`;

export function getAreas(trip: string): Promise<TripArea[]> {
  return call(areasPath(trip), {}, parseAreas);
}

export function addArea(trip: string, area: AreaInput): Promise<TripArea[]> {
  return call(areasPath(trip), json('POST', area), parseAreas);
}

export function removeArea(trip: string, id: string): Promise<TripArea[]> {
  return call(`${areasPath(trip)}/${encodeURIComponent(id)}`, { method: 'DELETE' }, parseAreas);
}

/** An area crossed, as the phone saw it: told to the trip when the area tells that way. */
export function crossArea(trip: string, id: string, arrived: boolean): Promise<null> {
  return call(
    `${areasPath(trip)}/${encodeURIComponent(id)}/crossed`,
    json('POST', { arrived }),
    () => null,
  );
}
